import React, { useState, useEffect, useRef, useContext, useCallback, useMemo, useLayoutEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { FaTrash, FaCheck, FaPlus, FaHeading } from "react-icons/fa";
import { debounce } from 'lodash';
import {
  Modal,
  Typography,
  Button,
  TextareaAutosize,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  MenuItem
} from "@mui/material";
import "./HsePreviewReportModal.css";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { config } from "../../config";
import axios from "../../APIs/axios";
import { toast } from "react-toastify";
import { getAccountDetails } from "../Services/localStorage";
import HSE_Cover from "../../HSE_Cover.jpg";
// import HSE_Cover_New from "../../HSE_Report_Cover.png";
// import HSE_Cover_New from "../../HSE PNG.png";
// import HSE_Cover_New from "../../HSEPortrait.png";
import HSE_Cover_New from "../../HSE2026_PNG.png";
import ExportExcel from "../ExportExcel/ExportExcel";
import { groupReferences, orderReferences } from "../../utils/annexureGrouping";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ExportSavedReportPDF from "../ExportSavedReportPDF/ExportSavedReportPDF";
import ExportWordDoc from "../ExportWordDoc/ExportWordDoc";
import PlaylistAddCircleIcon from "@mui/icons-material/PlaylistAddCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InfoIcon from "@mui/icons-material/Info";
import Chart from "react-apexcharts";
import html2canvas from "html2canvas";
import ImageViewerModal from "../ImageViewerModal/ImageViewerModal";
import CreatableSelect from "react-select/creatable";
import MultiLineCreatableSelect from "../HseSavedReportModal/MultiLineCreatableSelect";
import ImageCropModal from "../HseSavedReportModal/ImageCropModal";
import VariantsModal from "../VariantsModal/VariantsModal";
import Select from "react-select";
import { Category } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { IoAddCircle } from "react-icons/io5";
import JoditEditor from 'jodit-react';
import HTMLReactParser from 'html-react-parser';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import DialogBox from "../DialogBox/DialogBox";
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { ReportContextHSE } from "../ReportContext/ReportContextHSE";
import Tooltips from "@mui/material/Tooltip";
import ViewHseReportSectionBasedDetails from "../ViewHseReportDetails/ViewHseReportSectionBasedDetails";
import HseObservationRow from "./HseObservationRow";

// Number of columns in the preview observation table. The spacer rows below span
// this many columns so the table grid stays aligned.
const HSE_OBS_COLUMN_COUNT = 14;

// Virtualized table for one observation group (one `tableType`). Mirrors the
// implementation in HseSavedReportModal: only the rows in (and just around) the
// viewport are mounted, so a 300-1000+ row report stays responsive instead of
// mounting every row at once. The row component, its handlers and the data all
// live in the parent, so unmounting a row off-screen never loses edits.
//
// The list (<tbody>) sits below the column header inside the same scroll box, so
// the virtualizer is told the header's height via `scrollMargin`. We read that
// height directly from the <thead> (scroll-position independent, so it can't
// drift) and keep it current with a ResizeObserver — getting it right is what
// keeps the rendered window aligned with what's actually on screen.
function HseObservationGroup({
  tableType,
  rows,
  headingId,
  tableRef,
  renderRow,
  renderTrailing,
}) {
  const tbodyRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const setScrollContainerRef = useCallback(
    (node) => {
      scrollContainerRef.current = node;
      // Keep feeding the shared tableRef so the existing close-dropdowns-on
      // -scroll handler still works.
      if (tableRef) tableRef.current = node;
    },
    [tableRef]
  );
  const [scrollMargin, setScrollMargin] = useState(0);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    // Read-only rows are lightweight (see MultiLineCreatableSelect), so a low
    // estimate is close to reality. Underestimating is the safe direction: it
    // renders a few extra rows (cheap) and avoids a blank strip at the bottom
    // edge before real heights are measured. measureElement refines each row.
    estimateSize: () => 64,
    overscan: 12,
    scrollMargin,
  });

  useLayoutEffect(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return;
    const headEl = scrollEl.querySelector("thead");
    if (!headEl) return;
    const measure = () => {
      const h = headEl.offsetHeight;
      setScrollMargin((prev) => (Math.abs(prev - h) > 1 ? h : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(headEl);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [rows.length]);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const paddingTop =
    virtualItems.length > 0 ? Math.max(0, virtualItems[0].start - scrollMargin) : 0;
  const paddingBottom =
    virtualItems.length > 0
      ? Math.max(
        0,
        rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
      )
      : 0;

  return (
    <TableContainer component={Paper} className="table-scroll" ref={setScrollContainerRef}>
      <Table
        aria-labelledby={headingId}
        aria-rowcount={rows.length + 1 + (renderTrailing ? 1 : 0)}
      >
        <TableHead>
          <TableRow aria-rowindex={1}>
            <TableCell scope="col">Sr. No.</TableCell>
            <TableCell scope="col">Area / Process</TableCell>
            <TableCell scope="col">Equipment</TableCell>
            <TableCell scope="col">Category</TableCell>
            <TableCell scope="col">Check Point</TableCell>
            <TableCell scope="col">Criticality</TableCell>
            <TableCell scope="col">Status</TableCell>
            <TableCell scope="col">Observation</TableCell>
            <TableCell scope="col">Recommendation</TableCell>
            <TableCell scope="col">Legal Reference (if any)</TableCell>
            <TableCell scope="col">Max Points</TableCell>
            <TableCell scope="col">Score</TableCell>
            <TableCell scope="col">Objective Evidence</TableCell>
            <TableCell scope="col">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody ref={tbodyRef}>
          {paddingTop > 0 && (
            <TableRow aria-hidden="true" style={{ height: paddingTop }}>
              <TableCell
                colSpan={HSE_OBS_COLUMN_COUNT}
                style={{ padding: 0, border: 0, height: paddingTop }}
              />
            </TableRow>
          )}
          {virtualItems.map((virtualRow) =>
            renderRow(
              rows[virtualRow.index],
              virtualRow.index,
              rowVirtualizer.measureElement
            )
          )}
          {paddingBottom > 0 && (
            <TableRow aria-hidden="true" style={{ height: paddingBottom }}>
              <TableCell
                colSpan={HSE_OBS_COLUMN_COUNT}
                style={{ padding: 0, border: 0, height: paddingBottom }}
              />
            </TableRow>
          )}
          {renderTrailing && renderTrailing()}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const HsePreviewReportModal = ({
  open,
  setOpenModal,
  setOpenCreateReportModal,
  reportHeader,
  // selectedObservations,
  recommendations,
  selectedArea,
  selectedCategory,
  // setSelectedObservations,
  // selectedOrganization,
  // selectedSite,
  setRecommendations,
  setSelectedArea,
  setSelectedCategory,
  // setSelectedOrganization,
  // setSelectedSite,
  ReportUID,
  selectedDateTime,
  // observations,
  setCategoriesToDisplay,
  setAreasToDisplay,
  // setObservations,
  getAllHseData,
  // startDate,
  // endDate,
  // setEndDate,
  // setStartDate,

  generateUniqueId,
  getCurrentDateTime,
  setLoading,
  areaOptions,
  allData,
  getAllHseReports,
  selectedSector,
  setSelectedSector,
  setSelectedParam
}) => {
  const {

    backgroundBrief,
    setBackgroundBrief,
    exeSummary,
    setExeSummary,
    contents,
    setContents,
    introduction,
    setIntroduction,
    conclusion,
    setConclusion,
    bestPractice,
    setbestPractice,
    theWayForward,
    setTheWayForward,
    criticalObservations,
    setCriticalObservations,
    hasEditedCriticalObservations,
    setHasEditedCriticalObservations,
    manualCriticalObservations,
    setManualCriticalObservations,
    observations,
    setObservations,
    selectedObservations,
    setSelectedObservations,
    selectedOrganization,
    setSelectedOrganization,
    selectedSite,
    setSelectedSite,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    resetReportContext,
    editedObservations,
    setEditedObservations,
    isReportEdited,
    setIsReportEdited,
    facilityInfo,
    setFacilityInfo,
    allObservations, setAllObservations,
    classificationOfAuditObservations,
    setClassificationOfAuditObservations,
    auditScoreAnalysis, setAuditScoreAnalysis,
    improvementOpportunityAreas, setImprovementOpportunityAreas,
    overallAssessmentIndicator, setOverallAssessmentIndicator,

    timeFrom, setTimeFrom,
    timeTo, setTimeTo,
    briefPropertyDescription, setBriefPropertyDescription,
    numOfFloors, setNumOfFloors,
    avgStaffFootfall, setAvgStaffFootfall,
    noObjectionCertificate, setNoObjectionCertificate,
    nationalBuildingCodeCategory, setNationalBuildingCodeCategory,
    coordinationgPersonClientside, setCoordinationgPersonClientside,
    reportPreparedBy, setReportPreparedBy,
    reportReviewedBy, setReportReviewedBy,
    buildingName, setbuildingName,
    selectedTableType, setSelectedTableType,
    tableTypes, setTableTypes,
    safeGroupedObservations, setSafeGroupedObservations,

  } = useContext(ReportContextHSE)
  const cleanHTML = (html) => {
    if (!html) return "";

    return html
      // (<p><br></p>)
      .replace(/(<p>\s*(<br\s*\/?>)*\s*<\/p>){2,}/gi, "<p><br></p>")
      .replace(/(<p>\s*(<br\s*\/?>)*\s*<\/p>)+/gi, "")
      // multiple <p> gaps by replacing with a single one
      .replace(/<\/p>\s*<p>\s*<\/p>\s*<p>/gi, "</p><p>")
      // consecutive <p> tags (large paragraph gaps)
      .replace(/<\/p>\s*<p>/g, "</p><br><p>")
      .replace(/(<br\s*\/?>\s*){2,}/gi, "<br>")
      .replace(/¶/g, "")        // Remove literal pilcrow
      .replace(/&para;/g, "")   // Remove encoded pilcrow
      .replace(/\u00B6/g, "")
      .trim();
  };

  // const [backgroundBrief, setBackgroundBrief] = useState(
  //   reportHeader.background_brief
  //     .replace("Alpha Maier Pvt.Ltd", `${selectedOrganization.label}`)
  //     .replace("Manesar plant", `${selectedSite.label}`)
  //     .replace("Electrical Systems", "Health, Safety and Environment")
  //     .replace(
  //       "30th & 31st March 2022",
  //       `${startDate.getDate()}-${startDate.getMonth() + 1
  //       }-${startDate.getFullYear()} and ${endDate.getDate()}-${endDate.getMonth() + 1
  //       }-${endDate.getFullYear()}`
  //     )
  // );


  useEffect(() => {
    // console.log("headers",reportHeader)
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"
    ];

    const formattedStartDate = `${startDate.getDate()}th ${monthNames[startDate.getMonth()]} ${startDate.getFullYear()} and ${endDate.getDate()}th ${monthNames[endDate.getMonth()]} ${endDate.getFullYear()}`;

    const formattedStartDateExesummary = `${startDate.getDate()}th ${monthNames[startDate.getMonth()]} ${startDate.getFullYear()}`;
    const originalBackground =
      reportHeader.background_brief
        .replace("Alpha Maier Pvt.Ltd", `${selectedOrganization.label}`)
        .replace("Manesar plant", `${selectedSite.label}`)
        .replace("Electrical Systems", "Health, Safety and Environment")
        // .replace(
        //   "30th & 31st March 2022",
        //   `${startDate.getDate()}-${startDate.getMonth() + 1
        //   }-${startDate.getFullYear()} and ${endDate.getDate()}-${endDate.getMonth() + 1
        //   }-${endDate.getFullYear()}`
        // )
        .replace("30th & 31st March 2022", formattedStartDate);
    const originalSummary =
      reportHeader.exe_summary
        // .replace(
        //   "Alpha Maier private Ltd",
        //   `${selectedOrganization.label}(${selectedSite.label})`
        // )
        .replace(
          "Alpha Maier private Ltd",
          `${selectedOrganization.label}`
        )
        // .replace(
        //   "30th &amp; 31st March 2022",
        //   `${startDate.getDate()}-${startDate.getMonth() + 1
        //   }-${startDate.getFullYear()} and ${endDate.getDate()}-${endDate.getMonth() + 1
        //   }-${endDate.getFullYear()}`
        // )
        .replace(
          "30th &amp; 31st March 2022", formattedStartDateExesummary)
        .replace("<areas>", selectedArea.join(","))
        .replaceAll("electrical", "HSE")
        .replaceAll("Electrical", "HSE");

    if (!backgroundBrief || backgroundBrief === reportHeader.background_brief) {
      setBackgroundBrief(originalBackground);
    }

    if (!exeSummary || exeSummary === reportHeader.exe_summary) {
      setExeSummary(originalSummary);
    }


    if (!theWayForward) {
      setTheWayForward(reportHeader.the_way_forward);
    }

    if (!contents) {
      setContents(reportHeader.contents.replace("Scoring Table", "Charts"));
    }
    if (!introduction) {
      setIntroduction(reportHeader.introduction);
    }
    if (!bestPractice) {
      setbestPractice(reportHeader.best_practice);
    }
    if (!classificationOfAuditObservations) {
      setClassificationOfAuditObservations(reportHeader.classification_of_audit_observations)
    }
    if (!auditScoreAnalysis) {
      setAuditScoreAnalysis(reportHeader.audit_score_analysis)
    }
    if (!improvementOpportunityAreas) {
      setImprovementOpportunityAreas(reportHeader.improvement_opportunity_areas)
    }
    if (!overallAssessmentIndicator) {
      setOverallAssessmentIndicator(reportHeader.overall_assessment_indicator)
    }
    if (!conclusion) {
      setConclusion(reportHeader.conclusion.replaceAll("electrical", "HSE"));
    }

  }, [reportHeader, selectedOrganization, selectedSite, startDate, endDate]);
  const [dialog, setDialog] = useState({ open: false, message: "", title: "", accept: "", reject: "", onConfirm: null });
  const handleDialogBoxObservation = (action) => {
    if (action) {
      dialog.onConfirm(); // Execute the confirmed action (allow editing)
    }
    setDialog((prevDialog) => ({ ...prevDialog, open: false }));
  };



  useEffect(() => {
    // If criticalObservations was never touched, initialize it from reportHeader
    if (
      reportHeader.critical_observations?.length &&
      !hasEditedCriticalObservations &&
      (!criticalObservations || criticalObservations.length === 0)
    ) {
      // setCriticalObservations(reportHeader.critical_observations);
      const clonedCritical = reportHeader.critical_observations.map((obs) => ({
        ...obs
      }));
      setCriticalObservations(clonedCritical);

    }
  }, [reportHeader.report_id]); // better dependency: only reset when new report is loaded
  useEffect(() => {
    setHasEditedCriticalObservations(false);
  }, [reportHeader.report_id]);

  const [criticalityOptionList, setCriticalityOptionList] = useState([]);
  const [statusOptionList, setStatusOptionList] = useState([]);
  const [allScores, setAllScores] = useState([]);


  const [areaList, setAreaList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [hseCategoryList, setHseCategoryList] = useState([]);
  const [checkpointList, setCheckpointList] = useState([]);
  const [criticalityList, setCriticalityList] = useState([]);
  const [observationList, setObservationList] = useState([]);
  const [recommendationList, setRecommendationList] = useState([]);
  const [legalReferenceList, setLegalReferenceList] = useState([]);
  const [statusList, setStatusList] = useState([]);

  // Separate state variables for new row dropdown lists
  const [newRowAreaList, setNewRowAreaList] = useState([]);
  const [newRowEquipmentList, setNewRowEquipmentList] = useState([]);
  const [newRowHseCategoryList, setNewRowHseCategoryList] = useState([]);
  const [newRowCheckpointList, setNewRowCheckpointList] = useState([]);
  const [newRowCriticalityList, setNewRowCriticalityList] = useState([]);
  const [newRowObservationList, setNewRowObservationList] = useState([]);
  const [newRowRecommendationList, setNewRowRecommendationList] = useState([]);
  const [newRowLegalReferenceList, setNewRowLegalReferenceList] = useState([]);
  const [newRowStatusList, setNewRowStatusList] = useState([]);


  const [otherDetails, setOtherDetails] = useState("");

  const [screenNumber, setScreenNumber] = useState(1);
  const { userId, name } = getAccountDetails();

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // const [isReportEdited, setIsReportEdited] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [currentEditedRow, setCurrentEditedRow] = useState(-1);
  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState([]);
  const [scorePercent, setScorePercent] = useState(0);
  const [chartImage, setChartImage] = useState(null);
  // Warns when the Overall Assessment Indicator text exceeds the 4000-char PDF limit.
  const [charWarning, setCharWarning] = useState(false);
  const chartContainerRef = useRef(null);
  const [disableSaveNext, setDisableSaveNext] = useState(false);
  const [waitForCharts, setWaitForCharts] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [area, setArea] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [editedFields, setEditedFields] = useState([]);
  const [observationVariants, setObservationVariants] = useState([]);
  const [openVairantModal, setOpenVariantModal] = useState(false);
  const [confirmationShown, setConfirmationShown] = useState(false);

  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newRowInputs, setNewRowInputs] = useState({});
  const [newRowInputValues, setNewRowInputValues] = useState({});
  const [editInputValues, setEditInputValues] = useState({});
  const [newObservationAdded, setNewObservationAdded] = useState(false);
  const initializeNewRowInputs = (tableType, sectorType) => {
    setNewRowInputs((prev) => ({
      ...prev,
      [tableType]: {
        table_type: tableType, // Fixed key for table type
        sector_type: sectorType, // Include sector type
        is_selected: 1, // Fixed value for is_selected
        area: "",
        equipment: "",
        category: "",
        check_points: "",
        criticality: "",
        status: "",
        observation: "",
        recommendations: "",
        legal_reference: "",
        is_reference: "",
        score: "",
        imageUrls: [],
        system_implementation: "",
        // compliance_check:"",
      },
    }));
  };

  useEffect(() => {
    getCriticalityStatusOption()
    getAllScoreListBySectorType();
  }, [])

  const getAllScoreListBySectorType = async () => {
    try {
      const response = await axios.get(
        `${config.PATH}/api/get-score-by-sector-type?sector_type=${selectedSector.value || ''}`
      );
      if (response.data?.success && response.data?.data) {
        setAllScores(response.data.data || []);
      }

    } catch (error) {
      if (error.response?.status === 404) {


      } else {
        console.error('Error fetching Scores:', error);
      }
    }
  }

  const getCriticalityStatusOption = async () => {
    try {
      const response = await axios.get(
        `${config.PATH}/api/get-hse-criticality-status-select-options?sector_type=${selectedSector?.value || ''}`
      );

      if (response.data?.success && response.data?.data) {
        setCriticalityOptionList(response.data.data.criticality_options || []);
        setStatusOptionList(response.data.data.status_options || []);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.warn(`Sector type "${selectedSector?.value}" not found in HSE options - using empty lists`);
        setCriticalityOptionList([]);
        setStatusOptionList([]);
      } else {
        console.error('Error fetching criticality status options:', error);
      }
    }
  };


  // const fetchDataForCharts = () => {
  //   const observations = selectedObservations || [];

  //   const totalScore =
  //     observations.length > 0
  //       ? observations
  //         .map((e) => e.score || 0)
  //         .reduce((acc, score) => acc + score, 0)
  //       : 0;

  //   const percentage = Math.floor(
  //     (totalScore / (observations.length * 5)) * 100
  //   );

  //   setScorePercent(isNaN(percentage) ? 0 : percentage);
  //   setData(observations);
  // };

  const fetchDataForCharts = () => {

    const observations = selectedObservations || [];

    // const totalScore =
    //   observations.length > 0
    //     ? observations
    //       .map((e) => e.score || 0)
    //       .reduce((acc, score) => acc + score, 0)
    //     : 0;

    const totalScore = observations.reduce((acc, item) => {
      return acc + parseFloat(item.score || 0);
    }, 0);
    const totalMaxPoints = observations.reduce((acc, item) => {
      return acc + parseFloat(item.max_pts || 0);
    }, 0);

    const percentage =
      totalMaxPoints > 0
        ? Math.floor((totalScore / totalMaxPoints) * 100)
        : 0;

    // const percentage = Math.floor(
    //   (totalScore / (observations.length * 5)) * 100
    // );


    setScorePercent(isNaN(percentage) ? 0 : percentage);
    setData(observations);
  };

  // const convertChartToImage = async () => {
  //   try {
  //     if (!chartContainerRef.current) {
  //       console.error("chartContainerRef is null or undefined");
  //       return;
  //     }

  //     const contentHeight = chartContainerRef.current.scrollHeight;
  //     chartContainerRef.current.style.height = `${contentHeight}px`;

  //     const canvas = await html2canvas(chartContainerRef.current, {
  //       scrollY: -window.scrollY,
  //     });

  //     chartContainerRef.current.style.height = null;

  //     const image = canvas.toDataURL("image/png");
  //     console.log("shjagsdsdjgsadj j djash", image)
  //     setChartImage(image);
  //   } catch (error) {
  //     console.log("Error converting div to image:", error);
  //   }
  // };

  const captureInProgressRef = useRef(false);

  const convertChartToImage = useCallback(async () => {
    if (captureInProgressRef.current) return null;

    try {
      captureInProgressRef.current = true;

      const chartElement = chartContainerRef.current;

      if (!chartElement) {
        console.log("chart ref missing");
        return null;
      }

      // Wait for ApexCharts to finish painting before snapshotting. A single
      // animation frame isn't enough — the charts mount and animate
      // asynchronously, so capturing too early produces a heading-only blank
      // Dashboard page in the PDF/Word export. Two frames plus a short settle
      // give the chart SVGs time to render.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 400));

      // Capture the container's FULL height/width, not just the slice visible
      // inside the scrollable modal body. Without this the charts below the
      // fold get clipped out of the snapshot, leaving only the "Dashboard"
      // heading on the exported page (the blank-page bug).
      const fullHeight = chartElement.scrollHeight;
      const fullWidth = chartElement.scrollWidth;

      const canvas = await html2canvas(chartElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: -window.scrollY,
        height: fullHeight,
        width: fullWidth,
        windowHeight: fullHeight,
        logging: false,
      });

      const image = canvas.toDataURL("image/png");

      console.log("chart generated", image?.length);

      return image;
    } catch (error) {
      console.log("Error converting chart:", error);
      return null;
    } finally {
      captureInProgressRef.current = false;
    }
  }, []);

  // useEffect(() => {
  //   if (screenNumber === 11) {
  //     fetchDataForCharts();
  //     const timeoutId = setTimeout(() => {
  //       convertChartToImage();
  //     }, 1000); // Adjust the delay as needed (e.g., 1000 milliseconds)

  //     // Clear the timeout if the component unmounts or screenNumber changes
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [screenNumber, selectedObservations, convertChartToImage]);

  // useEffect(() => {
  //   if (screenNumber !== 11) {
  //     return;
  //   }

  //   let isCancelled = false;

  //   fetchDataForCharts();

  //   const timeoutId = setTimeout(async () => {
  //     if (
  //       isCancelled ||
  //       !isMountedRef.current ||
  //       !chartContainerRef.current
  //     ) {
  //       return;
  //     }

  //     await convertChartToImage();
  //   }, 1200);

  //   return () => {
  //     isCancelled = true;
  //     clearTimeout(timeoutId);
  //   };
  // }, [screenNumber]);

  useEffect(() => {
    if (screenNumber !== 11) return;
    fetchDataForCharts();
  }, [screenNumber, selectedObservations]);

  // Invalidate cached chart snapshot whenever observations change so the
  // Dashboard chart embedded in PDF/Word exports from the Preview flow is
  // regenerated from the latest data instead of a stale image. Mirrors the
  // guard already in HseSavedReportModal.
  useEffect(() => {
    if (screenNumber <= 11) setChartImage(null);
  }, [selectedObservations, screenNumber]);

  useEffect(() => {
    if (
      screenNumber !== 11 ||
      chartImage
    ) {
      return;
    }

    const generateChart = async () => {
      const image = await convertChartToImage();

      if (image) {
        console.log("setting chart image");

        setChartImage(image);
      }
    };

    const timeout = setTimeout(generateChart, 2500);

    return () => clearTimeout(timeout);
  }, [
    screenNumber,
    data,
    scorePercent,
    convertChartToImage,
    chartImage,
  ]);

  useEffect(() => {
    setWaitForCharts(true);
    if (screenNumber === 11) {
      // Simulate loading for 3 seconds
      const loaderTimeout = setTimeout(() => {
        setWaitForCharts(false);
      }, 5000);

      // Cleanup timeout if the component unmounts or when loading is complete
      return () => clearTimeout(loaderTimeout);
    }
  }, [screenNumber]);

  useEffect(() => {
    populateAreaList();
  }, [allData]);

  // Pre-compute dropdown options cache for performance
  const dropdownOptionsCache = useMemo(() => {
    const data = allData?.data || [];
    const cache = {
      areas: {},
      equipment: {},
      category: {},
      checkpoints: {},
      criticality: {},
      status: {},
      observation: {},
      recommendation: {},
      legalReference: {}
    };

    // Build areas by table_type
    data.forEach(item => {
      const tableType = item.table_type || 'default';
      if (!cache.areas[tableType]) {
        cache.areas[tableType] = new Set();
      }
      if (item.area) cache.areas[tableType].add(item.area);
    });

    // Build equipment by area
    data.forEach(item => {
      const area = item.area;
      if (!cache.equipment[area]) {
        cache.equipment[area] = new Set();
      }
      if (item.equipment) cache.equipment[area].add(item.equipment);
    });

    // Build category by area and equipment
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}`;
      if (!cache.category[key]) {
        cache.category[key] = new Set();
      }
      if (item.category) cache.category[key].add(item.category);
    });

    // Build checkpoints by area, equipment, and category
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}`;
      if (!cache.checkpoints[key]) {
        cache.checkpoints[key] = new Set();
      }
      if (item.check_points) cache.checkpoints[key].add(item.check_points);
    });

    // Build criticality by area, equipment, category, and checkpoint
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.check_points}`;
      if (!cache.criticality[key]) {
        cache.criticality[key] = new Set();
      }
      if (item.criticality) cache.criticality[key].add(item.criticality);
    });

    // Build status by area, equipment, category, and criticality
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.criticality}`;
      if (!cache.status[key]) {
        cache.status[key] = new Set();
      }
      if (item.status) cache.status[key].add(item.status);
    });

    // Build observation by area, equipment, category, checkpoint, criticality, and status
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.check_points}|${item.criticality}|${item.status}`;
      if (!cache.observation[key]) {
        cache.observation[key] = new Set();
      }
      if (item.observation) cache.observation[key].add(item.observation);
    });

    // Build recommendation by area, equipment, category, checkpoint, criticality, and status
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.check_points}|${item.criticality}|${item.status}`;
      if (!cache.recommendation[key]) {
        cache.recommendation[key] = new Set();
      }
      if (item.recommendations?.trim()) cache.recommendation[key].add(item.recommendations.trim());
    });

    // Build legal reference by area, equipment, category, checkpoint, criticality, and status
    data.forEach(item => {
      const key = `${item.area}|${item.equipment}|${item.category}|${item.check_points}|${item.criticality}|${item.status}`;
      if (!cache.legalReference[key]) {
        cache.legalReference[key] = new Set();
      }
      if (item.legal_reference?.trim()) cache.legalReference[key].add(item.legal_reference.trim());
    });

    // Convert Sets to Arrays
    Object.keys(cache.areas).forEach(key => {
      cache.areas[key] = Array.from(cache.areas[key]);
    });
    Object.keys(cache.equipment).forEach(key => {
      cache.equipment[key] = Array.from(cache.equipment[key]);
    });
    Object.keys(cache.category).forEach(key => {
      cache.category[key] = Array.from(cache.category[key]);
    });
    Object.keys(cache.checkpoints).forEach(key => {
      cache.checkpoints[key] = Array.from(cache.checkpoints[key]);
    });
    Object.keys(cache.criticality).forEach(key => {
      cache.criticality[key] = Array.from(cache.criticality[key]);
    });
    Object.keys(cache.status).forEach(key => {
      cache.status[key] = Array.from(cache.status[key]);
    });
    Object.keys(cache.observation).forEach(key => {
      cache.observation[key] = Array.from(cache.observation[key]);
    });
    Object.keys(cache.recommendation).forEach(key => {
      cache.recommendation[key] = Array.from(cache.recommendation[key]);
    });
    Object.keys(cache.legalReference).forEach(key => {
      cache.legalReference[key] = Array.from(cache.legalReference[key]);
    });

    return cache;
  }, [allData]);

  // Getter functions to retrieve options from cache
  const getAreaOptions = useCallback((tableType = null) => {
    const key = tableType || 'default';
    return dropdownOptionsCache.areas[key] || [];
  }, [dropdownOptionsCache]);

  const getEquipmentOptions = useCallback((area) => {
    return dropdownOptionsCache.equipment[area] || [];
  }, [dropdownOptionsCache]);

  const getCategoryOptions = useCallback((area, equipment) => {
    const key = `${area}|${equipment}`;
    return dropdownOptionsCache.category[key] || [];
  }, [dropdownOptionsCache]);

  const getCheckpointOptions = useCallback((area, equipment, category) => {
    const key = `${area}|${equipment}|${category}`;
    return dropdownOptionsCache.checkpoints[key] || [];
  }, [dropdownOptionsCache]);

  const getCriticalityOptions = useCallback((area, equipment, category, checkpoint) => {
    const key = `${area}|${equipment}|${category}|${checkpoint}`;
    return dropdownOptionsCache.criticality[key] || [];
  }, [dropdownOptionsCache]);

  const getStatusOptions = useCallback((area, equipment, category, criticality) => {
    const key = `${area}|${equipment}|${category}|${criticality}`;
    return dropdownOptionsCache.status[key] || [];
  }, [dropdownOptionsCache]);

  const getObservationOptions = useCallback((area, equipment, category, checkpoint, criticality, status) => {
    const key = `${area}|${equipment}|${category}|${checkpoint}|${criticality}|${status}`;
    return dropdownOptionsCache.observation[key] || [];
  }, [dropdownOptionsCache]);

  const getRecommendationOptions = useCallback((area, equipment, category, checkpoint, criticality, status) => {
    const key = `${area}|${equipment}|${category}|${checkpoint}|${criticality}|${status}`;
    return dropdownOptionsCache.recommendation[key] || [];
  }, [dropdownOptionsCache]);

  const getLegalReferenceOptions = useCallback((area, equipment, category, checkpoint, criticality, status) => {
    const key = `${area}|${equipment}|${category}|${checkpoint}|${criticality}|${status}`;
    return dropdownOptionsCache.legalReference[key] || [];
  }, [dropdownOptionsCache]);

  const populateAreaList = async (tableType = null) => {
    try {
      const options = getAreaOptions(tableType);
      setAreaList(options);
    } catch (err) {
      console.log(err);
    }
  };


  // New populate functions for new row dropdown lists
  const populateNewRowAreaList = async (tableType = null) => {
    try {
      const options = getAreaOptions(tableType);
      setNewRowAreaList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateNewRowEquipmentList = async (val, field) => {
    try {
      const options = getEquipmentOptions(val);
      setNewRowEquipmentList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateNewRowHseCategoryList = async (val, field, selectedArea) => {
    try {
      const options = getCategoryOptions(selectedArea, val);
      setNewRowHseCategoryList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateNewRowCheckpointList = async (val, field, selectedArea, selectedEquipment) => {
    try {
      const options = getCheckpointOptions(selectedArea, selectedEquipment, val);
      setNewRowCheckpointList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateNewRowCriticalityList = async (val, field, selectedArea, selectedEquipment, selectedCategory) => {
    try {
      const options = getCriticalityOptions(selectedArea, selectedEquipment, selectedCategory, val);
      setNewRowCriticalityList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateNewRowStatusList = async (selectedArea, selectedEquipment, selectedCategory, selectedCriticality) => {
    try {
      const options = getStatusOptions(selectedArea, selectedEquipment, selectedCategory, selectedCriticality);
      setNewRowStatusList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateNewRowObservationList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      const options = getObservationOptions(selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus);
      setNewRowObservationList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateNewRowRecommendationList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      const options = getRecommendationOptions(selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus);
      setNewRowRecommendationList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateNewRowLegalReferenceList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      const options = getLegalReferenceOptions(selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus);
      setNewRowLegalReferenceList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateHseCategoryList = async (val, field, selectedArea) => {
    try {
      const options = getCategoryOptions(selectedArea, val);
      setHseCategoryList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateCheckpointList = async (val, field, selectedArea, selectedEquipment) => {
    try {
      const options = getCheckpointOptions(selectedArea, selectedEquipment, val);
      setCheckpointList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateCriticalityList = async (val, field, selectedArea, selectedEquipment, selectedCategory) => {
    try {
      const options = getCriticalityOptions(selectedArea, selectedEquipment, selectedCategory, val);
      setCriticalityList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateStatusList = async (selectedArea, selectedEquipment, selectedCategory, selectedCriticality) => {
    try {
      const options = getStatusOptions(selectedArea, selectedEquipment, selectedCategory, selectedCriticality);
      setStatusList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateEquipmentList = async (val, field) => {
    try {
      const options = getEquipmentOptions(val);
      setEquipmentList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateObservationList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      const options = getObservationOptions(selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus);
      setObservationList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateRecommendationList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      const options = getRecommendationOptions(selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus);
      setRecommendationList(options);
    } catch (err) {
      console.log(err);
    }
  };

  const populateLegalReferenceList = async (selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus) => {
    try {
      const options = getLegalReferenceOptions(selectedArea, selectedEquipment, selectedCategory, selectedCheckpoint, selectedCriticality, selectedStatus);
      setLegalReferenceList(options);
    } catch (err) {
      console.log(err);
    }
  };

  // Helper to determine if a field should be disabled
  // For isNew rows: disabled unless this specific row is being edited
  // For regular rows: disabled when any other row is being edited
  const isFieldDisabled = (observation) => {
    if (observation.variant) {
      return true;
    }
    if (observation.isNew) {
      // isNew rows are only editable when this specific row is in edit mode
      return !(isEditing && currentEditedRow === observation.sr_no);
    }
    // Regular rows follow existing logic: disabled when editing another row
    return isEditing && currentEditedRow !== observation.sr_no;
  };

  // Handler for starting edit mode on a new row (isNew rows)
  const handleEditNewRow = (observation) => {
    // Check if another row is already being edited
    if (isEditing && currentEditedRow !== -1 && currentEditedRow !== observation.sr_no) {
      const unsavedRow = selectedObservations.find(
        (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
      );
      const unsavedTableType = unsavedRow?.table_type || "another table";
      const unsavedRowIndex = safeGroupedObservations[unsavedTableType]
        ? safeGroupedObservations[unsavedTableType].findIndex(
          (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
        )
        : -1;
      const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

      toast.warning(
        unsavedSrNo
          ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before editing another row.`
          : `You have an unsaved observation row in "${unsavedTableType}". Please save it before editing another row.`
      );
      return;
    }

    // Populate dropdown lists based on the row's current values
    // This ensures selectable fields have proper options when editing starts
    if (observation.area) {
      populateEquipmentList(observation.area, "area");
    }
    if (observation.equipment && observation.area) {
      populateHseCategoryList(observation.equipment, "equipment", observation.area);
    }
    if (observation.category && observation.area && observation.equipment) {
      populateCheckpointList(observation.category, "category", observation.area, observation.equipment);
    }
    if (observation.check_points && observation.area && observation.equipment && observation.category) {
      populateCriticalityList(observation.check_points, "check_points", observation.area, observation.equipment, observation.category);
    }
    if (observation.area && observation.equipment && observation.category && observation.criticality) {
      populateStatusList(observation.area, observation.equipment, observation.category, observation.criticality);
    }
    if (observation.area && observation.equipment && observation.category && observation.check_points && observation.criticality && observation.status) {
      populateObservationList(observation.area, observation.equipment, observation.category, observation.check_points, observation.criticality, observation.status);
      populateRecommendationList(observation.area, observation.equipment, observation.category, observation.check_points, observation.criticality, observation.status);
      populateLegalReferenceList(observation.area, observation.equipment, observation.category, observation.check_points, observation.criticality, observation.status);
    }

    // Set editing state for this row
    setIsEditing(true);
    setCurrentEditedRow(observation.sr_no);
  };

  const handleCellEdit = (e, index, field, originalContent, observationObj) => {

    if (currentEditedRow !== -1 && currentEditedRow !== observationObj.sr_no) {
      const unsavedRow = selectedObservations.find(
        (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
      );
      const unsavedTableType = unsavedRow?.table_type || "another table";
      const unsavedRowIndex = safeGroupedObservations[unsavedTableType]
        ? safeGroupedObservations[unsavedTableType].findIndex(
          (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
        )
        : -1;
      const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

      toast.warning(
        unsavedSrNo
          ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before editing another row.`
          : `You have an unsaved observation row in "${unsavedTableType}". Please save it before editing another row.`
      );
      return;
    }

    const updateEditedFields = (newField) => {
      if (!editedFields.includes(newField)) {
        setEditedFields((prevFields) => [...prevFields, newField]);
      }
    };

    let value = "";

    if (["area", "equipment", "category", "check_points", "criticality", "status", "observation", "recommendations", "legal_reference"].includes(field)) {
      value = e?.value ?? "";
    } else if (e?.target?.value !== undefined) {
      value = e.target.value;
    } else if (e?.target?.textContent !== undefined) {
      value = e.target.textContent;
    }

    const maxLength = field === "check_points" ? 1000 : 100;
    // Skip character validation for observation, recommendations, and legal_reference fields
    if (
      field !== "observation" &&
      field !== "recommendations" &&
      field !== "legal_reference" &&
      value.length > maxLength
    ) {
      alert(`${field} cannot exceed ${maxLength} characters.`);
      return;
    }

    if (field === "area" && e) {
      setArea([e.value]);
      updateEditedFields("A");
    }

    setIsEditing(true);
    setCurrentEditedRow(observationObj.sr_no);

    setIsReportEdited(true);

    // Dependent selection logic - clear dependent fields and populate lists
    if (field === "area") {
      let calculatedScore = 0;
      let calculatedMaxPoints = 0;
      // Clear dependent fields when area changes
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          area: value,
          equipment: "",
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              area: value,
              equipment: "",
              category: "",
              check_points: "",
              criticality: "",
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
            };
          }
        }
        return updated;
      });
      if (value) {
        populateEquipmentList(value, field);
      } else {
        setEquipmentList([]);
      }
      // Reset dependent dropdown lists when area changes
      setHseCategoryList([]);
      setCheckpointList([]);
      setCriticalityList([]);
      setStatusList([]);
      setObservationList([]);
      setRecommendationList([]);
      setLegalReferenceList([]);
      return;
    }

    if (field === "equipment") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      // Clear dependent fields when equipment changes
      const currentArea = observationObj.area || "";
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          equipment: value,
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              equipment: value,
              category: "",
              check_points: "",
              criticality: "",
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
            };
          }
        }
        return updated;
      });
      if (value) {
        populateHseCategoryList(value, field, currentArea);
      } else {
        setHseCategoryList([]);
      }
      // Reset dependent dropdown lists when equipment changes
      setCheckpointList([]);
      setCriticalityList([]);
      setStatusList([]);
      setObservationList([]);
      setRecommendationList([]);
      setLegalReferenceList([]);
      return;
    }

    if (field === "category") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      // Clear dependent fields when category changes
      const currentArea = observationObj.area || "";
      const currentEquipment = observationObj.equipment || "";
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          category: value,
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              category: value,
              check_points: "",
              criticality: "",
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
            };
          }
        }
        return updated;
      });
      if (value) {
        populateCheckpointList(value, field, currentArea, currentEquipment);
      } else {
        setCheckpointList([]);
      }
      // Reset dependent dropdown lists when category changes
      setCriticalityList([]);
      setStatusList([]);
      setObservationList([]);
      setRecommendationList([]);
      setLegalReferenceList([]);
      return;
    }

    if (field === "check_points") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      // Clear dependent fields when checkpoint changes
      const currentArea = observationObj.area || "";
      const currentEquipment = observationObj.equipment || "";
      const currentCategory = observationObj.category || "";
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          check_points: value,
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              check_points: value,
              criticality: "",
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
            };
          }
        }
        return updated;
      });
      if (value) {
        populateCriticalityList(value, field, currentArea, currentEquipment, currentCategory);
        populateLegalReferenceList(
          currentArea,
          currentEquipment,
          currentCategory,
          value,
          null,
          null
        );
      } else {
        setCriticalityList([]);
        setLegalReferenceList([]);
      }
      // Reset dependent dropdown lists when checkpoint changes
      setStatusList([]);
      setObservationList([]);
      setRecommendationList([]);
      return;
    }

    if (field === "criticality") {
      // Clear dependent fields when criticality changes
      const currentArea = observationObj.area || "";
      const currentEquipment = observationObj.equipment || "";
      const currentCategory = observationObj.category || "";

      // Calculate score based on criticality and current status (if exists)
      const currentStatus = observationObj.status || "";

      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      if (value && currentStatus) {
        const matchingScore = allScores.find(
          (score) =>
            score.criticality === value?.trim() &&
            score.status === currentStatus?.trim()
        );
        calculatedScore = matchingScore?.earned ?? matchingScore?.score ?? "0";
        calculatedMaxPoints = matchingScore?.max_pts ?? "0";
      }

      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          criticality: value,
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              criticality: value,
              status: "",
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
            };
          }
        }
        return updated;
      });
      if (value) {
        populateStatusList(currentArea, currentEquipment, currentCategory, value);
      } else {
        setStatusList([]);
      }
      // Reset dependent dropdown lists when criticality changes
      setObservationList([]);
      setRecommendationList([]);
      setLegalReferenceList([]);
      return;
    }

    if (field === "status") {
      // Clear dependent fields when status changes
      const currentArea = observationObj.area || "";
      const currentEquipment = observationObj.equipment || "";
      const currentCategory = observationObj.category || "";
      const currentCheckpoint = observationObj.check_points || "";
      const currentCriticality = observationObj.criticality || "";

      // Calculate score based on current criticality and new status
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";

      if (currentCriticality && value) {
        const matchingScore = allScores.find(
          (score) =>
            score.criticality === currentCriticality?.trim() &&
            score.status === value?.trim()
        );

        calculatedScore =
          matchingScore?.earned ??
          matchingScore?.score ??
          "0";

        calculatedMaxPoints =
          matchingScore?.max_pts ??
          "0";
      }

      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          status: value,
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      // Also update safeGroupedObservations to reflect in UI
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              status: value,
              observation: "",
              recommendations: "",
              legal_reference: "",
              max_pts: calculatedMaxPoints,
              score: calculatedScore,
            };
          }
        }
        return updated;
      });
      if (value) {
        populateObservationList(
          currentArea,
          currentEquipment,
          currentCategory,
          currentCheckpoint,
          currentCriticality,
          value
        );
        populateRecommendationList(
          currentArea,
          currentEquipment,
          currentCategory,
          currentCheckpoint,
          currentCriticality,
          value
        );
        populateLegalReferenceList(
          currentArea,
          currentEquipment,
          currentCategory,
          currentCheckpoint,
          currentCriticality,
          value
        );
      } else {
        setObservationList([]);
        setRecommendationList([]);
        setLegalReferenceList([]);
      }
      return;
    }

    if (field === "observation") {
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          observation: value,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              observation: value,
            };
          }
        }
        return updated;
      });
      updateEditedFields("O");
      return;
    }

    if (field === "recommendations") {
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          recommendations: value,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              recommendations: value,
            };
          }
        }
        return updated;
      });
      updateEditedFields("R");
      return;
    }

    if (field === "legal_reference") {
      setEditedObservations((prev) => {
        const existingIndex = prev.findIndex((obs) => obs.sr_no === observationObj.sr_no);
        const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;
        const updatedObs = {
          ...base,
          legal_reference: value,
          selectedRefIndex: index,
        };
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = updatedObs;
          return updated;
        } else {
          return [...prev, updatedObs];
        }
      });
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[observationObj.table_type]) {
          const obsIndex = updated[observationObj.table_type].findIndex(
            (obs) => obs.sr_no === observationObj.sr_no
          );
          if (obsIndex !== -1) {
            updated[observationObj.table_type][obsIndex] = {
              ...updated[observationObj.table_type][obsIndex],
              legal_reference: value,
            };
          }
        }
        return updated;
      });
      updateEditedFields("LR");
      return;
    }

    // Default handling for other fields
    setEditedObservations((prev) => {
      const existingIndex = prev.findIndex(
        (obs) =>
          (obs.sr_no && obs.sr_no === observationObj.sr_no)

      );

      const base = existingIndex !== -1 ? prev[existingIndex] : observationObj;

      const updatedObs = {
        ...base,
        [field]: value,
        selectedRefIndex: index,
      };

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = updatedObs;
        return updated;
      } else {
        return [...prev, updatedObs];
      }
    });

    switch (field) {
      case "category":
        updateEditedFields("CA");
        break;
      case "check_points":
        updateEditedFields("CH");
        break;
      case "observation":
        updateEditedFields("O");
        break;
      case "criticality":
        updateEditedFields("CR");
        break;
      case "recommendations":
        updateEditedFields("R");
        break;
      case "is_reference":
        updateEditedFields("I");
        break;
      case "legal_reference":
        updateEditedFields("LR");
        break;
      default:
        break;
    }

    return true;
  };

  const handleEditInputChange = (observationId, inputValue, field) => {
    setEditInputValues((prev) => ({
      ...prev,
      [`${observationId}-${field}`]: inputValue,
    }));
  };

  const handleEditFieldFocus = (observationId, field) => {
    const observation = allData?.data?.find((e) => e.sr_no === observationId);
    if (!observation) return;

    // Check for unsaved row
    if (currentEditedRow !== -1 && currentEditedRow !== observationId) {
      const unsavedRow = selectedObservations.find(
        (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
      );
      const unsavedTableType = unsavedRow?.table_type || "another table";
      const unsavedRowIndex = safeGroupedObservations[unsavedTableType]
        ? safeGroupedObservations[unsavedTableType].findIndex(
          (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
        )
        : -1;
      const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

      toast.warning(
        unsavedSrNo
          ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before editing another row.`
          : `You have an unsaved observation row in "${unsavedTableType}". Please save it before editing another row.`
      );
      return;
    }

    // Always populate input value for editable text
    const currentValue = observation[field];
    if (currentValue) {
      setEditInputValues((prev) => ({
        ...prev,
        [`${observationId}-${field}`]: currentValue,
      }));
    }

    // Only set editing state when switching to a different row
    if (currentEditedRow !== observationId) {
      setIsEditing(true);
      setCurrentEditedRow(observationId);
      setIsReportEdited(true);
    }
  };

  const handleEditFieldBlur = (observationId, field) => {
    // Save the edited value on blur
    const inputValue = editInputValues[`${observationId}-${field}`];
    if (inputValue !== undefined && inputValue !== "") {
      const observation = allData?.data?.find((e) => e.sr_no === observationId);
      if (observation) {
        handleCellEdit(
          { value: inputValue.trim(), label: inputValue.trim() },
          allData?.data?.findIndex((e) => e.sr_no === observationId),
          field,
          observation[field],
          observation
        );
      }
      setEditInputValues((prev) => ({
        ...prev,
        [`${observationId}-${field}`]: "",
      }));
    }
  };

  const handleChange = (e, name) => {
    setIsSaved(false);
    if (name === "background") {
      setBackgroundBrief(e.target.value);
    } else if (name === "contents") {
      setContents(e.target.value);
    }
    else if (name === "classification") {
      setClassificationOfAuditObservations(e.target.value);
    } else if (name === "exe") {
      setExeSummary(e.target.value);
    } else if (name === "conclusion") {
      setConclusion(e.target.value);
    } else if (name === "other details") {
      setOtherDetails(e.target.value);
    } else if (name === "best_practice") {
      setbestPractice(e.target.value);
    } else if (name === "the_way_forward") {
      setTheWayForward(e.target.value);
    } else if (name === "intro") {
      setIntroduction(e.target.value);
    }
  };

  const handleChangeFacilityInfo = (event) => {
    const { name, value } = event.target;
    setFacilityInfo((prevInfo) => ({
      ...prevInfo,
      [name]: value,
    }));
  };

  const handleNewKeyChange = (event) => {
    setNewKey(event.target.value);
  };

  const handleNewValueChange = (event) => {
    setNewValue(event.target.value);
  };

  const handleAddNewField = () => {
    if (newKey && newValue) {
      const keyExists = facilityInfo.hasOwnProperty(newKey);
      const valueExists = facilityInfo[newKey] === newValue;

      if (keyExists) {
        alert("This field already exists.");
        return;
      }

      setFacilityInfo((prevInfo) => ({
        ...prevInfo,
        [newKey]: newValue,
      }));
      setNewKey("");
      setNewValue("");
    } else {
      alert("Please fill both the fields before adding.");
    }
  };


  const handleNext = async () => {

    const unsavedCritical = criticalObservations.some(obs => !obs.observation?.trim());
    if (unsavedCritical) {
      toast.warning("Please save the critical observations before proceeding.");
      return;
    }
    if (otherDetails.trim()) {
      toast.warning("Please add the Other critical observation before proceeding.");
      return;
    }

    if (isEditing) {
      toast.warning("Please save changes before proceeding.");
      return;
    }
    // Capture a fresh Dashboard snapshot while screen 11's chart container is
    // still mounted, so the PDF/Word exports always receive a chartImage —
    // even when there are no observations.
    if (screenNumber === 11) {
      const image = await convertChartToImage();
      if (image) setChartImage(image);
    }
    if (screenNumber === 10) {
      setScreenNumber(screenNumber + 1);
    } else {
      setScreenNumber(screenNumber + 1);
    }
    // setOpenReportList(false);
  };

  const handlePrev = () => {
    const unsavedCritical = criticalObservations.some(obs => !obs.observation?.trim());

    if (unsavedCritical) {
      toast.warning("Please save the critical observations before going back.");
      return;
    }
    if (otherDetails.trim()) {
      toast.warning("Please add the Other critical observation before going back.");
      return;
    }
    if (isEditing) {
      toast.warning("Please save changes before going back.");
      return;
    }
    if (screenNumber === 13) {
      setScreenNumber(screenNumber - 1);
    } else {
      setScreenNumber(screenNumber - 1);
    }

  };

  // const handleNext = () => {
  //   // console.log("dbg", backgroundBrief);

  //   const unsavedCritical = criticalObservations.some(obs => !obs.observation?.trim());
  //   if (unsavedCritical) {
  //     toast.warning("Please save the critical observations before proceeding.");
  //     return;
  //   }
  //   if (otherDetails.trim()) {
  //     toast.warning("Please add the Other critical observation before proceeding.");
  //     return;
  //   }
  //   if (isEditing) {
  //     toast.warning("Please save changes before proceeding.");
  //     return;
  //   }
  //   setScreenNumber(screenNumber + 1);
  // };

  // const handlePrev = () => {
  //   const unsavedCritical = criticalObservations.some(obs => !obs.observation?.trim());

  //   if (unsavedCritical) {
  //     toast.warning("Please save the critical observations before going back.");
  //     return;
  //   }
  //   if (otherDetails.trim()) {
  //     toast.warning("Please add the Other critical observation before going back.");
  //     return;
  //   }
  //   if (isEditing) {
  //     toast.warning("Please save changes before going back.");
  //     return;
  //   }
  //   setScreenNumber(screenNumber - 1);
  // };

  const handleEdit = () => {
    // console.log("bgg", backgroundBrief);
    // console.log("obs",observations)
    //         console.log("selectedobs",selectedObservations);

    setOpenModal(false);
    setOpenCreateReportModal(true);
  };

  const removeItem = (index) => {
    const updatedObservations = [...criticalObservations];
    updatedObservations.splice(index, 1);
    setCriticalObservations(updatedObservations);
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        const MAX_WIDTH = 800; // Maximum width for the compressed image
        const MAX_HEIGHT = 800; // Maximum height for the compressed image

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.7); // 0.7 is the image quality (0.0 - 1.0)
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // const handleImageUpload = async (table_type, identifier, area, files) => {
  //   // First, save pending column edits from editedObservations to main arrays
  //   if (editedObservations.length > 0) {
  //     setSelectedObservations((prev) => {
  //       const updated = [...prev];
  //       editedObservations.forEach((obj) => {
  //         const srNoOrId = obj.sr_no || obj.id;
  //         const index = updated.findIndex(
  //           (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
  //         );
  //         if (index !== -1) {
  //           updated[index] = obj;
  //         }
  //       });
  //       return updated;
  //     });

  //     setObservations((prev) => {
  //       const updated = [...prev];
  //       editedObservations.forEach((obj) => {
  //         const srNoOrId = obj.sr_no || obj.id;
  //         const index = updated.findIndex(
  //           (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
  //         );
  //         if (index !== -1) {
  //           updated[index] = { ...obj, is_selected: 1 };
  //         }
  //       });
  //       return updated;
  //     });
  //   }

  //   const selectedObsCopy = [...selectedObservations];
  //   const obsCopy = [...observations];
  //   const editedObsCopy = [...editedObservations];


  //   // Ensure imageUrls is initialized
  //   selectedObsCopy.forEach((item) => {
  //     if (!item.imageUrls) item.imageUrls = [];
  //   });

  //   obsCopy.forEach((item) => {
  //     if (!item.imageUrls) item.imageUrls = [];
  //   });

  //   editedObsCopy.forEach((item) => {
  //     if (!item.imageUrls) item.imageUrls = [];
  //   });

  //   try {
  //     setLoading(true);

  //     for (const file of Array.from(files)) {
  //       if (!file.type.startsWith("image/")) {
  //         throw new Error("Only image files are allowed.");
  //       }

  //       const compressedImage = await compressImage(file);

  //       const formData = new FormData();
  //       formData.append("image", compressedImage);

  //       const response = await axios.post(
  //         `${config.PATH}/api/upload/image`,
  //         formData,
  //         {
  //           headers: {
  //             "Content-Type": "multipart/form-data",
  //           },
  //         }
  //       );

  //       const imageUrl = response.data.imageUrl;

  //       // Update selectedObservations
  //       const selectedObs = selectedObsCopy.find(
  //         (item) =>
  //           item.table_type === table_type &&
  //           (item.sr_no === identifier || item.id === identifier)
  //       );
  //       if (selectedObs && !selectedObs.imageUrls.includes(imageUrl)) {
  //         selectedObs.imageUrls.push(imageUrl);
  //       }
  //       // Update observations
  //       const obs = obsCopy.find(
  //         (item) =>
  //           item.table_type === table_type &&
  //           (item.sr_no === identifier || item.id === identifier)
  //       );
  //       if (obs && !obs.imageUrls.includes(imageUrl)) {
  //         obs.imageUrls.push(imageUrl);
  //       }

  //       // Update editedObservations
  //       const editedObs = editedObsCopy.find(
  //         (item) =>
  //           item.table_type === table_type &&
  //           (item.sr_no === identifier || item.id === identifier)
  //       );
  //       if (editedObs && !editedObs.imageUrls.includes(imageUrl)) {
  //         editedObs.imageUrls.push(imageUrl);
  //       }
  //     }

  //     setSelectedObservations(selectedObsCopy);
  //     setObservations(obsCopy);

  //     if (editedObsCopy.length > 0) {
  //       setEditedObservations(editedObsCopy);
  //     }

  //     setLoading(false);
  //     toast.success("Images uploaded successfully!");
  //   } catch (error) {
  //     console.log("Error uploading image:", error);
  //     toast.error(error.message);
  //   }
  // };

  // The row's file input calls this first. We validate, then open the crop modal
  // instead of uploading straight away — the actual upload (handleImageUpload)
  // runs on the cropped blobs the modal returns.
  const handleImagePick = (table_type, identifier, area, fileList) => {
    const picked = Array.from(fileList || []);
    const extOf = (name = "") => name.split(".").pop().toLowerCase();

    // iPhone HEIC/TIFF photos aren't supported. We detect by extension as well as
    // type because Chrome/Firefox report an empty file.type for HEIC, so a MIME
    // check alone would miss them. These get the on-screen note, not an upload.
    const isUnsupported = (f) =>
      /heic|heif|tiff?/i.test(f.type) ||
      ["heic", "heif", "tif", "tiff"].includes(extOf(f.name));

    // Supported formats: JPEG, PNG and SVG only.
    const isSupported = (f) =>
      ["image/jpeg", "image/png", "image/svg+xml"].includes(f.type) ||
      ["jpg", "jpeg", "png", "svg"].includes(extOf(f.name));

    picked.filter(isUnsupported).forEach((f) =>
      toast.error(
        `${f.name} can't be used here. iPhone HEIC/TIFF images aren't supported — please upload a JPEG, PNG or SVG instead (on iPhone: Settings → Camera → Formats → Most Compatible).`
      )
    );

    const files = picked.filter((f) => isSupported(f) && !isUnsupported(f));
    if (files.length === 0) {
      // Only show the generic message when nothing already got the HEIC/TIFF note.
      if (!picked.some(isUnsupported)) {
        toast.error("Only JPEG, PNG or SVG images are allowed.");
      }
      return;
    }
    setCropState({ open: true, files, target: { table_type, identifier, area } });
  };

  const handleCropCancel = () =>
    setCropState({ open: false, files: [], target: null });

  const handleCropComplete = async (croppedBlobs) => {
    const target = cropState.target;
    setCropState({ open: false, files: [], target: null });
    if (target) {
      await handleImageUpload(
        target.table_type,
        target.identifier,
        target.area,
        croppedBlobs
      );
    }
  };

  const handleImageUpload = async (table_type, identifier, area, files) => {
    try {
      setLoading(true);

      // First, save pending column edits from editedObservations to main arrays
      if (editedObservations.length > 0) {
        setSelectedObservations((prev) => {
          const updated = [...prev];
          editedObservations.forEach((obj) => {
            const srNoOrId = obj.sr_no || obj.id;
            const index = updated.findIndex(
              (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
            );
            if (index !== -1) {
              updated[index] = obj;
            }
          });
          return updated;
        });

        setAllObservations((prev) => {
          const updated = [...prev];
          editedObservations.forEach((obj) => {
            const srNoOrId = obj.sr_no || obj.id;
            const index = updated.findIndex(
              (obs) => obs.sr_no === srNoOrId || obs.id === srNoOrId
            );
            if (index !== -1) {
              updated[index] = { ...obj, is_selected: 1 };
            }
          });
          return updated;
        });
        setNewObservationAdded(true);
      }

      const uploadedUrls = [];

      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          throw new Error("Only image files are allowed.");
        }

        const compressedImage = await compressImage(file);

        const formData = new FormData();
        formData.append("image", compressedImage);

        const response = await axios.post(
          `${config.PATH}/api/upload/image`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        uploadedUrls.push(response.data.imageUrl);
      }

      const updateImages = (arr) => {
        let changed = false;
        const updated = arr.map((item) => {
          if (
            item.table_type === table_type &&
            (item.sr_no === identifier || item.id === identifier)
          ) {
            changed = true;
            return { ...item, imageUrls: [...(item.imageUrls || []), ...uploadedUrls] };
          }
          return item;
        });
        return changed ? updated : arr;
      };

      setSelectedObservations((prev) => updateImages(prev));
      setAllObservations((prev) => updateImages(prev));
      setEditedObservations((prev) => {
        if (prev.length === 0) return prev;
        return updateImages(prev);
      });

      // Also update safeGroupedObservations directly so new images appear
      // while the row is still in edit mode, without triggering a stale-data rebuild
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[table_type]) {
          updated[table_type] = updated[table_type].map((item) =>
            (item.sr_no === identifier || item.id === identifier)
              ? { ...item, imageUrls: [...(item.imageUrls || []), ...uploadedUrls] }
              : item
          );
        }
        return updated;
      });

      setLoading(false);
      toast.success("Images uploaded successfully!");
    } catch (error) {
      console.log("Error uploading image:", error);
      toast.error(error.message);
    }
  };

  const handleRemoveImage = (table_type, area, identifier, imageIndex) => {
    // Create deep copies to avoid mutation
    const selectedObsCopy = [...selectedObservations];
    const obsCopy = [...observations];
    const editedObsCopy = [...editedObservations];

    // Find the observation with the matching table_type, area, and identifier
    const observationToUpdate = selectedObsCopy.find(
      (obs) =>
        obs.table_type === table_type &&
        obs.area === area &&
        (obs.sr_no === identifier || obs.id === identifier)
    );

    if (observationToUpdate && observationToUpdate.imageUrls) {
      // Remove the image URL at the specified index
      observationToUpdate.imageUrls.splice(imageIndex, 1);

      // If there are no more images in the array, remove the imageUrls property
      if (observationToUpdate.imageUrls.length === 0) {
        delete observationToUpdate.imageUrls;
      }

      // Update the corresponding observation in obsCopy
      const obsIndex = obsCopy.findIndex(
        (obs) =>
          obs.table_type === table_type &&
          obs.area === area &&
          (obs.sr_no === identifier || obs.id === identifier)
      );

      if (obsIndex !== -1) {
        obsCopy[obsIndex] = {
          ...obsCopy[obsIndex],
          imageUrls: observationToUpdate.imageUrls,
        };
      }

      // Update the corresponding observation in editedObsCopy
      const editedObsIndex = editedObsCopy.findIndex(
        (obs) =>
          obs.table_type === table_type &&
          obs.area === area &&
          (obs.sr_no === identifier || obs.id === identifier)
      );

      if (editedObsIndex !== -1) {
        editedObsCopy[editedObsIndex] = {
          ...editedObsCopy[editedObsIndex],
          imageUrls: observationToUpdate.imageUrls,
        };
      }

      // Update state
      setSelectedObservations(selectedObsCopy);
      setObservations(obsCopy);
      setEditedObservations(editedObsCopy);

      // Update safeGroupedObservations to match
      setSafeGroupedObservations((prev) => {
        const updated = { ...prev };
        if (updated[table_type]) {
          updated[table_type] = updated[table_type].map((item) =>
            (item.sr_no === identifier || item.id === identifier)
              ? { ...item, imageUrls: observationToUpdate.imageUrls }
              : item
          );
        }
        return updated;
      });

      // Keep the open preview dialog in sync. It renders from
      // selectedObservation.image, which is a snapshot taken when the dialog
      // opened — without this the removed image stays visible until the dialog
      // is closed and reopened.
      setSelectedObservation((prev) =>
        prev?.image &&
        (prev.image.sr_no === identifier || prev.image.id === identifier)
          ? {
              ...prev,
              image: {
                ...prev.image,
                imageUrls: observationToUpdate.imageUrls || [],
              },
            }
          : prev
      );
    } else {
      toast.error("Observation not found.");
    }
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState({ image: null, index: null });
  // Holds the images a user just picked while they crop them, plus the row they
  // belong to. Upload only fires once the crop modal hands back the final blobs.
  const [cropState, setCropState] = useState({ open: false, files: [], target: null });
  const handleOpenImageDialog = (index, observation) => {
    setSelectedObservation({ image: observation, index });
    setOpenDialog(true);
  };

  const handleCloseImageDialog = () => {
    setSelectedObservation({ image: null, index: null });
    setOpenDialog(false);
  };
  const keyNotToCheck = [
    "remarks",
    "score",
    "refIndex",
    "selectedRefIndex",
    "equipment",
    "is_selected",
    "imageUrls",
    "edited_fields",
    "status",
    "category",
  ];

  const handleSave = async (complete) => {
    //console.log('---------- handleSave')
    setExeSummary(cleanHTML(exeSummary));
    setContents(cleanHTML(contents))
    // console.log("global",bestPractice)
    // if (otherDetails.trim()) {
    //   toast.warning("Please add the Other critical observation before proceeding.");
    //   return;
    // }

    // const hasEmptyObservation = criticalObservations.some(obs => obs.observation.trim() === "");
    // if (hasEmptyObservation) {
    //   toast.warning("Critical observation cannot be empty!");
    //   return;
    // }
    try {
      setLoading(true);
      let updatedSelectedObservations = [...selectedObservations];
      // let updatedAllObservations = [...observations];
      let updatedAllObservations = [...allObservations];
            console.log("updatedSelectedObservations", updatedSelectedObservations);
            console.log("updateAllObservation", updatedAllObservations)
      // return
      // Trimming fields in observations and report data
      const trimFields = obj =>
        Object.fromEntries(
          Object.entries(obj).map(([key, value]) =>
            typeof value === "string" ? [key, value.trim()] : [key, value]
          )
        );

      const regroupByArea = (rows) => {
        const grouped = [];
        const areaSet = Array.from(new Set(rows.map(r => r.area)));
        areaSet.forEach(area => {
          grouped.push(...rows.filter(r => r.area === area));
        });
        return grouped;
      };

      if (isEditing && currentEditedRow !== -1) {
        const rowStillExists = selectedObservations.some(
          (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
        );
        if (rowStillExists) {
          const unsavedRow = selectedObservations.find(
            (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
          );
          const unsavedTableType = unsavedRow?.table_type || "";
          const unsavedRowIndex = safeGroupedObservations[unsavedTableType]
            ? safeGroupedObservations[unsavedTableType].findIndex(
              (obs) => obs.sr_no === currentEditedRow || obs.id === currentEditedRow
            )
            : -1;
          const unsavedSrNo = unsavedRowIndex !== -1 ? unsavedRowIndex + 1 : null;

          toast.error(
            unsavedSrNo
              ? `You have an unsaved observation at row ${unsavedSrNo} in "${unsavedTableType}". Please save it before saving the report.`
              : `You have an unsaved observation row in "${unsavedTableType}". Please save it before saving the report.`
          );
          setLoading(false);
          return;
        }
        setIsEditing(false);
        setCurrentEditedRow(-1);
        setEditedObservations([]);
      }

      if (editedObservations.length > 0) {
        for (const obj of editedObservations) {
          const trimmedObj = trimFields(obj);

          // const emptyFieldFound = Object.entries(trimmedObj).some(
          //   ([key, value]) => !keyNotToCheck.includes(key) && value === ""
          // );
          // if (emptyFieldFound) {
          //   toast.error("Table fields can't be empty.");
          //   setLoading(false);
          //   return;
          // }

          const selectedIndex = updatedSelectedObservations.findIndex(
            (e) =>
              (obj.tempId && e.tempId === obj.tempId) || // match by tempId for new rows
              (obj.sr_no && e.sr_no === obj.sr_no)       // match by sr_no for saved rows
          );

          const allIndex = updatedAllObservations.findIndex(
            (e) =>
              (obj.tempId && e.tempId === obj.tempId) ||
              (obj.sr_no && e.sr_no === obj.sr_no)
          );

          if (selectedIndex !== -1) {
            updatedSelectedObservations[selectedIndex] = {
              ...updatedSelectedObservations[selectedIndex],
              ...trimmedObj,
            };
          }

          if (allIndex !== -1) {
            updatedAllObservations[allIndex] = {
              ...updatedAllObservations[allIndex],
              ...trimmedObj,
              is_selected: 1,
            };
          }
        }

        // if (isReportEdited) {
        //   const payload = editedObservations
        //     .map(({ sr_no, score, edited_fields, tempId, ...rest }) => ({
        //       ...trimFields(rest),
        //       // image: rest.image ,
        //       edited_fields: edited_fields || [],
        //     }))
        //     .filter(item => Object.keys(item).length > 0);
        //   // console.log("Payload to send:", payload);  
        //   if (payload.length > 0) {
        //     await axios.post(`${config.PATH}/api/insert-new-row`, payload);
        //   }
        // }
      }

      updatedSelectedObservations = regroupByArea(updatedSelectedObservations);
      updatedAllObservations = regroupByArea(updatedAllObservations);
      // console.log("updatedSelectedObservationsbefore", selectedObservations);
      // console.log("updateAllObservation", updatedAllObservations)

      requestAnimationFrame(() => {
        setSelectedObservations(updatedSelectedObservations);
        setObservations(updatedAllObservations);
      });

      // Trim fields in reportData
      const reportData = trimFields({
        report_id: ReportUID,
        user_id: userId,
        date_time: selectedDateTime,
        organization: selectedOrganization
          ? selectedOrganization.label
          : selectedOrganization,
        site: selectedSite ? selectedSite.label : selectedSite,
        org_id: selectedOrganization
          ? selectedOrganization.value
          : selectedOrganization,
        area: selectedArea,
        category: selectedCategory,
        background_brief: backgroundBrief,
        classification_of_audit_observations: classificationOfAuditObservations,
        audit_score_analysis: auditScoreAnalysis,
        improvement_opportunity_areas: improvementOpportunityAreas,
        overall_assessment_indicator: overallAssessmentIndicator,
        contents: contents,
        exe_summary: exeSummary,
        conclusion: conclusion,
        best_practice: bestPractice,
        the_way_forward: theWayForward,
        is_edited: isReportEdited,
        other_details: otherDetails,
        is_saved: 1,
        is_complete: complete === true ? true : false,
        start_date: startDate,
        end_date: endDate,
        sector_type: selectedSector?.value || "N/A",
        introduction: introduction,
        time_of_audit_from: timeFrom || null,
        time_of_audit_to: timeTo || null,
        brief_property_description: briefPropertyDescription || null,
        num_of_floors: numOfFloors || null,
        average_staff_footfall: avgStaffFootfall || null,
        no_objection_certificate: noObjectionCertificate || null,
        national_building_code_category: nationalBuildingCodeCategory || null,
        coordinating_person_clientSide: coordinationgPersonClientside || null,
        report_prepared_by: reportPreparedBy || null,
        report_reviewed_by: reportReviewedBy || null,
        building_name: buildingName || null,
      });

      if (module === "cmv") {
        reportData.type = "cmv";
      } else {
        reportData.type = "primary";
      }
      //  console.log("report preview data",reportData)
      const cmvEndPOint = `${config.PATH}/api/save-update-hse-cmv-report`;
      const reportEndPoint = `${config.PATH}/api/save-update-hse-report`;

      if (complete === true) {
        await axios.post(reportEndPoint, reportData);
        await axios.post(cmvEndPOint, reportData);
      }

      await axios.post(reportEndPoint, reportData);
      // console.log("updatedSelectedObservations", updatedSelectedObservations);
      // console.log("updatedAllObservations", updatedAllObservations);


      // Trim fields in observationsData
      const observationsData = trimFields({
        report_id: ReportUID,
        all_observations: updatedSelectedObservations,
        // all_observations: updatedAllObservations,
        organization: selectedOrganization.label,
        site: selectedSite.label,
        user_id: userId,
      });
      const observationEndpoint =
        complete === true
          ? `${config.PATH}/api/save-update-hse-cmv-observations`
          : `${config.PATH}/api/save-update-hse-observations`;

      await axios.post(observationEndpoint, observationsData);
      // console.log("going dta",observationsData)
      // console.log("selected",selectedObservations)

      setIsSaved(true);
      setEditedObservations([]);
      setCurrentEditedRow(-1);
      setIsEditing(false);
      setDisableSaveNext(false);
      getAllHseData();
      saveCriticalObservations(complete);
      saveFacilityInfo();
      setLoading(false);
      updateOrgReportStatus();
      updateSiteReportStatus();
      setEditedFields([]);
      setConfirmationShown(false);
      toast.success(
        `${complete === true ? "Report Completed and Saved" : "Report Saved"}`
      );
      // console.log("updatedSelectedObservations after", selectedObservations);
      // console.log("updateAllObservation", updatedAllObservations)
    } catch (error) {
      console.log("Error saving report:", error);
      setLoading(false);
    }
  };

  const saveCriticalObservations = async (complete) => {
    try {
      // Create a new array with objects excluding the 'id' field
      const observationsToSave = criticalObservations.map(
        ({ id, ...observation }) => observation
      );

      // if (observationsToSave.length === 0) {
      //   console.log("No observations to save.");
      //   return;
      // }

      const payload = {
        criticalObservations: observationsToSave,
        report_id: ReportUID,
      };

      const endpoint =
        complete === true
          ? `${config.PATH}/api/save-critical-hse-cmv-observations`
          : `${config.PATH}/api/save-hse-critical-observations`;

      await axios.post(endpoint, payload);

      // console.log("Critical observations saved successfully.");
    } catch (error) {
      console.error("Error saving critical observations:", error.message);
    }
  };

  const closeReport = () => {
    resetReportContext();
    setOpenModal(false);
    setSelectedObservations([]);
    setRecommendations([]);
    setSelectedArea([]);
    setSelectedCategory([]);
    setSelectedOrganization([]);
    setSelectedSite([]);
    setAreasToDisplay([]);
    setCategoriesToDisplay([]);
    setEndDate(null);
    setStartDate(null);
    generateUniqueId();
    getCurrentDateTime();
    setSelectedSector();
    setSelectedParam();
  };

  const handleClose = () => {
    if (isEditing && screenNumber === 10) {
      toast.warning("Please save changes before closing the report.");
      return;
    } else if (!isSaved) {
      toast.warning("Please save the report before closing.");
    } else {
      closeReport();
    }
  };

  // const handleScoreChange = (event, tableType, sr_no) => {
  //   setIsSaved(false);
  //   const newScore = parseInt(event.target.value, 10);

  //   // Validate the new score
  //   if ((newScore >= 0 && newScore <= 5) || event.target.value === "") {
  //     // Clone the observations and selectedObservations arrays
  //     const newObservations = [...observations];
  //     const newSelectedObservations = [...selectedObservations];

  //     // Find the relevant observation in observations using either sr_no or id
  //     const globalIndex = newObservations.findIndex(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );

  //     if (globalIndex !== -1) {
  //       newObservations[globalIndex].score =
  //         event.target.value === "" ? null : newScore;

  //       // Log the updated observation
  //       console.log("Updated Observation:", newObservations[globalIndex]);
  //     } else {
  //       console.log("Observation not found in observations");
  //     }

  //     // Find the relevant observation in selectedObservations using either sr_no or id
  //     const selectedIndex = newSelectedObservations.findIndex(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );

  //     if (selectedIndex !== -1) {
  //       newSelectedObservations[selectedIndex].score =
  //         event.target.value === "" ? null : newScore;

  //       // Log the updated selected observation
  //       console.log("Updated Selected Observation:", newSelectedObservations[selectedIndex]);
  //     } else {
  //       console.log("Observation not found in selectedObservations");
  //     }
  //     // Update the state with the modified arrays
  //     setObservations(newObservations);
  //     setSelectedObservations(newSelectedObservations);
  //   } else {
  //     alert("Invalid score. Please enter a value between 0 and 5.");
  //     // Find the observation to restore the original score in the input field
  //     const originalObservation = observations.find(
  //       (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
  //     );
  //     event.target.value = originalObservation ? originalObservation.score : "";
  //   }
  // };
  const handleScoreChange = (event, tableType, sr_no, index) => {
    setIsSaved(false);
    const newScore = parseInt(event.target.value, 10);

    if ((newScore >= 0 && newScore <= 5) || event.target.value === "") {
      const newObservations = [...observations];
      const newSelectedObservations = [...selectedObservations];

      const globalIndex = newObservations.findIndex(
        (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
      );

      if (globalIndex !== -1) {
        newObservations[globalIndex].score =
          event.target.value === "" ? null : newScore;
      }

      const selectedIndex = newSelectedObservations.findIndex(
        (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
      );

      if (selectedIndex !== -1) {
        newSelectedObservations[selectedIndex].score =
          event.target.value === "" ? null : newScore;
      }

      setIsEditing(true);
      setCurrentEditedRow(sr_no);
      setIsReportEdited(true);

      const updatedEdited = [...editedObservations];
      const existingIndex = updatedEdited.findIndex((obs) => obs.sr_no === sr_no);

      const newScoreValue = event.target.value === "" ? null : newScore;

      if (existingIndex !== -1) {
        updatedEdited[existingIndex] = {
          ...updatedEdited[existingIndex],
          score: newScoreValue,
        };
      } else {
        const obsToClone = newObservations[globalIndex];
        if (obsToClone) {
          updatedEdited.push({
            ...obsToClone,
            score: newScoreValue,
          });
        }
      }

      setObservations(newObservations);
      setSelectedObservations(newSelectedObservations);
      setEditedObservations(updatedEdited);
    } else {
      alert("Invalid score. Please enter a value between 0 and 5.");
      const originalObservation = observations.find(
        (obs) => obs.table_type === tableType && (obs.sr_no === sr_no || obs.id === sr_no)
      );
      event.target.value = originalObservation ? originalObservation.score : "";
    }
  };


  const handleDuplicateRow = (table_type, area, identifier) => {
    if (isEditing) {
      toast.warn(
        "Please finish editing the current row before adding a new row."
      );
      return;
    }

    // Find the row to duplicate
    const selectedOriginalRow = selectedObservations.find(
      (obs) =>
        obs.table_type === table_type &&
        obs.area === area &&
        (obs.sr_no === identifier || obs.id === identifier)
    );

    if (!selectedOriginalRow) {
      toast.error("Observation not found.");
      return;
    }

    // Generate a unique identifier based on the current timestamp
    const newSrNo = Date.now() % 1000000; // Using timestamp as unique identifier

    // Create a duplicated row with the new unique identifier
    const duplicatedRowForSelected = {
      ...selectedOriginalRow,
      sr_no: newSrNo,
      refIndex: (selectedOriginalRow.refIndex || 0) + 1, // Adjust refIndex or handle it based on your requirement
      imageUrls: selectedOriginalRow.imageUrls
        ? [...selectedOriginalRow.imageUrls]
        : [],
    };

    // Create copies of state arrays
    const updatedAllObservations = [...observations];
    const updatedSelectedObservations = [...selectedObservations];

    // Insert duplicated row into observations and selectedObservations
    updatedAllObservations.splice(selectedOriginalRow.refIndex + 1, 0, {
      ...duplicatedRowForSelected,
      is_selected: 1,
    });
    updatedSelectedObservations.splice(
      updatedSelectedObservations.findIndex(
        (obs) =>
          obs.table_type === table_type &&
          obs.area === area &&
          (obs.sr_no === identifier || obs.id === identifier)
      ) + 1,
      0,
      duplicatedRowForSelected
    );

    // Update state
    setObservations(updatedAllObservations);
    setSelectedObservations(updatedSelectedObservations);

    toast.success("New row added");
  };

  const handleDeleteRow = (table_type, area, identifier, bypassEditGuard = false) => {
    if (!bypassEditGuard && isEditing && currentEditedRow !== -1) {
      toast.warn(
        "Please finish editing the current row before deleting the row."
      );
      return;
    }

    // Create deep copies to avoid mutation
    const updatedSelectedObservations = [...selectedObservations];
    let updatedAllObservations = [...observations]; // Use let instead of const

    // Check if there is only one row left
    // if (updatedSelectedObservations.length === 1) {
    //   toast.warn("Cannot delete the last row.");
    //   return;
    // }
    
    // Find the index to delete using table_type, area, and identifier (sr_no or id)
    const rowIndex = updatedSelectedObservations.findIndex(
      (obs) =>
        obs.table_type === table_type &&
        obs.area === area &&
        (obs.sr_no === identifier || obs.id === identifier)
    );

    if (rowIndex === -1) {
      toast.error("Row not found.");
      return;
    }

    // Find the observation to delete
    const observationToDelete = updatedSelectedObservations[rowIndex];
    const refIndex = observationToDelete.refIndex;

    // Remove the observation from both arrays
    updatedSelectedObservations.splice(rowIndex, 1);
    updatedAllObservations = updatedAllObservations.filter(
      (obs) =>
        !(
          obs.table_type === table_type &&
          obs.area === area &&
          (obs.sr_no === identifier || obs.id === identifier)
        )
    );

    // Adjust refIndex for the remaining observations
    updatedSelectedObservations.forEach(
      (e) => (e.refIndex = e.refIndex > refIndex ? e.refIndex - 1 : e.refIndex)
    );

    setSelectedObservations(updatedSelectedObservations);
    setObservations(updatedAllObservations);
    setIsEditing(false);
    setEditedObservations([]);
    setCurrentEditedRow(-1);
    toast.error("Row deleted");
    // Calculate remaining rows for this table type
    const remainingRows = updatedSelectedObservations.filter(o => o.table_type === table_type);

    setSafeGroupedObservations((prev) => {
      const updated = { ...prev };
      // const remainingRows = updatedSelectedObservations.filter(o => o.table_type === table_type);

      if (remainingRows.length === 0) {
        delete updated[table_type];
      } else {
        updated[table_type] = remainingRows;
      }
      return updated;
    });
    // Remove the deleted table type from selectedTableType
    // setSelectedTableType((prev) => prev.filter(t => t.value !== table_type));
    if (remainingRows.length === 0) {
      setSelectedTableType((prev) => prev.filter(t => t.value !== table_type));
    }

    setNewObservationAdded(true);
  };
  
  useEffect(() => {
    if(screenNumber === 10 && newObservationAdded){
      handleSave();
      setNewObservationAdded(false);
      console.log('---------- auto save --------');
    }
  }, [newObservationAdded]);


  const getScoreColor = (percentage) => {
    if (percentage <= 33) {
      return "#FF0000"; // Red
    } else if (percentage > 33 && percentage <= 66) {
      return "#FFA500"; // Orange
    } else {
      return "#006400"; // Dark Green
    }
  };

  // const pieOptions = {
  //   labels: [
  //     `Obtained Score(${scorePercent}%)`,
  //     `Remaining Score(${100 - scorePercent}%)`,
  //   ],
  //   colors: [getScoreColor(scorePercent), "grey"],
  //   dataLabels: {
  //     enabled: true,
  //   },
  //   legend: {
  //     show: true,
  //     position: "bottom",
  //     horizontalAlign: "center",
  //     fontSize: "14px",
  //     markers: {
  //       width: 16,
  //       height: 16,
  //     },
  //   },
  //   responsive: [
  //     {
  //       breakpoint: 480,
  //       options: {
  //         chart: {
  //           width: 200,
  //         },
  //         legend: {
  //           position: "bottom",
  //         },
  //       },
  //     },
  //   ],
  // };

  const pieOptions = {
    chart: {
      type: "pie",
      animations: {
        enabled: false, // IMPORTANT FIX
      },
    },

    labels: ["Score", "Remaining"],

    tooltip: {
      enabled: true,
    },

    legend: {
      position: "bottom",
    },

    plotOptions: {
      pie: {
        dataLabels: {
          // Pull the percentage labels in toward the center so they sit
          // inside their slice instead of crossing the circle's outline
          // (a small slice like "Remaining" otherwise lands right on the rim).
          offset: -25,
          // Drop the label on very thin slices so it can't spill over the edge.
          minAngleToShowLabel: 12,
        },
      },
    },

    dataLabels: {
      enabled: true,
      // Hide the in-slice label for thin slices (< 10%); a small
      // "Remaining" sliver's value is shown outside the circle instead
      // (see the pie render below). Larger slices keep their % label.
      formatter: function (val) {
        return val < 10 ? "" : `${val.toFixed(1)}%`;
      },
    },

    noData: {
      text: "No Data",
    },
  };

  const areaCounts = data.reduce((counts, entry) => {
    const area = entry.area;
    counts[area] = (counts[area] || 0) + 1;
    return counts;
  }, {});

  const areasForAreaChart = Object.keys(areaCounts);

  const counts = areasForAreaChart.map((area) => {
    // Skip "NA" (not applicable) rows: their status yields max_pts = 0, so they
    // shouldn't count toward the area's earned score or its possible total.
    const areaObservations = data.filter(
      (entry) => entry.area === area && parseFloat(entry.max_pts || 0) > 0
    );
    const areaScore = areaObservations.reduce(
      (acc, entry) => acc + parseFloat(entry.score || 0),
      0
    );
    // Use each observation's own max_pts (matches the overall score calc in
    // fetchDataForCharts) instead of assuming a fixed 5 points per observation,
    // so the per-area bars reconcile with the overall Audit Score.
    const totalPossibleScore = areaObservations.reduce(
      (acc, entry) => acc + parseFloat(entry.max_pts || 0),
      0
    );
    const percentage =
      totalPossibleScore > 0
        ? Math.floor((areaScore / totalPossibleScore) * 100)
        : 0;
    return percentage.toFixed(0);
  });

  const barOptions = {
    chart: {
      id: "bar-chart",
    },
    colors: ["#005cdb"],
    xaxis: {
      categories: areasForAreaChart.map((area) => {
        // Truncate the area name if it's too long
        const maxChars = 15; // Adjust the maximum characters as needed
        return area.length > maxChars
          ? area.substring(0, maxChars) + "..."
          : area;
      }),
      labels: {
        style: {
          fontSize: "10px", // Adjust the font size as needed
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%",
        dataLabels: {
          position: "top", // top, center, bottom
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val;
      },
      offsetY: -20,
      style: {
        fontSize: "10px",
        colors: ["#304758"],
      },
    },
    tooltip: {
      y: {
        formatter: function (value, { dataPointIndex }) {
          // Display the full area name on hover
          const area = areasForAreaChart[dataPointIndex];
          return `${area}: ${value}%`; // Show area name along with percentage
        },
      },
    },
  };

  // Group the data by area and severity
  const areaSeverityData = data.reduce((result, entry) => {
    const area = entry.area;
    const severity = entry.criticality;

    if (!result[area]) {
      result[area] = { High: 0, Medium: 0, Low: 0 };
    }

    result[area][severity] += 1;
    return result;
  }, {});

  // Extract areas and severity counts
  const areas = Object.keys(areaSeverityData);

  const severityChartData = Object.keys(areaSeverityData).map((area) => ({
    name: area,
    High: areaSeverityData[area].High,
    Medium: areaSeverityData[area].Medium,
    Low: areaSeverityData[area].Low,
  }));

  // Transpose the data to match the series structure
  const transposedData = {
    High: [],
    Medium: [],
    Low: [],
  };

  severityChartData.forEach((area) => {
    transposedData.High.push(area.High);
    transposedData.Medium.push(area.Medium);
    transposedData.Low.push(area.Low);
  });

  // Prepare series data with specific colors for each severity level
  const seriesData = [
    { name: "High", data: [] },
    { name: "Medium", data: [] },
    { name: "Low", data: [] },
  ];

  areas.forEach((area) => {
    seriesData[0].data.push(areaSeverityData[area]?.High || 0);
    seriesData[1].data.push(areaSeverityData[area]?.Medium || 0);
    seriesData[2].data.push(areaSeverityData[area]?.Low || 0);
  });

  // Severity chart options
  const severityChartOptions = {
    chart: {
      id: "severity-chart",
      stacked: true,
    },
    xaxis: {
      categories: areas.map((area) => {
        // Truncate the area name if it's too long
        const maxChars = 15; // Adjust the maximum characters as needed
        return area.length > maxChars
          ? area.substring(0, maxChars) + "..."
          : area;
      }),
      labels: {
        style: {
          fontSize: "10px", // Adjust the font size as needed
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%",
        dataLabels: {
          total: {
            enabled: true,
            style: {
              fontSize: "10px", // Adjust the font size for the total data label
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false, // Hide data labels
    },
    colors: ["#FF0000", "#006400", "#005cdb"],
    tooltip: {
      y: {
        formatter: function (value, { dataPointIndex }) {
          // Display the full area name on hover
          const area = areas[dataPointIndex];
          return `${area}:${value}`;
        },
      },
    },
  };

  const handleObservationEdit = (index, e) => {
    // setDisableSaveNext(true);
    // const updatedObservations = [...criticalObservations];
    // updatedObservations[index].observation = e.target.value;
    const updatedObservations = criticalObservations.map((obs, i) =>
      i === index ? { ...obs, observation: e.target.value } : obs
    );
    setCriticalObservations(updatedObservations);
  };

  const updateOrgReportStatus = () => {
    try {
      const org_id = selectedOrganization.value;
      axios.post(`${config.PATH}/api/update-hse-org-report-status/${org_id}`);
    } catch (error) {
      console.log(error.message);
    }
  };

  const updateSiteReportStatus = () => {
    try {
      const org_id = selectedOrganization.value;
      const site_name = selectedSite.value;
      axios.post(
        `${config.PATH}/api/update-hse-site-report-status/${org_id}/${site_name}`
      );
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleComplete = async () => {
    if (!isSaved) {
      toast.warning("Please save the report before marking as complete.");
      return;
    }
    const userConfirmed = window.confirm(
      `Are you sure you want to mark this report as complete? Once done action cannot be reversed and you cannot edit the report.`
    );

    if (!userConfirmed) {
      return;
    }
    setIsComplete(true);
    handleSave(true);
    closeReport();
  };

  useEffect(() => {
    const uniqueCategories = new Set();
    const filteredOptions = [];

    allData.data.forEach((e) => {
      if (area.includes(e.area) && !uniqueCategories.has(e.category)) {
        filteredOptions.push({ value: e.category, label: e.category });
        uniqueCategories.add(e.category);
      }
    });

    setCategoryOptions(filteredOptions);
  }, [getAllHseData, area]);

  const customSelectStylesCreatable = {
    control: (provided) => ({
      ...provided,
      // borderBottom: "none", // Hide the separator
      boxShadow: "none", // Hide the box shadow
      cursor: "pointer", // Show the pointer cursor
      width: "150px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6d6d6d",   // match MUI placeholder
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      borderLeft: 0, // Hide the vertical separator beside the dropdown indicator
      padding: 0,
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999, // Increase the z-index
      position: "absolute",
    }),
  };

  const closeVariantModal = () => {
    setOpenVariantModal(false);
  };

  const handleConfirmSelection = (selectedVariants, removedItems) => {
    let map = {};

    const getId = (item) => item.sr_no || item.id;

    // Update both states with the selected variants
    setSelectedObservations(
      [...selectedObservations, ...selectedVariants].filter((e) => {
        const id = getId(e);
        if (!(map[id] || removedItems.includes(id))) {
          map[id] = true;
          return true;
        } else {
          return false;
        }
      })
    );

    map = {};
    setObservations(
      [...observations, ...selectedVariants].filter((e) => {
        const id = getId(e);
        if (!(map[id] || removedItems.includes(id))) {
          map[id] = true;
          return true;
        } else {
          return false;
        }
      })
    );
  };

  const getObservationVariants = async (observation, index) => {
    try {
      if (isEditing && currentEditedRow !== index) {
        toast.warn("Please finish editing the current row.");
        return;
      }
      setOpenVariantModal(true);
      const payload = {
        observation: observation,
        report: "new",
      };
      const response = await axios.post(
        `${config.PATH}/api/search-hse-data-by-observation`,
        payload
      );
      setObservationVariants(response.data);
    } catch (e) {
      console.log(e);
    }
  };

  const criticalityOptions = [
    { value: "High", label: "High" },
    { value: "Medium", label: "Medium" },
    { value: "Low", label: "Low" },
  ];

  // const groupedObservations = (selectedObservations || []).reduce((acc, observation) => {
  //   if (!acc[observation.table_type]) {
  //     acc[observation.table_type] = [];
  //   }
  //   acc[observation.table_type].push(observation);
  //   return acc;
  // }, {});
  // const safeGroupedObservations = { ...groupedObservations };
  // if (!safeGroupedObservations["Default"]) {
  //   safeGroupedObservations["Default"] = [];
  // }
  // const [selectedTableType, setSelectedTableType] = useState("");
  // const [tableTypes, setTableTypes] = useState([]);
  // useEffect(() => {
  //   if (allData?.data?.length > 0) {
  //     const types = [
  //       ...new Set(
  //         allData.data
  //           .map((d) => {
  //             if (typeof d.table_type === "object" && d.table_type !== null) {
  //               return d.table_type.value || d.table_type.type || ""; 
  //             }
  //             return d.table_type; 
  //           })
  //           .filter(Boolean)
  //           .map(String) // ensure all are strings
  //       ),
  //     ];
  //   //  console.log("types",types)
  //     setTableTypes(types);

  //     if (types.length > 0) {
  //       setSelectedTableType(types[0]); // default selection
  //     }
  //   }
  // }, [allData]);
  //   console.log("Tbaletypes",tableTypes)
  //   // console.log("selectedtabl;e",setSelectedTableType)
  //   const handleTableTypeChange = (event) => {
  //     setSelectedTableType(event.target.value);
  //   };
  // const [selectedTableType, setSelectedTableType] = useState([]);
  // const [tableTypes, setTableTypes] = useState([]);
  // const [safeGroupedObservations, setSafeGroupedObservations] = useState({});

  useEffect(() => {
    const groupedObservations = (selectedObservations || []).reduce((acc, observation) => {
      const key = observation.table_type;
      if (!key) return acc;
      if (!acc[key]) acc[key] = [];
      acc[key].push(observation);
      return acc;
    }, {});

    // if (!groupedObservations["Default"]) groupedObservations["Default"] = [];

    // setSafeGroupedObservations(groupedObservations);
    //    setSafeGroupedObservations((prev) => ({
    //   ...prev, // keep old tables (even if empty)
    //   ...groupedObservations, // overwrite only those that have data
    // }));
    setSafeGroupedObservations((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        updated[key] = groupedObservations[key] || [];
      });
      Object.keys(groupedObservations).forEach((key) => {
        if (!updated[key]) updated[key] = groupedObservations[key];
      });
      return updated;
    });

  }, [selectedObservations]);

  // Initialize new row inputs for each tableType when safeGroupedObservations changes
  useEffect(() => {
    if (safeGroupedObservations && Object.keys(safeGroupedObservations).length > 0) {
      Object.keys(safeGroupedObservations).forEach((tableType) => {
        if (!newRowInputs[tableType]) {
          initializeNewRowInputs(tableType, selectedSector?.value || "N/A");
        }
      });
    }
  }, [safeGroupedObservations]);

  // Extract table types from backend data
  useEffect(() => {
    if (allData?.data?.length > 0 && selectedSector?.value) {
      const filteredData = allData.data.filter(d => d.sector_type === selectedSector?.value);
      const types = [
        ...new Set(
          filteredData
            .map((d) => {
              if (typeof d.table_type === "object" && d.table_type !== null) {
                return d.table_type.value || d.table_type.type || "";
              }
              return d.table_type;
            })
            .filter(Boolean)
            .map(String)
        ),
      ];
      setTableTypes(types);

      if (types.length > 0 && !selectedTableType) {
        setSelectedTableType(types[0]);
      }
    }
  }, [allData, selectedSector]);

  // const handleTableTypeChange = (newValues) => {
  //   const selectedValues = newValues ? newValues.map((v) => v.value) : [];

  //   // detect removed table types
  //   const previousValues = selectedTableType.map(
  //     (v) => v.value
  //   );

  //   const removedTableTypes =
  //     previousValues.filter(
  //       (val) => !selectedValues.includes(val)
  //     );

  //   setSelectedTableType(newValues || []);

  //   // keep created table types
  //   setTableTypes((prev) => {
  //     const all = [
  //       ...new Set([...prev, ...selectedValues]),
  //     ];
  //     return all;
  //   });

  //   // observations
  //   setSafeGroupedObservations((prev) => {
  //     const updated = { ...prev };

  //     Object.keys(updated).forEach((key) => {
  //       if (!selectedValues.includes(key)) {
  //         delete updated[key];
  //       }
  //     });

  //     selectedValues.forEach((val) => {
  //       if (!updated[val]) {
  //         updated[val] = [];
  //       }
  //     });

  //     return updated;
  //   });

  //   setSelectedObservations((prev) =>
  //     prev.filter((obs) =>
  //       selectedValues.includes(obs.table_type)
  //     )
  //   );

  //   setAllObservations((prev) =>
  //     prev.filter((obs) =>
  //       selectedValues.includes(obs.table_type)
  //     )
  //   );

  //   setEditedObservations((prev) =>
  //     prev.filter((obs) =>
  //       selectedValues.includes(obs.table_type)
  //     )
  //   );

  //   // VERY IMPORTANT
  //   // remove ALL cached input state
  //   setNewRowInputs((prev) => {
  //     const updated = { ...prev };

  //     removedTableTypes.forEach((key) => {
  //       delete updated[key];
  //     });

  //     return updated;
  //   });

  //   setNewRowInputValues((prev) => {
  //     const updated = { ...prev };

  //     removedTableTypes.forEach((key) => {
  //       delete updated[key];
  //     });

  //     return updated;
  //   });
  // };

  const handleTableTypeChange = (newValues) => {
    const selectedValues = newValues ? newValues.map(v => v.value) : [];
    setSelectedTableType(newValues || []);

    // setTableTypes((prev) => {
    //   const all = [...new Set([...prev, ...selectedValues])];
    //   return all;
    // });

    setSafeGroupedObservations((prev) => {
      const updated = { ...prev };

      Object.keys(updated).forEach((key) => {
        if (!selectedValues.includes(key)) {
          delete updated[key];
        }
      });

      selectedValues.forEach((val) => {
        if (!updated[val]) updated[val] = [];
      });

      // to perform autosave each observation with table type
      setNewObservationAdded(true);

      return updated;
    });


    setSelectedObservations((prev) =>
      prev.filter((obs) => selectedValues.includes(obs.table_type))
    );

    setAllObservations((prev) =>
      prev.filter((obs) => selectedValues.includes(obs.table_type))
    );

    // Also prune `observations` — handleSave snapshots from this state into
    // the save payload (`all_observations`), so leaving it unpruned causes
    // removed rows to be re-persisted and reappear as selected in Edit mode.
    setObservations((prev) =>
      prev.filter((obs) => selectedValues.includes(obs.table_type))
    );

    setEditedObservations((prev) =>
      prev.filter((obs) => selectedValues.includes(obs.table_type))
    );
    // Clear input values for deselected table types
    setNewRowInputs((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (!selectedValues.includes(key)) {
          delete updated[key];
        }
      });
      return updated;
    });

    setNewRowInputValues((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        if (!selectedValues.includes(key)) {
          delete updated[key];
        }
      });
      return updated;
    });

  };

  const saveFacilityInfo = async () => {
    try {
      const payload = {
        report_id: ReportUID,
        facility_info: facilityInfo,
      };
      const res = await axios.post(
        `${config.PATH}/api/save-facility-info`,
        payload
      );
    } catch (err) {
      console.log(err.message);
    }
  };

  const handleRemoveField = (key) => {
    if (Object.keys(facilityInfo).length === 1) {
      alert("Cannot remove the last field.");
      return;
    }

    const updatedFacilityInfo = { ...facilityInfo };
    delete updatedFacilityInfo[key];
    setFacilityInfo(updatedFacilityInfo);
  };

  const handleChangeNewRow = (tableType, e, field) => {

    if (field === "score" && (e.target.value < 0 || e.target.value > 5)) {
      alert("Invalid score. Please enter a value between 0 and 5.");
      return; // Exit the function if the score is invalid
    }
    if (field == "score" && (e.target.value.includes("."))) {
      return;
    }

    // if (
    //   (field === "area" || field === "category" || field === "criticality") &&
    //   e.target.value.length > 100
    // ) {
    //   alert(`${field} cannot exceed 100 characters.`);
    //   return; // Do not update the state if validation fails
    // }

    setNewRowInputs((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: e.target.value,
      },
    }));
  };

  // const handleChangeNewRowSelect = (tableType, selectedOption, field) => {
  //   const value = selectedOption?.value || "";

  //   // if (value.length > 100) {
  //   //   alert(`${field} cannot exceed 100 characters.`);
  //   //   return;
  //   // }

  //   if (field === "area") {
  //     // Clear dependent fields when area changes
  //     setNewRowInputs((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         area: value,
  //         equipment: "",
  //         category: "",
  //         check_points: "",
  //         criticality: "",
  //         status: "",
  //         observation: "",
  //         recommendations: "",
  //         legal_reference: "",
  //       },
  //     }));
  //     populateNewRowEquipmentList(value, field);
  //   }
  //   if (field === "equipment") {
  //     // Clear dependent fields when equipment changes
  //     setNewRowInputs((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         equipment: value,
  //         category: "",
  //         check_points: "",
  //         criticality: "",
  //         status: "",
  //         observation: "",
  //         recommendations: "",
  //         legal_reference: "",
  //       },
  //     }));
  //     populateNewRowHseCategoryList(value, field, newRowInputs[tableType]?.area);
  //   }
  //   if (field === "category") {
  //     // Clear dependent fields when category changes
  //     setNewRowInputs((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         category: value,
  //         check_points: "",
  //         criticality: "",
  //         status: "",
  //         observation: "",
  //         recommendations: "",
  //         legal_reference: "",
  //       },
  //     }));
  //     populateNewRowCheckpointList(value, field, newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment);
  //   }
  //   if (field === "check_points") {
  //     // Clear dependent fields when checkpoint changes
  //     setNewRowInputs((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         check_points: value,
  //         criticality: "",
  //         status: "",
  //         observation: "",
  //         recommendations: "",
  //         legal_reference: "",
  //       },
  //     }));
  //     populateNewRowCriticalityList(value, field, newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category);
  //     populateNewRowLegalReferenceList(
  //       newRowInputs[tableType]?.area,
  //       newRowInputs[tableType]?.equipment,
  //       newRowInputs[tableType]?.category,
  //       value,
  //       null,
  //       null
  //     );
  //   }
  //   if (field === "criticality") {
  //     // Clear dependent fields when criticality changes
  //     setNewRowInputs((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         criticality: value,
  //         status: "",
  //         observation: "",
  //         recommendations: "",
  //         legal_reference: "",
  //       },
  //     }));
  //     populateNewRowStatusList(
  //       newRowInputs[tableType]?.area,
  //       newRowInputs[tableType]?.equipment,
  //       newRowInputs[tableType]?.category,
  //       value
  //     );
  //   }
  //   if (field === "status") {
  //     // Clear dependent fields when status changes
  //     setNewRowInputs((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         status: value,
  //         observation: "",
  //         recommendations: "",
  //         legal_reference: "",
  //       },
  //     }));
  //     populateNewRowObservationList(
  //       newRowInputs[tableType]?.area,
  //       newRowInputs[tableType]?.equipment,
  //       newRowInputs[tableType]?.category,
  //       newRowInputs[tableType]?.check_points,
  //       newRowInputs[tableType]?.criticality,
  //       value
  //     );
  //     populateNewRowRecommendationList(
  //       newRowInputs[tableType]?.area,
  //       newRowInputs[tableType]?.equipment,
  //       newRowInputs[tableType]?.category,
  //       newRowInputs[tableType]?.check_points,
  //       newRowInputs[tableType]?.criticality,
  //       value
  //     );
  //     populateNewRowLegalReferenceList(
  //       newRowInputs[tableType]?.area,
  //       newRowInputs[tableType]?.equipment,
  //       newRowInputs[tableType]?.category,
  //       newRowInputs[tableType]?.check_points,
  //       newRowInputs[tableType]?.criticality,
  //       value
  //     );
  //   }
  //   if (field === "observation" || field === "recommendations" || field === "legal_reference") {
  //     // No dependent fields to clear
  //     setNewRowInputs((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         [field]: value,
  //       },
  //     }));
  //   }
  // };

  const handleChangeNewRowSelect = (tableType, selectedOption, field) => {
    const value = selectedOption?.value || "";

    // First update selected field
    setNewRowInputs((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: value,
      },
    }));

    // IMPORTANT: clear editable cache values also
    setNewRowInputValues((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: "",
      },
    }));

    if (field === "area") {
      let calculatedScore = 0;
      let calculatedMaxPoints = 0;
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          area: value,
          equipment: "",
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          equipment: "",
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      populateNewRowEquipmentList(value, field);
    }

    if (field === "equipment") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          equipment: value,
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          category: "",
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      populateNewRowHseCategoryList(
        value,
        field,
        newRowInputs[tableType]?.area
      );
    }

    if (field === "category") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          category: value,
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          check_points: "",
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      populateNewRowCheckpointList(
        value,
        field,
        newRowInputs[tableType]?.area,
        newRowInputs[tableType]?.equipment
      );
    }

    if (field === "check_points") {
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          check_points: value,
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          criticality: "",
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      populateNewRowCriticalityList(
        value,
        field,
        newRowInputs[tableType]?.area,
        newRowInputs[tableType]?.equipment,
        newRowInputs[tableType]?.category
      );
    }

    if (field === "criticality") {

      // Calculate score based on criticality and current status (if exists)
      const currentStatus = newRowInputs[tableType]?.status || "";
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";

      if (value && currentStatus) {
        const matchingScore = allScores.find(
          (score) =>
            score.criticality === value?.trim() &&
            score.status === currentStatus?.trim()
        );

        calculatedScore =
          matchingScore?.earned ??
          matchingScore?.score ??
          "0";

        calculatedMaxPoints =
          matchingScore?.max_pts ??
          "0";
      }


      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          criticality: value,
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          status: "",
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      populateNewRowStatusList(
        newRowInputs[tableType]?.area,
        newRowInputs[tableType]?.equipment,
        newRowInputs[tableType]?.category,
        value
      );
    }

    if (field === "status") {
      // Calculate score based on current criticality and new status
      const currentCriticality = newRowInputs[tableType]?.criticality || "";
      let calculatedScore = "0";
      let calculatedMaxPoints = "0";
      if (currentCriticality && value) {
        const matchingScore = allScores.find(
          (score) =>
            score.criticality === currentCriticality?.trim() &&
            score.status === value?.trim()
        );
        calculatedScore = matchingScore?.earned ?? matchingScore?.score ?? "0";
        calculatedMaxPoints = matchingScore?.max_pts ?? "0";
      }


      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          status: value,
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          observation: "",
          recommendations: "",
          legal_reference: "",
          max_pts: calculatedMaxPoints,
          score: calculatedScore,
        },
      }));

      populateNewRowObservationList(
        newRowInputs[tableType]?.area,
        newRowInputs[tableType]?.equipment,
        newRowInputs[tableType]?.category,
        newRowInputs[tableType]?.check_points,
        newRowInputs[tableType]?.criticality,
        value
      );

      populateNewRowRecommendationList(
        newRowInputs[tableType]?.area,
        newRowInputs[tableType]?.equipment,
        newRowInputs[tableType]?.category,
        newRowInputs[tableType]?.check_points,
        newRowInputs[tableType]?.criticality,
        value
      );

      populateNewRowLegalReferenceList(
        newRowInputs[tableType]?.area,
        newRowInputs[tableType]?.equipment,
        newRowInputs[tableType]?.category,
        newRowInputs[tableType]?.check_points,
        newRowInputs[tableType]?.criticality,
        value
      );
    }

    if (
      field === "observation" ||
      field === "recommendations" ||
      field === "legal_reference"
    ) {
      setNewRowInputs((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          [field]: value,
        },
      }));
    }
  };

  const handleInputChange = (tableType, inputValue, field) => {
    setNewRowInputValues((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: inputValue,
      },
    }));
  };

  const handleFieldFocus = (tableType, field) => {
    // When focusing a field with a value, convert it to editable text
    const currentValue = newRowInputs[tableType]?.[field];
    if (currentValue) {
      setNewRowInputValues((prev) => ({
        ...prev,
        [tableType]: {
          ...prev[tableType],
          [field]: currentValue,
        },
      }));
    }
  };

  // const handleFieldBlur = (tableType, field) => {
  //   // Save the edited value on blur
  //   const inputValue = newRowInputValues[tableType]?.[field];
  //   if (inputValue !== undefined && inputValue !== "") {
  //     setNewRowInputs((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         [field]: inputValue.trim(),
  //       },
  //     }));
  //     setNewRowInputValues((prev) => ({
  //       ...prev,
  //       [tableType]: {
  //         ...prev[tableType],
  //         [field]: "",
  //       },
  //     }));
  //   }
  // };

  const handleFieldBlur = (tableType, field) => {
    // Only clear temporary typed value
    // Do NOT save typed text automatically

    setNewRowInputValues((prev) => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [field]: "",
      },
    }));
  };

  const handleAddRow = (tableType) => {
    //console.log('-----CheckIcon')
    const newSrNo = Date.now() % 1000000;

    const newRow = {
      ...newRowInputs[tableType],
      sr_no: newSrNo,
      table_type: tableType,
      is_selected: 1,
      sector_type: selectedSector?.value || "N/A",
      isNew: true,
    };

    // Initially set score to 0, then update using filter function
    let calculatedScore = 0;
    let calculatedMaxPoints = 0;
    // Filter allScores based on criticality and status to get the earned score
    if (newRow.criticality && newRow.status) {
      const matchingScore = allScores.find(
        (score) =>
          score.criticality === newRow.criticality?.trim() &&
          score.status === newRow.status?.trim()
      );
      calculatedScore = matchingScore?.earned ?? matchingScore?.score ?? 0;
      calculatedMaxPoints = matchingScore?.max_pts ?? 0;
    }

    const updatedRow = {
      ...newRow,
      area: newRow.area,
      equipment: newRow.equipment?.trim() || "N/A",
      category: newRow.category?.trim() || "N/A",
      check_points: newRow.check_points?.trim() || "N/A",
      criticality: newRow.criticality?.trim() || "N/A",
      status: newRow.status?.trim() || "N/A",
      observation: newRow.observation,
      recommendations: newRow.recommendations?.trim() || "N/A",
      legal_reference: newRow.legal_reference?.trim() || "N/A",
      max_pts: calculatedMaxPoints,
      score: calculatedScore,
      system_implementation: newRow.system_implementation?.trim() || "N/A",
      imageUrls: newRow.imageUrls?.length > 0 ? newRow.imageUrls : [],
    };

    // Validate the required fields
    const emptyFields = [];
    if (!newRow.area || newRow.area === "") emptyFields.push("Area");
    if (!newRow.equipment || newRow.equipment === "") emptyFields.push("Equipment");
    if (!newRow.category || newRow.category === "") emptyFields.push("Category");
    if (!newRow.check_points || newRow.check_points === "") emptyFields.push("Checkpoint");
    if (!newRow.criticality || newRow.criticality === "") emptyFields.push("Criticality");
    if (!newRow.status || newRow.status === "") emptyFields.push("Status");
    if (!newRow.observation || newRow.observation === "") emptyFields.push("Observation");
    if (!newRow.recommendations || newRow.recommendations === "") emptyFields.push("Recommendations");
    // if(!newRow.legal_reference || newRow.legal_reference === "") emptyFields.push("Legal Reference");

    if (emptyFields.length > 0) {
      toast.warning(`${emptyFields.join(", ")} can't be set empty`)
      return
    }


    setEditedObservations((prevObservations) => [...prevObservations, updatedRow]);
    setIsReportEdited(true);

    setSelectedObservations((prev) => [...prev, updatedRow]);
    setObservations((prev) => [...prev, updatedRow]);
    setAllObservations((prev) => [...prev, updatedRow]);

    initializeNewRowInputs(tableType, selectedSector?.value || "N/A");
    setNewObservationAdded(true);
  };


  const renderNewRowInputs = (tableType) => {
    return (
      <TableRow key={`new-row-${tableType}`}>
        <TableCell>{safeGroupedObservations[tableType]?.length + 1}</TableCell>
        {/* area */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={areaSelectRef}
              placeholder="Area"
              multiline={false}
              options={getAreaOptions(tableType)}
              value={newRowInputs[tableType]?.area || ""}
              onSelect={(val) => handleChangeNewRowSelect(tableType, { label: val, value: val }, "area")}
              onFocus={() => handleFieldFocus(tableType, "area")}
              onBlur={() => handleFieldBlur(tableType, "area")}
              onMenuOpen={() => {
                handleFieldFocus(tableType, "area");
                setIsAreaDropdownOpen(true);
                populateNewRowAreaList(tableType);
              }}
              onMenuClose={() => setIsAreaDropdownOpen(false)}
              menuPortalTarget={document.body}
            />
          </div>
        </TableCell>

        {/* Equipment */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={equipmentSelectRef}
              placeholder="Equipment"
              multiline={false}
              options={getEquipmentOptions(newRowInputs[tableType]?.area)}
              value={newRowInputs[tableType]?.equipment || ""}
              onSelect={(val) => handleChangeNewRowSelect(tableType, { label: val, value: val }, "equipment")}
              onFocus={() => handleFieldFocus(tableType, "equipment")}
              onBlur={() => handleFieldBlur(tableType, "equipment")}
              onMenuOpen={() => {
                handleFieldFocus(tableType, "equipment");
                setIsEquipmentDropdownOpen(true);
                populateNewRowEquipmentList(newRowInputs[tableType]?.area, "equipment");
              }}
              onMenuClose={() => setIsEquipmentDropdownOpen(false)}
              menuPortalTarget={document.body}
            />
          </div>
        </TableCell>

        {/* Category */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={categorySelectRef}
              placeholder="Category"
              multiline={false}
              options={getCategoryOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment)}
              value={newRowInputs[tableType]?.category || ""}
              onSelect={(val) => handleChangeNewRowSelect(tableType, { label: val, value: val }, "category")}
              onFocus={() => handleFieldFocus(tableType, "category")}
              onBlur={() => handleFieldBlur(tableType, "category")}
              onMenuOpen={() => {
                handleFieldFocus(tableType, "category");
                setIsCategoryDropdownOpen(true);
                populateNewRowHseCategoryList(newRowInputs[tableType]?.equipment, "category", newRowInputs[tableType]?.area);
              }}
              onMenuClose={() => setIsCategoryDropdownOpen(false)}
              menuPortalTarget={document.body}
            />
          </div>
        </TableCell>

        {/* Checkpoint */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={checkpointSelectRef}
              placeholder="Check Point"
              multiline={true}
              options={getCheckpointOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category)}
              value={newRowInputs[tableType]?.check_points || ""}
              onSelect={(val) => handleChangeNewRowSelect(tableType, { label: val, value: val }, "check_points")}
              onFocus={() => handleFieldFocus(tableType, "check_points")}
              onBlur={() => handleFieldBlur(tableType, "check_points")}
              onMenuOpen={() => {
                handleFieldFocus(tableType, "check_points");
                setIsCheckpointDropdownOpen(true);
                populateNewRowCheckpointList(newRowInputs[tableType]?.category, "check_points", newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment);
              }}
              onMenuClose={() => setIsCheckpointDropdownOpen(false)}
              menuPortalTarget={document.body}
            />
          </div>
        </TableCell>

        {/* Criticality */}
        {/* <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <Select
              ref={criticalitySelectRef}
              styles={{
                ...customSelectStylesCreatable,
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              placeholder="Criticality"
              options={(() => {
                if (!newRowInputs[tableType]?.check_points) {
                  return []; // No options until checkpoint is selected
                }
                const cachedOptions = getCriticalityOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.check_points);
                const optionsToUse = cachedOptions.length > 0 ? cachedOptions : criticalityOptionList;
                return optionsToUse.map((criticality) => ({
                  label: criticality,
                  value: criticality,
                }));
              })()}
              value={
                newRowInputs[tableType]?.criticality
                  ? { label: newRowInputs[tableType].criticality, value: newRowInputs[tableType].criticality }
                  : null
              }
              isSearchable={false}
              isClearable
              onChange={(e) => handleChangeNewRowSelect(tableType, e, "criticality")}
              onMenuOpen={() => setIsCriticalityDropdownOpen(true)}
              onMenuClose={() => setIsCriticalityDropdownOpen(false)}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>
        </TableCell> */}
        {/* Criticality */}
        <TableCell
          className="editable-cell"
          style={{ height: "100px" }}
        >
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={criticalitySelectRef}
              placeholder="Criticality"
              multiline={false}
              allowCreate={false}
              options={(() => {
                if (
                  !newRowInputs[tableType]?.check_points
                ) {
                  return [];
                }

                const cachedOptions =
                  getCriticalityOptions(
                    newRowInputs[tableType]?.area,
                    newRowInputs[tableType]?.equipment,
                    newRowInputs[tableType]?.category,
                    newRowInputs[tableType]?.check_points
                  );

                return cachedOptions.length > 0
                  ? cachedOptions
                  : criticalityOptionList;
              })()}
              value={
                newRowInputs[tableType]
                  ?.criticality || ""
              }
              onSelect={(val) =>
                handleChangeNewRowSelect(
                  tableType,
                  val
                    ? {
                      label: val,
                      value: val,
                    }
                    : null,
                  "criticality"
                )
              }
              onMenuOpen={() =>
                setIsCriticalityDropdownOpen(true)
              }
              onMenuClose={() =>
                setIsCriticalityDropdownOpen(false)
              }
              menuPortalTarget={document.body}
              isDisabled={false}
            />
          </div>
        </TableCell>

        {/* Status */}
        {/* <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <Select
              ref={statusSelectRef}
              styles={{
                ...customSelectStylesCreatable,
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              placeholder="Status"
              options={(() => {
                if (!newRowInputs[tableType]?.criticality) {
                  return []; // No options until criticality is selected
                }
                const cachedOptions = getStatusOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.criticality);
                const optionsToUse = cachedOptions.length > 0 ? cachedOptions : statusOptionList;
                return optionsToUse.map((status) => ({
                  label: status,
                  value: status,
                }));
              })()}
              value={
                newRowInputs[tableType]?.status
                  ? { label: newRowInputs[tableType].status, value: newRowInputs[tableType].status }
                  : null
              }
              isSearchable={false}
              isClearable
              onChange={(e) => handleChangeNewRowSelect(tableType, e, "status")}
              onMenuOpen={() => setIsStatusDropdownOpen(true)}
              onMenuClose={() => setIsStatusDropdownOpen(false)}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>
        </TableCell> */}

        {/* Status */}
        <TableCell
          className="editable-cell"
          style={{ height: "100px" }}
        >
          <div className="cell-content">
            <MultiLineCreatableSelect
              ref={statusSelectRef}
              placeholder="Status"
              multiline={false}
              allowCreate={false}
              options={(() => {
                if (
                  !newRowInputs[tableType]
                    ?.criticality
                ) {
                  return [];
                }

                const cachedOptions =
                  getStatusOptions(
                    newRowInputs[tableType]?.area,
                    newRowInputs[tableType]
                      ?.equipment,
                    newRowInputs[tableType]
                      ?.category,
                    newRowInputs[tableType]
                      ?.criticality
                  );

                return cachedOptions.length > 0
                  ? cachedOptions
                  : statusOptionList;
              })()}
              value={
                newRowInputs[tableType]?.status ||
                ""
              }
              onSelect={(val) =>
                handleChangeNewRowSelect(
                  tableType,
                  val
                    ? {
                      label: val,
                      value: val,
                    }
                    : null,
                  "status"
                )
              }
              onMenuOpen={() =>
                setIsStatusDropdownOpen(true)
              }
              onMenuClose={() =>
                setIsStatusDropdownOpen(false)
              }
              menuPortalTarget={document.body}
              isDisabled={false}
            />
          </div>
        </TableCell>

        {/* Observations */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Observation"
              multiline={true}
              options={getObservationOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.check_points, newRowInputs[tableType]?.criticality, newRowInputs[tableType]?.status)}
              value={newRowInputs[tableType]?.observation || ""}
              onSelect={(val) => handleChangeNewRowSelect(tableType, { label: val, value: val }, "observation")}
              onFocus={() => handleFieldFocus(tableType, "observation")}
              onBlur={() => handleFieldBlur(tableType, "observation")}
              onMenuOpen={() => {
                handleFieldFocus(tableType, "observation");
                populateNewRowObservationList(
                  newRowInputs[tableType]?.area,
                  newRowInputs[tableType]?.equipment,
                  newRowInputs[tableType]?.category,
                  newRowInputs[tableType]?.check_points,
                  newRowInputs[tableType]?.criticality,
                  newRowInputs[tableType]?.status
                );
              }}
              menuPortalTarget={document.body}
            />
          </div>
        </TableCell>

        {/* Recommendations */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Recommendation"
              multiline={true}
              options={(() => {
                if (!newRowInputs[tableType]?.observation) {
                  return []; // No options until observation is selected
                }
                const raw = getRecommendationOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.check_points, newRowInputs[tableType]?.criticality, newRowInputs[tableType]?.status);
                const cleaned = [...new Set(raw.map((r) => r?.trim()).filter(Boolean))];
                if (cleaned.length === 0) return ["N/A"];
                return cleaned;
              })()}
              value={newRowInputs[tableType]?.recommendations?.trim() || ""}
              onSelect={(val) => handleChangeNewRowSelect(tableType, { label: val, value: val }, "recommendations")}
              onFocus={() => handleFieldFocus(tableType, "recommendations")}
              onBlur={() => handleFieldBlur(tableType, "recommendations")}
              onMenuOpen={() => {
                handleFieldFocus(tableType, "recommendations");
                populateNewRowRecommendationList(
                  newRowInputs[tableType]?.area,
                  newRowInputs[tableType]?.equipment,
                  newRowInputs[tableType]?.category,
                  newRowInputs[tableType]?.check_points,
                  newRowInputs[tableType]?.criticality,
                  newRowInputs[tableType]?.status
                );
              }}
              menuPortalTarget={document.body}
            />
          </div>
        </TableCell>

        {/* Legal Reference */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Legal Reference"
              multiline={true}
              options={(() => {
                if (!newRowInputs[tableType]?.recommendations) {
                  return []; // No options until recommendations is selected
                }
                const raw = getLegalReferenceOptions(newRowInputs[tableType]?.area, newRowInputs[tableType]?.equipment, newRowInputs[tableType]?.category, newRowInputs[tableType]?.check_points, newRowInputs[tableType]?.criticality, newRowInputs[tableType]?.status);
                const cleaned = [...new Set(raw.map((r) => r?.trim()).filter(Boolean))];
                if (cleaned.length === 0) return ["N/A"];
                return cleaned;
              })()}
              value={newRowInputs[tableType]?.legal_reference?.trim() || ""}
              onSelect={(val) => handleChangeNewRowSelect(tableType, { label: val, value: val }, "legal_reference")}
              onFocus={() => handleFieldFocus(tableType, "legal_reference")}
              onBlur={() => handleFieldBlur(tableType, "legal_reference")}
              onMenuOpen={() => {
                handleFieldFocus(tableType, "legal_reference");
                populateNewRowLegalReferenceList(
                  newRowInputs[tableType]?.area,
                  newRowInputs[tableType]?.equipment,
                  newRowInputs[tableType]?.category,
                  newRowInputs[tableType]?.check_points,
                  newRowInputs[tableType]?.criticality,
                  newRowInputs[tableType]?.status
                );
              }}
              menuPortalTarget={document.body}
            />
          </div>
        </TableCell>
        {/* Max Points */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Max Points"
              multiline={false}
              allowCreate={false}
              isScoring={true}
              value={newRowInputs[tableType]?.max_pts ?? 0}
              isDisabled={true}
            />
          </div>
        </TableCell>

        {/* Scores */}
        <TableCell className="editable-cell" style={{ height: "100px" }}>
          <div className="cell-content">
            <MultiLineCreatableSelect
              placeholder="Score"
              multiline={false}
              allowCreate={false}
              isScoring={true}
              value={newRowInputs[tableType]?.score ?? 0}
              isDisabled={true}
            />
          </div>
        </TableCell>

        <TableCell>
          <Tooltips
            title="Please add the row to upload images"
            placement="top"
            enterTouchDelay={0}
            leaveTouchDelay={2500}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [40, 0],
                    },
                  },
                ],
              },
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "6px 0", border: "1px solid grey", borderRadius: "5px", width: "100px", cursor: "not-allowed", position: "relative", background: "#f0f0f0", opacity: 0.6 }}>
              <CloudUploadOutlinedIcon size={30} />
            </div>
          </Tooltips>
        </TableCell>

        <TableCell>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CheckIcon
              onClick={() => handleAddRow(tableType)}
              style={{ cursor: "pointer", color: "green" }}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  };

  // const addObservation = () => {   //it adds a new line of observation in critical observation screen 7 to make it point wise
  //   setCriticalObservations([
  //     ...criticalObservations,
  //     { observation: otherDetails },
  //   ]);
  //   setOtherDetails(""); // Clear the text area after adding
  // };
  const addObservation = () => {
    // Check if there's already any empty observation
    const hasEmptyObservation = criticalObservations.some(
      (obs) => obs.observation.trim() === ""
    );

    if (hasEmptyObservation) {
      toast.warning("Please fill the existing empty critical observation before adding a new one!");
      return;
    }

    // Prepare new observation: either from user input or blank
    const newObservation = {
      observation: otherDetails.trim() !== "" ? otherDetails.trim() : ""
    };

    // Add to the end of the list
    const updatedManual = [...manualCriticalObservations, newObservation];
    setManualCriticalObservations(updatedManual);
    // setCriticalObservations([...criticalObservations, newObservation]);
    const clonedCritical = criticalObservations.map((obs) => ({ ...obs }));
    setCriticalObservations([...clonedCritical, { ...newObservation }]);

    setHasEditedCriticalObservations(true);
    setOtherDetails(""); // Clear the input

  };
  const editor = useRef(null);
  const contentEditor = useRef(null);
  const introductionEditor = useRef(null);
  const exeSummaryEditor = useRef(null);
  const bestPracticeEditor = useRef(null);
  const wayForwardPlanEditor = useRef(null);
  const conclusionEditor = useRef(null);
  const classificationEditor = useRef(null);
  const auditScoreEditor = useRef(null);
  const improvementOpportunityEditor = useRef(null);
  const overallAssessmentEditor = useRef(null);
  const areaSelectRef = useRef(null);
  const criticalitySelectRef = useRef(null);
  const equipmentSelectRef = useRef(null);
  const categorySelectRef = useRef(null);
  const checkpointSelectRef = useRef(null);
  const statusSelectRef = useRef(null);
  const tableRef = useRef(null);
  const [isAreaDropdownOpen, setIsAreaDropdownOpen] = useState(false);
  const [isCriticalityDropdownOpen, setIsCriticalityDropdownOpen] = useState(false);
  const [isEquipmentDropdownOpen, setIsEquipmentDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isCheckpointDropdownOpen, setIsCheckpointDropdownOpen] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);

  const editAreaSelectRef = useRef(null);
  const editCriticalitySelectRef = useRef(null);
  const editEquipmentSelectRef = useRef(null);
  const editCategorySelectRef = useRef(null);
  const editCheckpointSelectRef = useRef(null);
  const editStatusSelectRef = useRef(null);
  const [isEditAreaDropdownOpen, setIsEditAreaDropdownOpen] = useState(false);
  const [isEditCriticalityDropdownOpen, setIsEditCriticalityDropdownOpen] = useState(false);
  const [isEditEquipmentDropdownOpen, setIsEditEquipmentDropdownOpen] = useState(false);
  const [isEditCategoryDropdownOpen, setIsEditCategoryDropdownOpen] = useState(false);
  const [isEditCheckpointDropdownOpen, setIsEditCheckpointDropdownOpen] = useState(false);
  const [isEditStatusDropdownOpen, setIsEditStatusDropdownOpen] = useState(false);

  //
  const [selectedReportDataView, setSelectedReportDataView] = useState({});
  const [openViewReport, setOpenViewReport] = useState(false);
  //

  useEffect(() => {
    const closeDropdownOnScroll = () => {
      if (isAreaDropdownOpen && areaSelectRef.current) {
        areaSelectRef.current.blur();
        setIsAreaDropdownOpen(false);
      }
      if (isCriticalityDropdownOpen && criticalitySelectRef.current) {
        criticalitySelectRef.current.blur();
        setIsCriticalityDropdownOpen(false);
      }
      if (isEquipmentDropdownOpen && equipmentSelectRef.current) {
        equipmentSelectRef.current.blur();
        setIsEquipmentDropdownOpen(false);
      }
      if (isCategoryDropdownOpen && categorySelectRef.current) {
        categorySelectRef.current.blur();
        setIsCategoryDropdownOpen(false);
      }
      if (isStatusDropdownOpen && statusSelectRef.current) {
        statusSelectRef.current.blur();
        setIsStatusDropdownOpen(false);
      }
      if (isEditAreaDropdownOpen && editAreaSelectRef.current) {
        editAreaSelectRef.current.blur();
        setIsEditAreaDropdownOpen(false);
      }
      if (isCriticalityDropdownOpen && criticalitySelectRef.current) {
        criticalitySelectRef.current.blur();
        setIsCriticalityDropdownOpen(false);
      }
      if (isEditEquipmentDropdownOpen && editEquipmentSelectRef.current) {
        editEquipmentSelectRef.current.blur();
        setIsEditEquipmentDropdownOpen(false);
      }
      if (isEditCategoryDropdownOpen && editCategorySelectRef.current) {
        editCategorySelectRef.current.blur();
        setIsEditCategoryDropdownOpen(false);
      }
      if (isEditStatusDropdownOpen && editStatusSelectRef.current) {
        editStatusSelectRef.current.blur();
        setIsEditStatusDropdownOpen(false);
      }
    };

    const tableElement = tableRef.current;
    if (tableElement) {
      tableElement.addEventListener("scroll", closeDropdownOnScroll);
      tableElement.addEventListener("wheel", closeDropdownOnScroll, { passive: true });
      tableElement.addEventListener("touchmove", closeDropdownOnScroll, { passive: true });
    }

    return () => {
      if (tableElement) {
        tableElement.removeEventListener("scroll", closeDropdownOnScroll);
        tableElement.removeEventListener("wheel", closeDropdownOnScroll);
        tableElement.removeEventListener("touchmove", closeDropdownOnScroll);
      }
    };
  }, [isAreaDropdownOpen, isCriticalityDropdownOpen, isCheckpointDropdownOpen]);



  const handleChangeJoditEditor = (newContent) => {
    setBackgroundBrief(newContent);
  };
  const isTyping = useRef(false);
  const debouncedChange = useRef(
    debounce((value) => {
      setBackgroundBrief(value);
    }, 2000)
  ).current;



  const debouncedSetClassification = useRef(
    debounce((value) => {
      setClassificationOfAuditObservations(value);
    }, 2000)
  ).current;
  const debouncedSetAuditScore = useRef(
    debounce((value) => {
      setAuditScoreAnalysis(value);
    }, 2000)
  ).current;
  const debouncedSetImprovement = useRef(
    debounce((value) => {
      setImprovementOpportunityAreas(value);
    }, 2000)
  ).current;
  const debouncedSetOverallAssessment = useRef(
    debounce((value) => {
      setOverallAssessmentIndicator(value);
    }, 2000)
  ).current;


  const debouncedSetContents = useRef(
    debounce((value) => {
      setContents(value);
    }, 2000),
  ).current;
  const debouncedSetIntroduction = useRef(
    debounce((value) => {
      setIntroduction(value);
    }, 2000),
  ).current;

  const debouncedSetExeSummary = useRef(
    debounce((value) => {
      setExeSummary(value);
    }, 2000),
  ).current;
  const debouncedSetTheWayForward = useRef(
    debounce((value) => {
      setTheWayForward(value);
    }, 2000),
  ).current;
  const debouncedSetConclusion = useRef(
    debounce((value) => {
      setConclusion(value);
    }, 2000),
  ).current;
  const debouncedSetGlobalPractice = useRef(
    debounce((value) => {
      setbestPractice(value);
    }, 2000),
  ).current;

  // ✅ PHASE 1: DEBOUNCE HANDLERS FOR TABLE CELL EDITS (Reduces lag with 20+ rows)
  const debouncedObservationEdit = useMemo(
    () =>
      debounce((e, index, field, originalContent, observationObj) => {
        handleCellEdit(e, index, field, originalContent, observationObj);
      }, 300),
    [handleCellEdit]
  );

  const debouncedRecommendationEdit = useMemo(
    () =>
      debounce((e, index, field, originalContent, observationObj) => {
        handleCellEdit(e, index, field, originalContent, observationObj);
      }, 300),
    [handleCellEdit]
  );

  const debouncedLegalReferenceEdit = useMemo(
    () =>
      debounce((e, index, field, originalContent, observationObj) => {
        handleCellEdit(e, index, field, originalContent, observationObj);
      }, 300),
    [handleCellEdit]
  );

  // Cleanup debounce handlers on unmount
  useEffect(() => {
    return () => {
      debouncedObservationEdit.cancel();
      debouncedRecommendationEdit.cancel();
      debouncedLegalReferenceEdit.cancel();
    };
  }, [debouncedObservationEdit, debouncedRecommendationEdit, debouncedLegalReferenceEdit]);

  // BATCHED SAVE FOR TABLE ROWS (Prevents cascading re-renders on save)
  const handleSaveRowBatch = useCallback(
    (observation, tableType) => {
      // Validate required fields
      if (!observation.area || !observation.equipment || !observation.category ||
        !observation.check_points || !observation.criticality || !observation.status) {
        toast.error("Please fill all required fields before saving.");
        return;
      }

      // Prepare observation with default legal_reference if empty
      const preparedObservation = {
        ...observation,
        legal_reference: observation.legal_reference || "N/A",
      };

      // Batch all state updates together using requestAnimationFrame to prevent cascading re-renders
      requestAnimationFrame(() => {
        setEditedObservations((prev) => {
          const existingIndex = prev.findIndex((obs) => obs.sr_no === observation.sr_no || obs.id === observation.id);
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = preparedObservation;
            return updated;
          }
          return prev;
        });

        setSafeGroupedObservations((prev) => {
          const updated = { ...prev };
          if (updated[tableType]) {
            const obsIndex = updated[tableType].findIndex(
              (obs) => obs.sr_no === observation.sr_no || obs.id === observation.id
            );
            if (obsIndex !== -1) {
              updated[tableType][obsIndex] = preparedObservation;
            }
          }
          return updated;
        });

        setSelectedObservations((prev) => {
          const updated = [...prev];
          const obsIndex = updated.findIndex(
            (obs) => obs.sr_no === observation.sr_no || obs.id === observation.id
          );
          if (obsIndex !== -1) {
            updated[obsIndex] = preparedObservation;
          }
          return updated;
        });

        setObservations((prev) => {
          const updated = [...prev];
          const obsIndex = updated.findIndex(
            (obs) => obs.sr_no === observation.sr_no || obs.id === observation.id
          );
          if (obsIndex !== -1) {
            updated[obsIndex] = { ...preparedObservation, is_selected: 1 };
          }
          return updated;
        });

        // Final state reset
        setIsEditing(false);
        setCurrentEditedRow(-1);
        setEditedFields([]);
        toast.success("Row saved successfully!");
      });
    },
    []
  );

  const riskLevels = [
    {
      range: "≥ 85%",
      risk: "Low Risk",
      color: "#00A651",
      interpretation: "Electrical systems and controls are well maintained, only minor improvements needed.",
    },
    {
      range: "65% – 84%",
      risk: "Medium Risk",
      color: "#FFFF00",
      interpretation: "Controls are adequate but with noticeable weaknesses.",
    },
    {
      range: "25% – 64%",
      risk: "High Risk",
      color: "#FFA500",
      interpretation: "High vulnerabilities with major compliance gaps.",
    },
    {
      range: "< 25%",
      risk: "Severe Risk",
      color: "#FF0000",
      interpretation: "Controls are ineffective, urgent corrective action required.",
    },
  ];
  const getRiskLevel = (score) => {
    if (score >= 85) return riskLevels[0];
    if (score >= 65) return riskLevels[1];
    if (score >= 25) return riskLevels[2];
    return riskLevels[3];
  };

  const currentRisk = getRiskLevel(scorePercent);
  // const currentRisk=35;

  const [references, setReferences] = useState([]);
  const [newHeading, setNewHeading] = useState("");
  // "Add row" drafts, one per section, keyed by section name so each section is
  // managed independently. Shape: { [sectionName]: { document, relevance } }.
  const [newRowsBySection, setNewRowsBySection] = useState({});
  // Annexure ids hidden for THIS report only (report-specific delete).
  const [deletedAnnexureIds, setDeletedAnnexureIds] = useState(
    Array.isArray(reportHeader?.deleted_annexure_ids)
      ? reportHeader.deleted_annexure_ids
      : []
  );

  // Keep hidden-ids in sync when the report header (or its hidden list) changes.
  useEffect(() => {
    if (Array.isArray(reportHeader?.deleted_annexure_ids)) {
      setDeletedAnnexureIds(reportHeader.deleted_annexure_ids);
    } else {
      setDeletedAnnexureIds([]);
    }
  }, [
    reportHeader?.report_id,
    JSON.stringify(reportHeader?.deleted_annexure_ids),
  ]);

  // Annexures the user has NOT hidden for this report — used for view + export.
  const visibleReferences = references.filter(
    (ref) => !deletedAnnexureIds.includes(ref.id)
  );

  // Group references under their section headings, then flatten back to a
  // section-ordered list for the exporters. Shared pure helpers (Saved / Preview
  // / View all use these) — see utils/annexureGrouping + its unit tests.
  const groupedReferences = useMemo(
    () => groupReferences(visibleReferences),
    [visibleReferences]
  );
  const orderedVisibleReferences = useMemo(
    () => orderReferences(groupedReferences),
    [groupedReferences]
  );

  useEffect(() => {
    fetchReferences();
  }, []);

  const fetchReferences = async () => {
    try {
      // const res = await axios.get("http://localhost:5000/api/references");
      const res = await axios.get(
        `${config.PATH}/api/get-all-annexure-details`
      );;

      setReferences(res.data);
    } catch (err) {
      console.error("Error fetching references:", err);
    }
  };

  // Add a data row INTO a specific section. The row carries that section's name
  // so the backend can store and later group / delete it per-section.
  const handleAddSectionRow = async (sectionName) => {
    const draft = newRowsBySection[sectionName] || { document: "", relevance: "" };
    if (!draft.document.trim() || !draft.relevance.trim()) {
      toast.error("Please fill both fields before adding.");
      return;
    }
    try {
      const res = await axios.post(`${config.PATH}/api/create-annexure-details`, {
        document: draft.document,
        relevance: draft.relevance,
        type: "data",
        section_name: sectionName,
      });
      setReferences([...references, res.data]);
      setNewRowsBySection((prev) => ({
        ...prev,
        [sectionName]: { document: "", relevance: "" },
      }));
    } catch (err) {
      // Backend validation (duplicate / spelling / string) returns 400 with a message.
      const msg =
        err?.response?.data?.error || "Error adding annexure. Please try again.";
      toast.error(msg);
      console.error("Error adding reference:", err);
    }
  };

  // Update one field of a section's pending "add row" draft.
  const handleNewRowChange = (sectionName, field, value) => {
    setNewRowsBySection((prev) => ({
      ...prev,
      [sectionName]: {
        document: "",
        relevance: "",
        ...(prev[sectionName] || {}),
        [field]: value,
      },
    }));
  };

  const handleAddHeading = async () => {
    if (!newHeading.trim()) {
      toast.error("Please enter section heading text.");
      return;
    }
    try {
      const res = await axios.post(`${config.PATH}/api/create-annexure-details`, {
        document: null,
        relevance: "",
        type: "heading",
        section_name: newHeading,
      });
      setReferences([...references, res.data]);
      setNewHeading("");
    } catch (err) {
      const msg =
        err?.response?.data?.error || "Error adding heading. Please try again.";
      toast.error(msg);
      console.error("Error adding heading:", err);
    }
  };

  // Report-specific delete: hides the annexure for THIS report only (the global
  // annexure row is preserved and still available to other reports).
  const handleDelete = async (id) => {
    if (!window.confirm("Hide this annexure for this report?")) return;
    if (!ReportUID) {
      toast.error("Please save the report before deleting an annexure.");
      return;
    }
    try {
      const res = await axios.patch(
        `${config.PATH}/api/reports/${ReportUID}/annexure/${id}/hide`
      );
      setDeletedAnnexureIds(
        Array.isArray(res?.data?.deleted_annexure_ids)
          ? res.data.deleted_annexure_ids
          : [...deletedAnnexureIds, id]
      );
    } catch (err) {
      toast.error("Could not delete annexure for this report.");
      console.error("Error hiding reference for report:", err);
    }
  };

  // Report-specific section delete: hides the heading AND every row under it for
  // THIS report only, so no rows are left orphaned with no heading. The global
  // annexure rows are preserved for other reports.
  const handleDeleteSection = async (headingRef) => {
    if (!window.confirm("Hide this section and its rows for this report?")) return;
    if (!ReportUID) {
      toast.error("Please save the report before deleting an annexure.");
      return;
    }
    const group = groupedReferences.find((g) => g.key === headingRef.id);
    const idsToHide = [headingRef.id, ...(group ? group.rows.map((r) => r.id) : [])];
    try {
      // Sequential so each hide builds on the server's prior deleted_annexure_ids
      // (the JSON column is read-modify-write; parallel calls would race).
      let latest = null;
      for (const id of idsToHide) {
        const res = await axios.patch(
          `${config.PATH}/api/reports/${ReportUID}/annexure/${id}/hide`
        );
        if (Array.isArray(res?.data?.deleted_annexure_ids)) {
          latest = res.data.deleted_annexure_ids;
        }
      }
      setDeletedAnnexureIds(
        latest || [...new Set([...deletedAnnexureIds, ...idsToHide])]
      );
    } catch (err) {
      toast.error("Could not delete section for this report.");
      console.error("Error hiding section for report:", err);
    }
  };

  // preview modal
  const handleViewReport = async (report_id) => {  
    try {
      const endpoint =
        module === "cmv"
          ? `${config.PATH}/api/hse-cmv-report/${report_id}`
          : `${config.PATH}/api/hse-report/${report_id}`;
      const report = await axios.get(endpoint);
      setSelectedReportDataView(report.data);
      setOpenViewReport(true);
    } catch (err) {
      toast.error("Please Save your report.");
      console.log(err);
    }
};


  if (screenNumber === 1) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>


                {/* New cover page with HTML */}
                {/* <div className="hse-report-container">
                  <div className="hse-report-cover">
                    <img src={HSE_Cover_New} alt="Cover" />
                  </div>
                  <div className="hse-report-details">
                    <p>Client: {selectedOrganization.label}</p>
                    <p>Location: {selectedSite.label}</p>
                    <p>Service: HSE Audit</p>
                    <p>
                      Date: {startDate.getTime() === endDate.getTime()
                        ? `${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()}`
                        : `${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()} to ${endDate.getDate()}-${endDate.getMonth() + 1}-${endDate.getFullYear()}`}
                    </p>
                    <a href="https://www.momentumindia.in">www.momentumindia.in</a>
                  </div>
                  <div className="hse-report-title">
                    HSE<br />Report
                    <span className="hse-report-year">
                      <span className="line"></span>
                      {new Date().getFullYear()}
                    </span>
                  </div>
                  <div className="hse-report-footer">
                    Prepared by Momentum India
                  </div>
                </div> */}


                {/* <div className="hse-report-container">
                  <div className="hse-report-cover">
                    <img src={HSE_Cover_New} alt="Cover" />
                  </div>
                       <div
  style={{
    position: "absolute",
    top: "350px",
    left: "120px",
    fontSize: "22px",
    fontFamily: "Montserrat",
    color: "#307268",
  }}
>
  {selectedOrganization?.label || ""}
</div>

<div
  style={{
    position: "absolute",
    top: "409px",
    left: "120px",
    fontSize: "22px",
    fontFamily: "Montserrat",
    color: "#307268",
  }}
>
  HSE Audit
</div>

<div
  style={{
    position: "absolute",
    top: "468px",
    left: "126px",
    fontSize: "22px",
    fontFamily: "Montserrat",
    color: "#307268",
  }}
>
  {selectedSite?.label || ""}
</div>

<div
  style={{
    position: "absolute",
    top: "534px",
    left: "90px",
    fontSize: "18px",
    fontFamily: "Montserrat",
    color: "#307268",
  }}
>
  {startDate.getTime() === endDate.getTime()
    ? `${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()}`
    : `${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()} to ${endDate.getDate()}-${endDate.getMonth() + 1}-${endDate.getFullYear()}`}
</div>
                </div> */}
                <div className="hse-report-container">
                  <div className="hse-report-cover">
                    <img src={HSE_Cover_New} alt="Cover" style={{ width: "100%", height: "100%" }} />
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: "593px",
                      left: "144px",
                      fontSize: "20px",
                      fontFamily: "Montserrat",
                      color: "#307268",
                    }}
                  >
                    {selectedOrganization?.label || ""}
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      top: "685px",
                      left: "149px",
                      fontSize: "20px",
                      fontFamily: "Montserrat",
                      color: "#307268",
                    }}
                  >
                    HSE Audit
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      top: "637px",
                      left: "170px",
                      fontSize: "20px",
                      fontFamily: "Montserrat",
                      color: "#307268",
                    }}
                  >
                    {selectedSite?.label || ""}
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      top: "730px",
                      left: "128px",
                      fontSize: "18px",
                      fontFamily: "Montserrat",
                      color: "#307268",
                    }}
                  >
                    {startDate.getTime() === endDate.getTime()
                      ? `${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()}`
                      : `${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()} to ${endDate.getDate()}-${endDate.getMonth() + 1}-${endDate.getFullYear()}`}
                  </div>

                </div>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handleEdit}
                  style={{ background: "#efc71d" }}
                >
                  Back
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  else if (screenNumber === 2) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      UNDERSTANDING THE REPORT
                    </div>

                    <JoditEditor
                      // ref={editor}
                      key={screenNumber}
                      ref={contentEditor}
                      placeholder="Enter your text here"
                      value={contents}
                      config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setContents(newContent)} // still update on blur
                      onChange={(newContent) => {
                        isTyping.current = true;
                        debouncedSetContents(newContent)
                      }}// debounced change
                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 3) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005;
                </button>
              </div>

              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div className="sub-headings">
                    AUDIT DESCRIPTION
                  </div>
                  <div className="review-table-wrapper" style={{ borderRadius: "5px" }}>
                    <table style={{ width: "100%", fontFamily: "montserrat", borderCollapse: "collapse", borderRadius: "5px" }}>
                      <tbody>
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC" }}>Client</td>
                          <td>{selectedOrganization.label}</td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC" }}>Location</td>
                          <td>{selectedSite.label}</td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC" }}>Date of Site Visit</td>
                          <td>{startDate.getDate()}-{startDate.getMonth() + 1
                          }-{startDate.getFullYear()}</td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC" }}>Study</td>
                          <td>HSE Audit Report</td>
                        </tr>
                        {(timeFrom || timeTo) ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}> Time of Audit (From & To)</td>
                            {/* <td>{timeFrom} to {timeTo}</td> */}
                            <td>
                              {
                                (timeFrom && timeTo
                                  ? `${timeFrom} to ${timeTo}`
                                  : timeFrom
                                    ? `${timeFrom} to N/A`
                                    : timeTo
                                      ? `N/A to ${timeTo}`
                                      : "N/A") + (" (Date wise breakup)")}
                            </td>
                          </tr>
                          : []}
                        {briefPropertyDescription ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>Brief Property Description</td>
                            <td>{briefPropertyDescription}</td>
                          </tr>
                          : []}
                        {numOfFloors ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>Number of floors</td>
                            <td>{numOfFloors}</td>
                          </tr>
                          : []}
                        {avgStaffFootfall ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>Average Staff Footfall</td>
                            <td>{avgStaffFootfall}</td>
                          </tr>
                          : []}
                        {noObjectionCertificate ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>No Objection Certificate:</td>
                            <td>{noObjectionCertificate}</td>
                          </tr>
                          : []}
                        {nationalBuildingCodeCategory ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>National Building Code Category</td>
                            <td>{nationalBuildingCodeCategory}</td>
                          </tr>
                          : []}
                        {coordinationgPersonClientside ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>Coordinating Person – Client Side</td>
                            <td>{coordinationgPersonClientside}</td>
                          </tr>
                          : []}
                        {reportPreparedBy ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>Report Prepared By</td>
                            <td>{reportPreparedBy}</td>
                          </tr>
                          : []}
                        {reportReviewedBy ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>Report Reviewed By</td>
                            <td>{reportReviewedBy}</td>
                          </tr>
                          : []}
                        {buildingName ?
                          <tr>
                            <td style={{ backgroundColor: "#FFF8DC" }}>Building Name</td>
                            <td>{buildingName}</td>
                          </tr>
                          : []}
                        <tr>
                          <td style={{ backgroundColor: "#FFF8DC" }}>Date of Submission of Report</td>
                          <td>{endDate.getDate()}-{endDate.getMonth() + 1
                          }-{endDate.getFullYear()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Typography>
              </div>

              <hr />

              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>


      </>
    );
  }
  else if (screenNumber === 4) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      CLASSIFICATION OF AUDIT OBSERVATIONS
                    </div>
                    <JoditEditor
                      key={screenNumber}
                      ref={classificationEditor}
                      placeholder="Enter your text here"
                      value={classificationOfAuditObservations}
                      config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setClassificationOfAuditObservations(newContent)} // This updates backgroundBrief on blur
                      // onChange={handleChangeJoditEditor} // Updates state on every change
                      onChange={(newContent) => {
                        isTyping.current = true;
                        debouncedSetClassification(newContent)
                      }}
                    />

                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 5) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      AUDIT OBJECTIVE
                    </div>
                    <JoditEditor
                      key={screenNumber}
                      ref={editor}
                      placeholder="Enter your text here"
                      value={backgroundBrief}
                      config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setBackgroundBrief(newContent)} // This updates backgroundBrief on blur
                      // onChange={handleChangeJoditEditor} // Updates state on every change
                      onChange={(newContent) => {
                        isTyping.current = true;
                        debouncedChange(newContent)
                      }}
                    />

                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 6) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">EXECUTIVE SUMMARY</div>
                    <JoditEditor
                      key={screenNumber}
                      // ref={editor}
                      ref={exeSummaryEditor}
                      placeholder="Enter your text here"
                      value={exeSummary}
                      config={config}

                      tabIndex={1}
                      onBlur={(newContent) => setExeSummary(newContent)} // This updates backgroundBrief on blur
                      onChange={(newContent) => {
                        isTyping.current = true;
                        debouncedSetExeSummary(newContent)
                      }} // Updates state on every change
                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 7) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      AUDIT SCORE ANALYSIS
                    </div>
                    <JoditEditor
                      key={screenNumber}
                      ref={auditScoreEditor}
                      placeholder="Enter your text here"
                      value={auditScoreAnalysis}
                      config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setAuditScoreAnalysis(newContent)} // This updates backgroundBrief on blur
                      // onChange={handleChangeJoditEditor} // Updates state on every change
                      onChange={(newContent) => {
                        isTyping.current = true;
                        debouncedSetAuditScore(newContent)
                      }}
                    />

                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 8) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">
                      IMPROVEMENT OPPORTUNITY AREAS
                    </div>
                    <JoditEditor
                      key={screenNumber}
                      ref={improvementOpportunityEditor}
                      placeholder="Enter your text here"
                      value={improvementOpportunityAreas}
                      config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setImprovementOpportunityAreas(newContent)}
                      // onChange={handleChangeJoditEditor} // Updates state on every change
                      onChange={(newContent) => {
                        isTyping.current = true;
                        debouncedSetImprovement(newContent)
                      }}
                    />

                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 9) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="sub-headings">
                ANNEXURE - REFERENCES & STANDARDS
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <div className="table-containerr">
                  <table className="styled-tablee">
                    <thead>
                      <tr>
                        <th>Reference Type and Document / Standard</th>
                        <th>Relevance to Audit Findings</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {groupedReferences.map((group) => (
                        <React.Fragment key={group.key}>
                          {/* Section heading row (omitted for the Ungrouped bucket) */}
                          {group.heading && (
                            <tr className="section-headingg">
                              <td colSpan="2">{group.heading.section_name}</td>
                              <td className="action-celll">
                                <DeleteForeverIcon
                                  style={{ cursor: "pointer" }}
                                  className="delete-iconn"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Delete section ${group.section_name || ""} and its rows`}
                                  title="Delete Section"
                                  onClick={() => handleDeleteSection(group.heading)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleDeleteSection(group.heading);
                                    }
                                  }}
                                />
                              </td>
                            </tr>
                          )}

                          {/* Data rows for this section */}
                          {group.rows.map((ref) => (
                            <tr key={ref.id}>
                              <td>{ref.document}</td>
                              <td>{ref.relevance}</td>
                              <td className="action-celll">
                                <DeleteForeverIcon
                                  style={{ cursor: "pointer" }}
                                  className="delete-iconn"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Delete annexure ${ref.document || ""}`}
                                  title="Delete Row"
                                  onClick={() => handleDelete(ref.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleDelete(ref.id);
                                    }
                                  }}
                                />
                              </td>
                            </tr>
                          ))}

                          {/* Per-section "add row" (only for real sections) */}
                          {group.heading && (
                            <tr className="new-roww">
                              <td>
                                <input
                                  type="text"
                                  placeholder="Enter new document..."
                                  aria-label={`New document for section ${group.section_name}`}
                                  value={(newRowsBySection[group.section_name] || {}).document || ""}
                                  onChange={(e) =>
                                    handleNewRowChange(group.section_name, "document", e.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  placeholder="Enter relevance..."
                                  aria-label={`Relevance for new row in section ${group.section_name}`}
                                  value={(newRowsBySection[group.section_name] || {}).relevance || ""}
                                  onChange={(e) =>
                                    handleNewRowChange(group.section_name, "relevance", e.target.value)
                                  }
                                />
                              </td>
                              <td className="action-celll">
                                <FaCheck
                                  className="add-icon"
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Add row to section ${group.section_name}`}
                                  title="Add Row"
                                  onClick={() => handleAddSectionRow(group.section_name)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleAddSectionRow(group.section_name);
                                    }
                                  }}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}

                      {/* Row for Adding Section Header */}
                      <tr className="new-roww">
                        <td colSpan="2">
                          <input
                            type="text"
                            placeholder="Enter section heading..."
                            aria-label="New section heading"
                            value={newHeading}
                            onChange={(e) => setNewHeading(e.target.value)}
                          />
                        </td>
                        <td className="action-celll">
                          <FaCheck
                            className="heading-iconn"
                            role="button"
                            tabIndex={0}
                            aria-label="Add section heading"
                            title="Add Section"
                            onClick={handleAddHeading}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleAddHeading();
                              }
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 10) {
    return (
      <>
        <VariantsModal
          data={observationVariants}
          open={openVairantModal}
          handleClose={closeVariantModal}
          handleConfirmSelection={handleConfirmSelection}
        />
        <ImageViewerModal
          imageUrl={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
        {cropState.open && (
          <ImageCropModal
            open={cropState.open}
            files={cropState.files}
            onCancel={handleCropCancel}
            onComplete={handleCropComplete}
          />
        )}
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <div className="review-modal-subheader">
                  <Typography variant="h5">Preview Report</Typography>
                </div>

                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="sub-headings">
                CRITICAL OBSERVATIONS, RECOMMENDATIONS & REASONING - HSE REPORT
              </div>

              <div style={{ overflowY: "scroll" }} className="review-modal-body">

                <div>
                  {tableTypes.length > 0 && (
                    <CreatableSelect
                      placeholder="Select or type table type..."
                      value={selectedTableType}
                      onChange={handleTableTypeChange}
                      options={tableTypes.map((type) => ({ label: type, value: type }))}
                      isClearable
                      isMulti
                    // formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
                    // isValidNewOption={() => false}
                    />

                  )}
                </div>
                <div className="table-container">
                  {Object.keys(safeGroupedObservations)
                    .filter((tableType) => {
                      if (!selectedTableType?.length) return true;

                      return selectedTableType.some(
                        (item) => item.value === tableType
                      );
                    })
                    .map((tableType, groupIdx) => {
                      const headingId = `hse-preview-obs-group-${groupIdx}-${tableType.replace(/[^a-zA-Z0-9]+/g, "-")}`;
                      return (
                      <div key={tableType}>
                        <Typography id={headingId} style={{ fontWeight: "500", padding: "10px" }} gutterBottom>
                          {tableType}
                          {/* {selectedTableType} */}
                        </Typography>
                        <HseObservationGroup
                          tableType={tableType}
                          rows={safeGroupedObservations[tableType]}
                          headingId={headingId}
                          tableRef={tableRef}
                          renderTrailing={() => renderNewRowInputs(tableType)}
                          renderRow={(observation, index, measureRef) => (
                                  <HseObservationRow
                                    key={`obs-row-${index}`}
                                    measureRef={measureRef}
                                    dataIndex={index}
                                    observation={observation}
                                    index={index}
                                    tableType={tableType}
                                    isEditing={isEditing}
                                    currentEditedRow={currentEditedRow}
                                    selectedObservations={selectedObservations}
                                    safeGroupedObservations={safeGroupedObservations}
                                    selectedObservation={selectedObservation}
                                    openDialog={openDialog}
                                    dialog={dialog}
                                    allData={allData}
                                    areaList={areaList}
                                    equipmentList={equipmentList}
                                    hseCategoryList={hseCategoryList}
                                    checkpointList={checkpointList}
                                    criticalityOptionList={criticalityOptionList}
                                    statusOptionList={statusOptionList}
                                    customSelectStylesCreatable={customSelectStylesCreatable}
                                    editedObservations={editedObservations}
                                    observations={observations}
                                    handleCellEdit={handleCellEdit}
                                    handleDeleteRow={handleDeleteRow}
                                    handleEditNewRow={handleEditNewRow}
                                    handleEditFieldFocus={handleEditFieldFocus}
                                    handleEditFieldBlur={handleEditFieldBlur}
                                    handleOpenImageDialog={handleOpenImageDialog}
                                    handleCloseImageDialog={handleCloseImageDialog}
                                    handleRemoveImage={handleRemoveImage}
                                    handleImageUpload={handleImagePick}
                                    handleDialogBoxObservation={handleDialogBoxObservation}
                                    handleScoreChange={handleScoreChange}
                                    getObservationVariants={getObservationVariants}
                                    isFieldDisabled={isFieldDisabled}
                                    populateHseCategoryList={populateHseCategoryList}
                                    populateCheckpointList={populateCheckpointList}
                                    populateCriticalityList={populateCriticalityList}
                                    populateStatusList={populateStatusList}
                                    setIsEditing={setIsEditing}
                                    setCurrentEditedRow={setCurrentEditedRow}
                                    setNewObservationAdded={setNewObservationAdded}
                                    setIsReportEdited={setIsReportEdited}
                                    setSelectedImage={setSelectedImage}
                                    setIsEditAreaDropdownOpen={setIsEditAreaDropdownOpen}
                                    setIsEditEquipmentDropdownOpen={setIsEditEquipmentDropdownOpen}
                                    setIsEditCategoryDropdownOpen={setIsEditCategoryDropdownOpen}
                                    setIsEditCheckpointDropdownOpen={setIsEditCheckpointDropdownOpen}
                                    setAreaList={setAreaList}
                                    setCheckpointList={setCheckpointList}
                                    setEquipmentList={setEquipmentList}
                                    setHseCategoryList={setHseCategoryList}
                                    setEditedObservations={setEditedObservations}
                                    setEditInputValues={setEditInputValues}
                                    setObservations={setObservations}
                                    setSafeGroupedObservations={setSafeGroupedObservations}
                                    setSelectedObservations={setSelectedObservations}
                                    editAreaSelectRef={editAreaSelectRef}
                                    editEquipmentSelectRef={editEquipmentSelectRef}
                                    editCategorySelectRef={editCategorySelectRef}
                                    editCheckpointSelectRef={editCheckpointSelectRef}
                                  />
                          )}
                        />
                      </div>
                      );
                    })}
                </div>
              </div>
              <hr />
              <div
                style={{ marginBottom: "10px" }}
                className="review-modal-footer"
              >
                <button
                  className="button-styles"
                  onClick={() => handleViewReport(ReportUID)}
                  style={{ background: "#efc71d" }}
                >
                  Preview
                </button>
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={() => handleNext()}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
        {openViewReport && (
          <ViewHseReportSectionBasedDetails
            selectedReportData={selectedReportDataView}
            setOpenViewReport={setOpenViewReport}
            reportHeader={reportHeader}
            screenNumber={10}
            completeReport={false}
          />
        )}
      </>
    );
  }
  else if (screenNumber === 11) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">DASHBOARD</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-bodyy" style={{ overflowY: "auto" }}>
                <div
                  id="chart-container-for-report"
                  className="chart-container-for-report"
                  ref={chartContainerRef}
                >
                  <div className="total-serverity-div-for-report">
                    <div className="severity-item-for-report">
                      Total Observations
                      <br />
                      <span>{data.length}</span>
                      <hr />
                    </div>
                    <div
                      style={{ color: "#FF0000" }}
                      className="severity-item-for-report"
                    >
                      High Severity Observations
                      <br />
                      <span>
                        {data.filter((e) => e.criticality === "High").length}
                      </span>
                      <hr />
                    </div>
                    <div
                      style={{ color: "#006400" }}
                      className="severity-item-for-report"
                    >
                      Medium Severity Observations
                      <br />
                      <span>
                        {data.filter((e) => e.criticality === "Medium").length}
                      </span>
                      <hr />
                    </div>
                    <div
                      style={{ color: "#005cdb" }}
                      className="severity-item-for-report"
                    >
                      Low Severity Observations
                      <br />
                      <span>
                        {data.filter((e) => e.criticality === "Low").length}
                      </span>
                      <hr />
                    </div>
                  </div>
                  <div className="area-chart-for-report">
                    Area Chart
                    <Chart
                      options={barOptions}
                      series={[
                        {
                          name: "",
                          data: counts,
                        },
                      ]}
                      type="bar"
                      height={300}
                    />
                  </div>
                  <div className="severity-chart-for-report">
                    Severity Chart
                    <Chart
                      options={severityChartOptions}
                      series={seriesData}
                      type="bar"
                      height={300}
                    />
                  </div>
                  {/* <div className="pie-chart-for-report">
                    Audit Score
                    <Chart
                      options={pieOptions}
                      series={[scorePercent, 100 - scorePercent]}
                      type="pie"
                      style={{ width: "100%" }}
                      height={250}
                    />
                  </div> */}
                  <div className="pie-chart-for-report">
                    Audit Score

                    {Number.isFinite(scorePercent) && scorePercent >= 0 ? (
                      (() => {
                        const validScore = Math.max(0, Math.min(100, Number(scorePercent) || 0));
                        const remaining = Math.max(0, 100 - validScore);
                        // Thin "Remaining" sliver (< 10%): its in-slice label is
                        // suppressed by the formatter and shown just above the
                        // circle instead, to avoid overlapping the "Score" label.
                        const showRemainingOutside = remaining > 0 && remaining < 10;
                        return (
                          <div
                            style={{
                              position: "relative",
                              paddingTop: showRemainingOutside ? "18px" : 0,
                              // When the "Remaining" label is drawn inside the slice
                              // (> 10%), nudge the chart in slightly so the label
                              // doesn't sit right against the circle's right edge.
                              paddingRight: remaining > 10 ? "5px" : 0,
                            }}
                          >
                            {showRemainingOutside && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: 10,
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  color: "#000000",
                                  // Black text on the light page background keeps the
                                  // value legible; the subtle shadow still mirrors the
                                  // in-slice label drop shadow.
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.85)",
                                  whiteSpace: "nowrap",
                                  zIndex: 2,
                                  pointerEvents: "none",
                                }}
                              >
                                {remaining.toFixed(1)}%
                              </span>
                            )}
                            <Chart
                              key={`audit-score-${scorePercent}`}
                              options={pieOptions}
                              series={[validScore, remaining]}
                              type="pie"
                              width="100%"
                              height={250}
                            />
                          </div>
                        );
                      })()
                    ) : (
                      <div
                        style={{
                          height: "250px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No Data Available
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  disabled={waitForCharts}
                  style={{
                    background: waitForCharts ? "lightgrey" : "#efc71d",
                  }}
                >
                  {waitForCharts ? "Wait..." : "Save"}
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  disabled={waitForCharts}
                  style={{
                    background: waitForCharts ? "lightgrey" : "#efc71d",
                  }}
                >
                  &#171;{waitForCharts ? "Wait..." : "Prev"}
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  disabled={waitForCharts}
                  style={{
                    background: waitForCharts ? "lightgrey" : "#efc71d",
                  }}
                >
                  {waitForCharts ? "Wait..." : "Next"} &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 12) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="sub-headings">
                OVERALL ASSESSMENT INDICATOR
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div className="review-modal-body" style={{ display: "flex", gap: "10px", overflowY: "auto", marginBottom: "20px" }}>

                    {/* Left Risk Section */}
                    <div
                      className="left-section-hse"
                      style={{
                        width: "200px",
                        padding: "10px",
                        borderRadius: "4px",
                        backgroundColor: currentRisk.color,
                        color: "#000",
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {/* <p> 30%</p> */}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <p style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}>{scorePercent}%</p>
                        <p style={{ margin: "4px 0 0 0", fontSize: "12px", fontWeight: "bold" }}>{currentRisk.risk}</p>
                      </div>
                      {/* <p>{Math.floor((cumulativeScore / 10) * 100)}%</p> */}
                    </div>

                    {/* Right JoditEditor */}
                    <div style={{ flexGrow: 1 }}>
                      <JoditEditor
                        key={screenNumber}
                        ref={overallAssessmentEditor}
                        placeholder="Enter your text here"
                        value={overallAssessmentIndicator}
                        config={config}
                        tabIndex={1}
                        onBlur={(newContent) => setOverallAssessmentIndicator(newContent)}
                        onChange={(newContent) => {
                          isTyping.current = true;
                          const plainText = (newContent || "")
                            .replace(/<[^>]*>/g, "")
                            .replace(/\s+/g, "")
                            .replace(/&nbsp;/g, "")
                            .trim();
                          setCharWarning(plainText.length > 4000);
                          debouncedSetOverallAssessment(newContent)
                        }}
                      />
                      {charWarning && (
                        <p style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                          PDF can generate only 4000 characters
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ marginBottom: "20px", overflowX: "auto" }}>
                    <h3 style={{ color: "#307260", fontFamily: "Montserrat" }}>Risk Legend</h3>
                    {/* <p style={{fontFamily: "Montserrat"}}>The above image is per display purpose only</p> */}
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Montserrat" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#307260", color: "#efc71d" }}>
                          <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Score Range</th>
                          <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Risk Level</th>
                          <th style={{ padding: "8px", border: "1px solid #000", fontSize: "14px" }}>Interpretation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskLevels.map((level, index) => (
                          <tr key={index}>
                            <td style={{ textAlign: "center", color: level.color, border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
                              {level.range}
                            </td>
                            <td style={{ textAlign: "center", backgroundColor: level.color, color: "#000", border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
                              {level.risk}
                            </td>
                            <td style={{ border: "1px solid #000", padding: "8px", fontSize: "13px" }}>
                              {level.interpretation}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                </Typography>

              </div>
              <hr />
              <div className="review-modal-footer">
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                <button
                  className="button-styles"
                  color="primary"
                  onClick={handleNext}
                  style={{ background: "#efc71d" }}
                >
                  Next &#187;
                </button>
              </div>
            </div>
          </div>
        </Modal>
      </>
    );
  }
  else if (screenNumber === 13) {
    return (
      <>
        <Modal open={open} onClose={handleClose}>
          <div className="review-modal-container">
            <div className="review-modal-content">
              <div className="review-modal-header">
                <Typography variant="h5">Preview Report</Typography>
                <button className="custom-close-button" onClick={handleClose}>
                  &#10005; {/* Unicode for 'X' */}
                </button>
              </div>
              <div className="review-modal-body" style={{ overflowY: "auto" }}>
                <Typography variant="body1" component="div">
                  <div>
                    <div className="sub-headings">CONCLUSION</div>
                    <JoditEditor
                      key={screenNumber}
                      // ref={editor}
                      ref={conclusionEditor}
                      placeholder="Enter your text here"
                      value={conclusion}
                      config={config}
                      tabIndex={1}
                      onBlur={(newContent) => setConclusion(newContent)} // This updates backgroundBrief on blur
                      onChange={(newContent) => {
                        isTyping.current = true;
                        debouncedSetConclusion(newContent)
                      }} // Updates state on every change
                    />
                  </div>
                </Typography>
              </div>
              <hr />
              <div className="review-modal-footer" id="conclusionFooter">
                <button
                  className="button-styles"
                  onClick={() => handleViewReport(ReportUID)}
                  style={{ background: "#efc71d" }}
                >
                  Preview
                </button>
                <button
                  className="button-styles"
                  onClick={handleSave}
                  style={{ background: "#efc71d" }}
                >
                  Save
                </button>
                <button
                  className="button-styles"
                  onClick={handlePrev}
                  style={{ background: "#efc71d" }}
                >
                  &#171; Prev
                </button>
                {/* <button
                  className="button-styles"
                  onClick={handleComplete}
                  // disabled={isComplete}
                  style={{ background: "#efc71d" }}
                >
                  Complete
                </button> */}
                <ExportSavedReportPDF
                  selectedOrganization={selectedOrganization}
                  selectedSite={selectedSite}
                  backgroundBrief={backgroundBrief}
                  contents={contents}
                  exeSummary={exeSummary}
                  criticalObservations={criticalObservations}
                  selectedObservations={selectedObservations}
                  selectedArea={selectedArea}
                  selectedCategory={selectedCategory}
                  recommendations={recommendations}
                  conclusion={conclusion}
                  selectedDateTime={selectedDateTime.split("T")[0]}
                  reportType="HSE"
                  isSaved={isSaved}
                  chartImage={chartImage}
                  scorePercent={scorePercent}
                  otherDetails={otherDetails}
                  ReportUID={ReportUID}
                  startDate={startDate}
                  endDate={endDate}
                  bestPractice={bestPractice}
                  theWayForward={theWayForward}
                  name={name}
                  facilityInfo={facilityInfo}
                  introduction={introduction}
                  timeFrom={timeFrom}
                  timeTo={timeTo}
                  briefPropertyDescription={briefPropertyDescription}
                  numOfFloors={numOfFloors}
                  avgStaffFootfall={avgStaffFootfall}
                  noObjectionCertificate={noObjectionCertificate}
                  nationalBuildingCodeCategory={nationalBuildingCodeCategory}
                  coordinationgPersonClientside={coordinationgPersonClientside}
                  reportPreparedBy={reportPreparedBy}
                  reportReviewedBy={reportReviewedBy}
                  buildingName={buildingName}
                  classificationOfAuditObservations={classificationOfAuditObservations}
                  auditScoreAnalysis={auditScoreAnalysis}
                  improvementOpportunityAreas={improvementOpportunityAreas}
                  overallAssessmentIndicator={overallAssessmentIndicator}
                  references={orderedVisibleReferences}
                />
                <ExportExcel
                  selectedOrganization={selectedOrganization}
                  selectedSite={selectedSite}
                  backgroundBrief={backgroundBrief}
                  contents={contents}
                  exeSummary={exeSummary}
                  criticalObservations={criticalObservations}
                  selectedObservations={selectedObservations}
                  selectedArea={selectedArea}
                  selectedCategory={selectedCategory}
                  recommendations={recommendations}
                  conclusion={conclusion}
                  selectedDateTime={selectedDateTime.split("T")[0]}
                  isSaved={isSaved}
                  reportType="HSE"
                  chartImage={chartImage}
                  scorePercent={scorePercent}
                  otherDetails={otherDetails}
                  ReportUID={ReportUID}
                  bestPractice={bestPractice}
                  theWayForward={theWayForward}
                  facilityInfo={facilityInfo}
                  introduction={introduction}
                  timeFrom={timeFrom}
                  startDate={new Date(startDate)}
                  endDate={new Date(endDate)}
                  name={name}
                  timeTo={timeTo}
                  briefPropertyDescription={briefPropertyDescription}
                  numOfFloors={numOfFloors}
                  avgStaffFootfall={avgStaffFootfall}
                  noObjectionCertificate={noObjectionCertificate}
                  nationalBuildingCodeCategory={nationalBuildingCodeCategory}
                  coordinationgPersonClientside={coordinationgPersonClientside}
                  reportPreparedBy={reportPreparedBy}
                  reportReviewedBy={reportReviewedBy}
                  buildingName={buildingName}
                  classificationOfAuditObservations={classificationOfAuditObservations}
                  auditScoreAnalysis={auditScoreAnalysis}
                  improvementOpportunityAreas={improvementOpportunityAreas}
                  overallAssessmentIndicator={overallAssessmentIndicator}
                  references={orderedVisibleReferences}
                />
                <ExportWordDoc
                  selectedOrganization={selectedOrganization}
                  selectedSite={selectedSite}
                  backgroundBrief={backgroundBrief}
                  contents={contents}
                  exeSummary={exeSummary}
                  criticalObservations={criticalObservations}
                  selectedObservations={selectedObservations}
                  selectedArea={selectedArea}
                  selectedCategory={selectedCategory}
                  recommendations={recommendations}
                  conclusion={conclusion}
                  selectedDateTime={selectedDateTime.split("T")[0]}
                  isSaved={isSaved}
                  otherDetails={otherDetails}
                  reportType="HSE"
                  chartImage={chartImage}
                  scorePercent={scorePercent}
                  ReportUID={ReportUID}
                  bestPractice={bestPractice}
                  theWayForward={theWayForward}
                  startDate={startDate}
                  endDate={endDate}
                  name={name}
                  facilityInfo={facilityInfo}
                  introduction={introduction}
                  timeFrom={timeFrom}
                  timeTo={timeTo}
                  briefPropertyDescription={briefPropertyDescription}
                  numOfFloors={numOfFloors}
                  avgStaffFootfall={avgStaffFootfall}
                  noObjectionCertificate={noObjectionCertificate}
                  nationalBuildingCodeCategory={nationalBuildingCodeCategory}
                  coordinationgPersonClientside={coordinationgPersonClientside}
                  reportPreparedBy={reportPreparedBy}
                  reportReviewedBy={reportReviewedBy}
                  buildingName={buildingName}
                  classificationOfAuditObservations={classificationOfAuditObservations}
                  auditScoreAnalysis={auditScoreAnalysis}
                  improvementOpportunityAreas={improvementOpportunityAreas}
                  overallAssessmentIndicator={overallAssessmentIndicator}
                  references={orderedVisibleReferences}
                />
              </div>
            </div>
          </div>
        </Modal>
        {openViewReport && (
          <ViewHseReportSectionBasedDetails
            selectedReportData={selectedReportDataView}
            setOpenViewReport={setOpenViewReport}
            reportHeader={reportHeader}
            screenNumber={0}
            completeReport={true}
          />
        )}
      </>
    );
  }









  //   else if (screenNumber === 2) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">
  //                       AUDIT OBJECTIVE
  //                     </div>
  //                     <JoditEditor
  //                       ref={editor}
  //                       placeholder="Enter your text here"
  //                       value={backgroundBrief}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setBackgroundBrief(newContent)} // This updates backgroundBrief on blur
  //                       // onChange={handleChangeJoditEditor} // Updates state on every change
  //                       onChange={(newContent) => {
  //                         isTyping.current = true;
  //                         debouncedChange(newContent)
  //                       }}
  //                     />

  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={() => handleNext()}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } else if (screenNumber === 3) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">
  //                       UNDERSTANDING THE REPORT
  //                     </div>
  //                     <JoditEditor
  //                       // ref={editor}
  //                       ref={contentEditor}
  //                       placeholder="Enter your text here"
  //                       value={contents}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setContents(newContent)} // still update on blur
  //                       onChange={(newContent) => debouncedSetContents(newContent)} // debounced change
  //                     />
  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={handleNext}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } else if (screenNumber === 4) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">INTRODUCTION</div>
  //                     <JoditEditor
  //                       // ref={editor}
  //                       ref={introductionEditor}
  //                       placeholder="Enter your text here"
  //                       value={introduction}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setIntroduction(newContent)} // still update on blur
  //                       onChange={(newContent) => debouncedSetIntroduction(newContent)} // debounced change
  //                     />
  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={handleNext}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } else if (screenNumber === 5) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">EXECUTIVE SUMMARY</div>
  //                     <JoditEditor
  //                       // ref={editor}
  //                       ref={exeSummaryEditor}
  //                       placeholder="Enter your text here"
  //                       value={exeSummary}
  //                       config={config}

  //                       tabIndex={1}
  //                       onBlur={(newContent) => setExeSummary(newContent)} // This updates backgroundBrief on blur
  //                       onChange={(newContent) => {
  //                         debouncedSetExeSummary(newContent)
  //                       }} // Updates state on every change
  //                     />
  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={handleNext}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } else if (screenNumber === 6) {
  //     return (

  //       <Modal open={true} onClose={handleClose}>
  //         <div className="review-modal-container">
  //           <div className="review-modal-content">
  //             <div className="review-modal-header">
  //               <Typography variant="h5">Preview Report</Typography>
  //               <button className="custom-close-button" onClick={handleClose}>
  //                 &#10005; {/* Unicode for 'X' */}
  //               </button>
  //             </div>
  //             <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //               <Typography variant="body1" component="div">
  //                 <div className="sub-headings">ACADEMIC INFORMATION</div>
  //                 <br />
  //                 <TableContainer component={Paper}>
  //                   <Table>
  //                     <TableHead>
  //                       <TableRow>
  //                         <TableCell>FACILITY INFORMATION</TableCell>
  //                         <TableCell>COMMENTS & NOTES</TableCell>
  //                       </TableRow>
  //                     </TableHead>
  //                     <TableBody>
  //                       {facilityInfo &&
  //                         Object.entries(facilityInfo).map(([key, value], index) => (
  //                           <TableRow key={index}>
  //                             <TableCell>{key}:</TableCell>
  //                             <TableCell style={{ display: "flex" }}>
  //                               <TextField
  //                                 value={value}
  //                                 onChange={(e) =>
  //                                   handleChangeFacilityInfo({
  //                                     target: { name: key, value: e.target.value },
  //                                   })
  //                                 }
  //                                 placeholder="Enter your text here"
  //                                 style={{ width: "100%", minHeight: "80px" }}
  //                               />
  //                               <IconButton
  //                                 onClick={() => handleRemoveField(key)}
  //                                 style={{ color: "red", marginLeft: "10px" }}
  //                                 aria-label="delete"
  //                               >
  //                                 <DeleteIcon />
  //                               </IconButton>
  //                             </TableCell>
  //                           </TableRow>
  //                         ))}

  //                       <TableRow>
  //                         <TableCell>
  //                           <TextField
  //                             label="New Field Name"
  //                             value={newKey}
  //                             onChange={handleNewKeyChange}
  //                             variant="outlined"
  //                             style={{ width: "100%" }}
  //                           />
  //                         </TableCell>

  //                         <TableCell style={{ verticalAlign: "middle", padding: "10px" }}>
  //                           <div
  //                             style={{
  //                               display: "flex",
  //                               alignItems: "center",
  //                               justifyContent: "space-between",
  //                               width: "100%",
  //                               gap: "10px", // space between TextField and Button
  //                               padding: "5px 10px", // inner padding inside the cell
  //                             }}
  //                           >
  //                             <TextField
  //                               value={newValue}
  //                               onChange={(e) =>
  //                                 handleNewValueChange({
  //                                   target: { value: e.target.value },
  //                                 })
  //                               }
  //                               placeholder="Enter your text here"
  //                               style={{ width: "100%", minHeight: "80px" }}
  //                             />
  //                             <button
  //                               onClick={handleAddNewField}
  //                               className="button-styles"
  //                               style={{
  //                                 background: "#efc71d",
  //                                 padding: "8px 16px",
  //                                 fontWeight: "bold",
  //                                 borderRadius: "6px",
  //                                 boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
  //                               }}
  //                             >
  //                               ADD
  //                             </button>
  //                           </div>
  //                         </TableCell>

  //                       </TableRow>
  //                     </TableBody>
  //                   </Table>
  //                 </TableContainer>
  //               </Typography>
  //             </div>
  //             <hr />
  //             <div className="review-modal-footer">
  //               <button
  //                 className="button-styles"
  //                 onClick={handleSave}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Save
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 onClick={handlePrev}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 &#171; Prev
  //               </button>
  //               <button
  //                 className="button-styles"
  //                 color="primary"
  //                 onClick={handleNext}
  //                 style={{ background: "#efc71d" }}
  //               >
  //                 Next &#187;
  //               </button>
  //             </div>
  //           </div>
  //         </div>
  //       </Modal>

  //     );

  //   } else if (screenNumber === 7) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">CRITICAL OBSERVATIONS</div>
  //                     <br />
  //                     <div className="critical-observations-div">
  //                       {criticalObservations.length === 0 ? (
  //                         <div className="no-observations">
  //                           <em>No critical observations</em>
  //                         </div>
  //                       ) : (
  //                         criticalObservations.map((observation, index) => (
  //                           <div key={index} className="observation-item" style={{ display: "flex", gap: "5px" }}>
  //                             <textarea
  //                               onChange={(e) => handleObservationEdit(index, e)}
  //                               style={{ width: "100%", fontFamily: "inherit", padding: "10px" }}
  //                               value={observation.observation}
  //                             />
  //                             &nbsp;
  //                             <CancelIcon
  //                               onClick={() => removeItem(index)}
  //                               className="cancel-icon"
  //                             >
  //                               &#10005;
  //                             </CancelIcon>
  //                           </div>
  //                         ))
  //                       )}
  //                     </div>
  //                     <br />
  //                     <div className="custom-text-box">
  //                       <textarea
  //                         onChange={(e) => handleChange(e, "other details")}
  //                         value={otherDetails}
  //                         placeholder="Other Details..."
  //                         style={{
  //                           width: "99%",
  //                           // height: "5vh",
  //                           fontFamily: "inherit",
  //                           padding: "10px"
  //                         }}
  //                       />
  //                       <IoAddCircle size={30} className="IoAddCircle" style={{ cursor: "pointer" }} onClick={addObservation} />
  //                     </div>

  //                   </div>
  //                 </Typography>
  //               </div>

  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   disabled={disableSaveNext}
  //                   style={{
  //                     background: disableSaveNext ? "lightgrey" : "#efc71d",
  //                   }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={handleNext}
  //                   disabled={disableSaveNext}
  //                   style={{
  //                     background: disableSaveNext ? "lightgrey" : "#efc71d",
  //                   }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } else if (screenNumber === 8) {
  //     return (
  //       <>
  //         <VariantsModal
  //           data={observationVariants}
  //           open={openVairantModal}
  //           handleClose={closeVariantModal}
  //           handleConfirmSelection={handleConfirmSelection}
  //         />
  //         <ImageViewerModal
  //           imageUrl={selectedImage}
  //           onClose={() => setSelectedImage(null)}
  //         />
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <div className="review-modal-subheader">
  //                   <Typography variant="h6">PREVIEW REPORT : CRITICAL HSE OBSERVATIONS, PHOTOS & RECOMMENDATIONS</Typography>
  //                 </div>

  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               {/* <div style={{ fontWeight: "600" }} className="sub-headings">
  //                 CRITICAL HSE OBSERVATIONS, PHOTOS & RECOMMENDATIONS
  //               </div> */}
  //               <div style={{ overflowY: "scroll" }} className="review-modal-body">
  //                 <div className="table-container">
  //                   {
  //                   Object.keys(safeGroupedObservations).map((tableType) => (
  //                     <div key={tableType}>
  //                       <Typography style={{ fontWeight: "500", padding: "10px" }} gutterBottom>
  //                         {tableType}
  //                       </Typography>
  //                       <TableContainer component={Paper} className="table-scroll">
  //                         <Table>
  //                           <TableHead>
  //                             <TableRow>
  //                               <TableCell>Sr. No.</TableCell>
  //                               <TableCell>Areas</TableCell>
  //                               <TableCell>Check Point</TableCell>
  //                               <TableCell>Observation</TableCell>
  //                               <TableCell>Criticality</TableCell>
  //                               <TableCell>Recommendation</TableCell>
  //                               <TableCell>IS Reference</TableCell>
  //                               <TableCell>Score</TableCell>
  //                               <TableCell>system_implementation</TableCell>
  //                               <TableCell>compliance_check</TableCell>
  //                               <TableCell>Photo Evidences</TableCell>
  //                               <TableCell>Actions</TableCell>
  //                             </TableRow>
  //                           </TableHead>
  //                           <TableBody>
  //                             {safeGroupedObservations[tableType].map(
  //                               (observation, index) => (
  //                                 <TableRow
  //                                   key={`${observation.table_type}-${observation.area}-${observation.sr_no || observation.id}`}
  //                                   className={
  //                                     (isEditing &&
  //                                       currentEditedRow === (observation.sr_no)) ||
  //                                       !isEditing
  //                                       ? "even-row"
  //                                       : "odd-row"
  //                                   }
  //                                   style={
  //                                     observation.variant === true
  //                                       ? { backgroundColor: "#f2f2f2" }
  //                                       : {}
  //                                   }
  //                                 >
  //                                   <TableCell>{index + 1}</TableCell>
  //                                   {/* area */}
  //                                   <TableCell
  //                                     className="editable-cell"
  //                                     style={{ height: "100px" }}
  //                                   >
  //                                     <div
  //                                       className="cell-content"
  //                                       style={{
  //                                         color:
  //                                           isEditing &&
  //                                             currentEditedRow !==
  //                                             observation.sr_no

  //                                             ? "grey"
  //                                             : "black",
  //                                       }}
  //                                     >
  //                                       <CreatableSelect
  //                                         styles={customSelectStylesCreatable}
  //                                         placeholder="Area"
  //                                         options={areaOptions()}
  //                                         defaultValue={{
  //                                           label: observation.area,
  //                                           value: observation.area,
  //                                         }}
  //                                         isSearchable
  //                                         onChange={(e) =>
  //                                           handleCellEdit(
  //                                             e,
  //                                             index,
  //                                             "area",
  //                                             observation.area,
  //                                             observation
  //                                           )
  //                                         }
  //                                         isDisabled={
  //                                           isEditing &&
  //                                           currentEditedRow !==
  //                                           observation.sr_no


  //                                         }
  //                                       />
  //                                     </div>
  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                       />
  //                                     )}
  //                                   </TableCell>
  //                                   {/* checkpoints */}
  //                                   <TableCell
  //                                     className="editable-cell"
  //                                     style={{ height: "100px" }}
  //                                   >
  //                                     <div
  //                                       className="cell-content"
  //                                       style={{

  //                                         marginRight: "10px"
  //                                       }}
  //                                     >

  //                                       <TextField
  //                                         id={`outlined-textarea-${index}`}
  //                                         value={observation.check_points}

  //                                         onChange={(e) => {
  //                                           const newValue = e.target.value;

  //                                           const proceed = handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: newValue } },
  //                                             index,
  //                                             "check_points",
  //                                             observation.check_points,
  //                                             { ...observation, check_points: newValue }
  //                                           );

  //                                           if (!proceed) return;

  //                                           if (newValue !== observation.check_points) {
  //                                             setSelectedObservations((prev) => {
  //                                               return prev.map((obs) =>
  //                                                 (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                                   ? { ...obs, check_points: newValue }
  //                                                   : obs
  //                                               );
  //                                             });

  //                                           }
  //                                         }}


  //                                         onBlur={(e) => {
  //                                           handleCellEdit(e, index, "check_points", observation.check_points, observation);
  //                                         }}
  //                                         sx={{ width: "200px" }}
  //                                         InputProps={{
  //                                           sx: { fontSize: "10px" },
  //                                           disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                         }}
  //                                         placeholder="Check Point"
  //                                         variant="outlined"
  //                                         size="small"
  //                                         fullWidth
  //                                         multiline
  //                                         minRows={1.5}
  //                                         maxRows={10}
  //                                       />



  //                                     </div>
  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         onClick={(e) =>
  //                                           isEditing
  //                                             ? handleCellEdit(
  //                                               e,
  //                                               index,
  //                                               "check_points",
  //                                               observation.check_points,
  //                                               observation
  //                                             )
  //                                             : null
  //                                         }
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                       />
  //                                     )}
  //                                   </TableCell>
  //                                   {/* observation */}
  //                                   <TableCell className="editable-cell" style={{ height: "100px" }}>
  //                                     <div
  //                                       className="cell-content"
  //                                       style={{
  //                                         marginRight: "10px",
  //                                       }}
  //                                     >
  //                                       <TextField
  //                                         id={`outlined-textarea-${index}`}
  //                                         placeholder="Observation"
  //                                         value={
  //                                           observation.observation
  //                                             ? observation.observation.replace(/\s+/g, " ").trim()
  //                                             : ""
  //                                         }
  //                                         onChange={(e) => {
  //                                           const newValue = e.target.value;

  //                                           const proceed = handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: newValue } },
  //                                             index,
  //                                             "observation",
  //                                             observation.observation,
  //                                             { ...observation, observation: newValue }
  //                                           );

  //                                           if (!proceed) return;

  //                                           setSelectedObservations((prev) =>
  //                                             prev.map((obs) =>
  //                                               (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                                 ? { ...obs, observation: newValue, hasEdited: true }
  //                                                 : obs
  //                                             )
  //                                           );
  //                                         }}
  //                                         onBlur={(e) => {
  //                                           const latestValue = e.target.value;

  //                                           handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: latestValue } },
  //                                             index,
  //                                             "observation",
  //                                             observation.observation,
  //                                             { ...observation, observation: latestValue }
  //                                           );
  //                                         }}
  //                                         sx={{ width: "200px" }}
  //                                         InputProps={{
  //                                           sx: { fontSize: "10px" },
  //                                           disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                         }}
  //                                         variant="outlined"
  //                                         size="small"
  //                                         fullWidth
  //                                         multiline
  //                                         minRows={1.5}
  //                                         maxRows={10}
  //                                       />

  //                                       <DialogBox dialog={dialog} handleDialogBox={handleDialogBoxObservation} />

  //                                     </div>

  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         onClick={(e) =>
  //                                           isEditing
  //                                             ? handleCellEdit(
  //                                               e,
  //                                               index,
  //                                               "observation",
  //                                               observation.observation,
  //                                               observation
  //                                             )
  //                                             : null
  //                                         }
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                       />
  //                                     )}
  //                                   </TableCell>

  //                                   {/* criticality */}

  //                                   <TableCell className="editable-cell" style={{ height: "100px" }}>
  //                                     <Select
  //                                       styles={{
  //                                         ...customSelectStylesCreatable,
  //                                       }}
  //                                       placeholder="Criticality"
  //                                       options={criticalityOptions.length > 0 ? criticalityOptions : []}
  //                                       noOptionsMessage={() => "No options"}
  //                                       value={
  //                                         observation.criticality
  //                                           ? { label: observation.criticality, value: observation.criticality }
  //                                           : null
  //                                       }
  //                                       isSearchable
  //                                       onChange={(selectedOption) => {
  //                                         if (currentEditedRow !== -1 && currentEditedRow !== observation.sr_no) {
  //                                           toast.warning("Please save the current row before editing another.");
  //                                           return;
  //                                         }

  //                                         if (selectedOption) {
  //                                           const newValue = selectedOption.value;


  //                                           const proceed = handleCellEdit(
  //                                             { value: newValue },
  //                                             index,
  //                                             "criticality",
  //                                             observation.criticality,
  //                                             { ...observation, criticality: newValue }
  //                                           );

  //                                           if (!proceed) return;


  //                                           setSelectedObservations((prev) =>
  //                                             prev.map((obs) =>
  //                                               (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                                 ? { ...obs, criticality: newValue, hasEdited: true }
  //                                                 : obs
  //                                             )
  //                                           );
  //                                         }
  //                                       }}
  //                                       isDisabled={
  //                                         (isEditing && currentEditedRow !== observation.sr_no) ||
  //                                         observation.variant === true
  //                                       }
  //                                       menuPlacement="auto"
  //                                     />

  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                         onClick={(e) => {
  //                                           e.stopPropagation();
  //                                           if (
  //                                             (isEditing && currentEditedRow !== observation.sr_no) ||
  //                                             observation.variant === true
  //                                           ) {
  //                                             toast.warning(
  //                                               "Please save changes in the currently edited row before editing another row."
  //                                             );
  //                                           }
  //                                         }}
  //                                       />
  //                                     )}
  //                                   </TableCell>

  //                                   {/* recommendations */}
  //                                   <TableCell className="editable-cell">
  //                                     <div
  //                                       className="cell-content"
  //                                       style={{

  //                                         marginRight: "10px"
  //                                       }}
  //                                     >

  //                                       <TextField
  //                                         id={`outlined-textarea-${index}`}
  //                                         value={observation.recommendations}
  //                                         placeholder="Recommendations"
  //                                         onChange={(e) => {
  //                                           const newValue = e.target.value;

  //                                           const proceed = handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: newValue } },
  //                                             index,
  //                                             "recommendations",
  //                                             observation.recommendations,
  //                                             { ...observation, recommendations: newValue }
  //                                           );

  //                                           if (!proceed) return;

  //                                           setSelectedObservations((prev) =>
  //                                             prev.map((obs) =>
  //                                               (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                                 ? { ...obs, recommendations: newValue, hasEdited: true }
  //                                                 : obs
  //                                             )
  //                                           );
  //                                         }}
  //                                         onBlur={(e) => {
  //                                           const latestValue = e.target.value;

  //                                           handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: latestValue } },
  //                                             index,
  //                                             "recommendations",
  //                                             observation.recommendations,
  //                                             { ...observation, recommendations: latestValue }
  //                                           );
  //                                         }}
  //                                         sx={{ width: "200px" }}
  //                                         InputProps={{
  //                                           sx: { fontSize: "10px" },
  //                                           disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                         }}
  //                                         variant="outlined"
  //                                         size="small"
  //                                         fullWidth
  //                                         multiline
  //                                         minRows={1.5}
  //                                         maxRows={10}
  //                                       />


  //                                     </div>
  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         onClick={(e) =>
  //                                           isEditing
  //                                             ? handleCellEdit(
  //                                               e,
  //                                               index,
  //                                               "recommendations",
  //                                               observation.recommendations,
  //                                               observation
  //                                             )
  //                                             : null
  //                                         }
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                       />
  //                                     )}
  //                                   </TableCell>
  //                                   {/* isreference */}
  //                                   <TableCell
  //                                     className="editable-cell"
  //                                     style={{ height: "100px" }}
  //                                   >
  //                                     <div
  //                                       className="cell-content"
  //                                       style={{

  //                                         marginRight: "10px"
  //                                       }}
  //                                     >

  //                                       <TextField
  //                                         id={`outlined-textarea-${index}`}
  //                                         value={observation.is_reference}
  //                                         placeholder="IS Reference"
  //                                         onChange={(e) => {
  //                                           const newValue = e.target.value;

  //                                           const proceed = handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: newValue } },
  //                                             index,
  //                                             "is_reference",
  //                                             observation.is_reference,
  //                                             { ...observation, is_reference: newValue }
  //                                           );

  //                                           if (!proceed) return;

  //                                           setSelectedObservations((prev) =>
  //                                             prev.map((obs) =>
  //                                               (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                                 ? { ...obs, is_reference: newValue, hasEdited: true }
  //                                                 : obs
  //                                             )
  //                                           );
  //                                         }}
  //                                         onBlur={(e) => {
  //                                           const latestValue = e.target.value;

  //                                           handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: latestValue } },
  //                                             index,
  //                                             "is_reference",
  //                                             observation.is_reference,
  //                                             { ...observation, is_reference: latestValue }
  //                                           );
  //                                         }}
  //                                         sx={{ width: "200px" }}
  //                                         InputProps={{
  //                                           sx: { fontSize: "10px" },
  //                                           disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                         }}
  //                                         variant="outlined"
  //                                         size="small"
  //                                         fullWidth
  //                                         multiline
  //                                         minRows={1.5}
  //                                         maxRows={10}
  //                                       />

  //                                     </div>
  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         onClick={(e) =>
  //                                           isEditing
  //                                             ? handleCellEdit(
  //                                               e,
  //                                               index,
  //                                               "is_reference",
  //                                               observation.is_reference,
  //                                               observation
  //                                             )
  //                                             : null
  //                                         }
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                       />
  //                                     )}
  //                                   </TableCell>
  //                                   {/* scores */}
  //                                   <TableCell className="editable-cell" style={{ height: "100px" }}>
  //                                     <div
  //                                       className="cell-content"
  //                                       style={{

  //                                         marginRight: "10px",
  //                                       }}
  //                                     >
  //                                       <TextField
  //                                         type="number"
  //                                         value={observation.score ?? ""}
  //                                         placeholder="Score"
  //                                         sx={{ width: "80px" }}
  //                                         InputProps={{
  //                                           sx: { fontSize: "10px" },
  //                                           inputProps: {
  //                                             min: 0,
  //                                             max: 5,
  //                                             step: 1,
  //                                           },
  //                                           disabled:
  //                                             observation.variant === true ||
  //                                             (isEditing && currentEditedRow !== observation.sr_no),
  //                                         }}
  //                                         onChange={(e) =>
  //                                           handleScoreChange(
  //                                             e,
  //                                             observation.table_type,
  //                                             observation.sr_no || observation.id,
  //                                             index
  //                                           )
  //                                         }
  //                                         size="small"
  //                                         variant="outlined"
  //                                       />
  //                                     </div>

  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         onClick={(e) =>
  //                                           isEditing
  //                                             ? setCurrentEditedRow(index)
  //                                             : null
  //                                         }
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                       />
  //                                     )}
  //                                   </TableCell>

  //                                   {/* system_implementation */}
  //                                   <TableCell className="editable-cell">
  //                                     <div
  //                                       className="cell-content"
  //                                       style={{

  //                                         marginRight: "10px"
  //                                       }}
  //                                     >

  //                                       <TextField
  //                                         id={`outlined-textarea-${index}`}
  //                                         value={observation.system_implementation}
  //                                         placeholder="system_implentation"
  //                                         onChange={(e) => {
  //                                           const newValue = e.target.value;

  //                                           const proceed = handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: newValue } },
  //                                             index,
  //                                             "system_implementation",
  //                                             observation.system_implementation,
  //                                             { ...observation, system_implementation: newValue }
  //                                           );

  //                                           if (!proceed) return;

  //                                           setSelectedObservations((prev) =>
  //                                             prev.map((obs) =>
  //                                               (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                                 ? { ...obs, recommendations: newValue, hasEdited: true }
  //                                                 : obs
  //                                             )
  //                                           );
  //                                         }}
  //                                         onBlur={(e) => {
  //                                           const latestValue = e.target.value;

  //                                           handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: latestValue } },
  //                                             index,
  //                                             "system_implementation",
  //                                             observation.system_implementation,
  //                                             { ...observation, system_implementation: latestValue }
  //                                           );
  //                                         }}
  //                                         sx={{ width: "200px" }}
  //                                         InputProps={{
  //                                           sx: { fontSize: "10px" },
  //                                           disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                         }}
  //                                         variant="outlined"
  //                                         size="small"
  //                                         fullWidth
  //                                         multiline
  //                                         minRows={1.5}
  //                                         maxRows={10}
  //                                       />


  //                                     </div>
  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         onClick={(e) =>
  //                                           isEditing
  //                                             ? handleCellEdit(
  //                                               e,
  //                                               index,
  //                                               "system_implementation",
  //                                               observation.system_implementation,
  //                                               observation
  //                                             )
  //                                             : null
  //                                         }
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                       />
  //                                     )}
  //                                   </TableCell>

  //                                 {/* compliance_check */}
  //                                 <TableCell className="editable-cell">
  //                                     <div
  //                                       className="cell-content"
  //                                       style={{

  //                                         marginRight: "10px"
  //                                       }}
  //                                     >

  //                                       <TextField
  //                                         id={`outlined-textarea-${index}`}
  //                                         value={observation.compliance_check}
  //                                         placeholder="compliance_check"
  //                                         onChange={(e) => {
  //                                           const newValue = e.target.value;

  //                                           const proceed = handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: newValue } },
  //                                             index,
  //                                             "compliance_check",
  //                                             observation.compliance_check,
  //                                             { ...observation, compliance_check: newValue }
  //                                           );

  //                                           if (!proceed) return;

  //                                           setSelectedObservations((prev) =>
  //                                             prev.map((obs) =>
  //                                               (obs.sr_no || obs.id) === (observation.sr_no || observation.id)
  //                                                 ? { ...obs, compliance_check: newValue, hasEdited: true }
  //                                                 : obs
  //                                             )
  //                                           );
  //                                         }}
  //                                         onBlur={(e) => {
  //                                           const latestValue = e.target.value;

  //                                           handleCellEdit(
  //                                             { ...e, target: { ...e.target, textContent: latestValue } },
  //                                             index,
  //                                             "compliance_check",
  //                                             observation.compliance_check,
  //                                             { ...observation, compliance_check: latestValue }
  //                                           );
  //                                         }}
  //                                         sx={{ width: "200px" }}
  //                                         InputProps={{
  //                                           sx: { fontSize: "10px" },
  //                                           disabled: currentEditedRow !== -1 && currentEditedRow !== observation.sr_no,
  //                                         }}
  //                                         variant="outlined"
  //                                         size="small"
  //                                         fullWidth
  //                                         multiline
  //                                         minRows={1.5}
  //                                         maxRows={10}
  //                                       />


  //                                     </div>
  //                                     {!observation.variant && (
  //                                       <EditOutlinedIcon
  //                                         onClick={(e) =>
  //                                           isEditing
  //                                             ? handleCellEdit(
  //                                               e,
  //                                               index,
  //                                               "compliance_check",
  //                                               observation.compliance_check,
  //                                               observation
  //                                             )
  //                                             : null
  //                                         }
  //                                         className="edit-icon"
  //                                         fontSize="small"
  //                                       />
  //                                     )}
  //                                   </TableCell>


  //                                   {/* photop upload  */}

  //                                   <TableCell>
  //                                     <div
  //                                       style={{
  //                                         display: "flex",
  //                                         alignItems: "center",
  //                                         justifyContent: "center",
  //                                         gap: "15px",
  //                                       }}
  //                                     >
  //                                       {/* Upload Icon with input */}
  //                                       <div
  //                                         style={{
  //                                           display: "flex",
  //                                           alignItems: "center",
  //                                           justifyContent: "center",
  //                                           padding: "6px 0",
  //                                           border: "1px solid grey",
  //                                           borderRadius: "5px",
  //                                           width: "100px",
  //                                           cursor:
  //                                             isEditing && currentEditedRow !== observation.sr_no
  //                                               ? "not-allowed"
  //                                               : "pointer",
  //                                           position: "relative",
  //                                           background:
  //                                             isEditing && currentEditedRow !== observation.sr_no
  //                                               ? "#f0f0f0"
  //                                               : "white",
  //                                           opacity:
  //                                             isEditing && currentEditedRow !== observation.sr_no
  //                                               ? 0.6
  //                                               : 1,
  //                                         }}
  //                                       >
  //                                         <CloudUploadOutlinedIcon fontSize="small" />
  //                                         <input
  //                                           type="file"
  //                                           accept="image/*"
  //                                           multiple
  //                                           onChange={(e) =>
  //                                             handleImageUpload(
  //                                               observation.table_type,
  //                                               observation.sr_no || observation.id,
  //                                               observation.area,
  //                                               e.target.files
  //                                             )
  //                                           }
  //                                           style={{
  //                                             position: "absolute",
  //                                             width: "100%",
  //                                             height: "100%",
  //                                             top: 0,
  //                                             left: 0,
  //                                             opacity: 0,
  //                                             cursor:
  //                                               isEditing && currentEditedRow !== observation.sr_no
  //                                                 ? "not-allowed"
  //                                                 : "pointer",
  //                                           }}
  //                                           disabled={
  //                                             isEditing && currentEditedRow !== observation.sr_no
  //                                           }
  //                                         />
  //                                       </div>

  //                                       {/* View Uploaded Images Icon */}
  //                                       <div
  //                                         style={{
  //                                           display: "flex",
  //                                           alignItems: "center",
  //                                           justifyContent: "center",
  //                                           padding: "6px 0",
  //                                           border: "1px solid grey",
  //                                           borderRadius: "5px",
  //                                           width: "100px",
  //                                           cursor: "pointer",
  //                                         }}
  //                                         onClick={() =>
  //                                           handleOpenImageDialog(observation.sr_no || observation.id, observation)
  //                                         }
  //                                       >
  //                                         <InsertPhotoOutlinedIcon fontSize="small" />
  //                                       </div>
  //                                     </div>

  //                                     {/* Image Preview Dialog */}
  //                                     {selectedObservation?.image &&
  //                                       selectedObservation.image.sr_no === observation.sr_no && (
  //                                         <Dialog onClose={handleCloseImageDialog} open={openDialog} maxWidth={false}>
  //                                           <DialogTitle>Uploaded Images</DialogTitle>
  //                                           <DialogContent dividers>
  //                                             {selectedObservation.image.imageUrls?.length > 0 ? (
  //                                               <div
  //                                                 style={{
  //                                                   display: "grid",
  //                                                   gridTemplateColumns: "repeat(4, 1fr)",
  //                                                   gap: "10px",
  //                                                 }}
  //                                               >
  //                                                 {selectedObservation.image.imageUrls.map((imageUrl, imgIndex) => (
  //                                                   <div style={{ display: "flex" }} key={imgIndex}>
  //                                                     <img
  //                                                       src={imageUrl}
  //                                                       alt={`Image ${imgIndex + 1}`}
  //                                                       onClick={() => setSelectedImage(imageUrl)}
  //                                                       style={{
  //                                                         cursor: "pointer",
  //                                                         width: "200px",
  //                                                         height: "200px",
  //                                                         objectFit: "cover",
  //                                                         borderRadius: "4px",
  //                                                       }}
  //                                                     />
  //                                                     <CancelIcon
  //                                                       className="cancel-icon"
  //                                                       onClick={() =>
  //                                                         handleRemoveImage(
  //                                                           observation.table_type,
  //                                                           observation.area,
  //                                                           observation.sr_no || observation.id,
  //                                                           imgIndex
  //                                                         )
  //                                                       }
  //                                                       style={{ cursor: "pointer", marginLeft: 5 }}
  //                                                     />
  //                                                   </div>
  //                                                 ))}
  //                                               </div>
  //                                             ) : (
  //                                               <Typography gutterBottom>No File Uploaded</Typography>
  //                                             )}
  //                                           </DialogContent>
  //                                           <DialogActions>
  //                                             <Button onClick={handleCloseImageDialog}>Close</Button>
  //                                           </DialogActions>
  //                                         </Dialog>
  //                                       )}
  //                                   </TableCell>



  //                                   <TableCell className="table-actions">
  //                                     <div style={{
  //                                       display: "flex",
  //                                       justifyContent: "center",
  //                                     }}>
  //                                       {!observation.variant && <InfoIcon
  //                                         onClick={() =>
  //                                           getObservationVariants(observation.observation, observation.sr_no || observation.id)
  //                                         }
  //                                         style={{ cursor: "pointer" }}
  //                                       />}
  //                                       {/* {!observation.variant && <PlaylistAddCircleIcon
  //                                         onClick={() =>
  //                                           handleDuplicateRow(
  //                                             observation.table_type,
  //                                             observation.area,
  //                                             observation.sr_no || observation.id
  //                                           )
  //                                         }
  //                                         style={{ cursor: "pointer" }}
  //                                       />} */}
  //                                       <DeleteForeverIcon
  //                                         onClick={() =>
  //                                           handleDeleteRow(
  //                                             observation.table_type,
  //                                             observation.area,
  //                                             observation.sr_no || observation.id
  //                                           )
  //                                         }
  //                                         style={{ cursor: "pointer" }}
  //                                       />
  //                                     </div>
  //                                   </TableCell>
  //                                 </TableRow>
  //                               )
  //                             )}
  //                             {renderNewRowInputs(tableType)}
  //                           </TableBody>
  //                         </Table>
  //                       </TableContainer>
  //                     </div>
  //                   ))}
  //                 </div>
  //               </div>
  //               <hr />
  //               <div
  //                 style={{ marginBottom: "10px" }}
  //                 className="review-modal-footer"
  //               >
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={() => handleNext()}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } else if (screenNumber === 9) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">GLOBAL BEST PRACTICES</div>
  //                     <JoditEditor
  //                       // ref={editor}
  //                       ref={bestPracticeEditor}
  //                       placeholder="Enter your text here"
  //                       value={bestPractice}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setbestPractice(newContent)} // This updates backgroundBrief on blur
  //                       onChange={(newContent) => { debouncedSetGlobalPractice(newContent) }} // Updates state on every change
  //                     />
  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={handleNext}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } else if (screenNumber === 10) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Charts</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <div
  //                   id="chart-container-for-report"
  //                   className="chart-container-for-report"
  //                   ref={chartContainerRef}
  //                 >
  //                   <div className="total-serverity-div-for-report">
  //                     <div className="severity-item-for-report">
  //                       Total Observations
  //                       <br />
  //                       <span>{data.length}</span>
  //                       <hr />
  //                     </div>
  //                     <div
  //                       style={{ color: "#FF0000" }}
  //                       className="severity-item-for-report"
  //                     >
  //                       High Severity Observations
  //                       <br />
  //                       <span>
  //                         {data.filter((e) => e.criticality === "High").length}
  //                       </span>
  //                       <hr />
  //                     </div>
  //                     <div
  //                       style={{ color: "#006400" }}
  //                       className="severity-item-for-report"
  //                     >
  //                       Medium Severity Observations
  //                       <br />
  //                       <span>
  //                         {data.filter((e) => e.criticality === "Medium").length}
  //                       </span>
  //                       <hr />
  //                     </div>
  //                     <div
  //                       style={{ color: "#005cdb" }}
  //                       className="severity-item-for-report"
  //                     >
  //                       Low Severity Observations
  //                       <br />
  //                       <span>
  //                         {data.filter((e) => e.criticality === "Low").length}
  //                       </span>
  //                       <hr />
  //                     </div>
  //                   </div>
  //                   <div className="area-chart-for-report">
  //                     Area Chart
  //                     <Chart
  //                       options={barOptions}
  //                       series={[
  //                         {
  //                           name: "",
  //                           data: counts,
  //                         },
  //                       ]}
  //                       type="bar"
  //                       height={300}
  //                     />
  //                   </div>
  //                   <div className="severity-chart-for-report">
  //                     Severity Chart
  //                     <Chart
  //                       options={severityChartOptions}
  //                       series={seriesData}
  //                       type="bar"
  //                       height={300}
  //                     />
  //                   </div>
  //                   <div className="pie-chart-for-report">
  //                     Audit Score
  //                     <Chart
  //                       options={pieOptions}
  //                       series={[scorePercent, 100 - scorePercent]}
  //                       type="pie"
  //                       style={{ width: "100%" }}
  //                       height={250}
  //                     />
  //                   </div>
  //                 </div>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   disabled={waitForCharts}
  //                   style={{
  //                     background: waitForCharts ? "lightgrey" : "#efc71d",
  //                   }}
  //                 >
  //                   {waitForCharts ? "Wait..." : "Save"}
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   disabled={waitForCharts}
  //                   style={{
  //                     background: waitForCharts ? "lightgrey" : "#efc71d",
  //                   }}
  //                 >
  //                   &#171;{waitForCharts ? "Wait..." : "Prev"}
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={handleNext}
  //                   disabled={waitForCharts}
  //                   style={{
  //                     background: waitForCharts ? "lightgrey" : "#efc71d",
  //                   }}
  //                 >
  //                   {waitForCharts ? "Wait..." : "Next"} &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } else if (screenNumber === 11) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">THE WAY FORWARD</div>
  //                     <JoditEditor
  //                       // ref={editor}
  //                       ref={wayForwardPlanEditor}
  //                       placeholder="Enter your text here"
  //                       value={theWayForward}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setTheWayForward(newContent)}
  //                       onChange={(newContent) => { debouncedSetTheWayForward(newContent) }}
  //                     />
  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={handleNext}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } 

  //     else if (screenNumber === 12) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">
  //                       Classification of Audit Observations
  //                     </div>
  //                     <JoditEditor
  //                       ref={classificationEditor}
  //                       placeholder="Enter your text here"
  //                       value={classificationOfAuditObservations}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setClassificationOfAuditObservations(newContent)} // This updates backgroundBrief on blur
  //                       // onChange={handleChangeJoditEditor} // Updates state on every change
  //                       onChange={(newContent) => {
  //                         isTyping.current = true;
  //                         debouncedSetClassification(newContent)
  //                       }}
  //                     />

  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={() => handleNext()}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   }
  //    else if (screenNumber === 13 ) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">
  //                       AUDIT SCORE ANALYSIS
  //                     </div>
  //                     <JoditEditor
  //                       ref={auditScoreEditor}
  //                       placeholder="Enter your text here"
  //                       value={auditScoreAnalysis}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setAuditScoreAnalysis(newContent)} // This updates backgroundBrief on blur
  //                       // onChange={handleChangeJoditEditor} // Updates state on every change
  //                       onChange={(newContent) => {
  //                         isTyping.current = true;
  //                         debouncedSetAuditScore(newContent)
  //                       }}
  //                     />

  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={() => handleNext()}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   }
  //      else if (screenNumber === 14 ) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">
  //                       IMPROVEMENT OPPORTUNITY AREAS
  //                     </div>
  //                     <JoditEditor
  //                       ref={improvementOpportunityEditor}
  //                       placeholder="Enter your text here"
  //                       value={improvementOpportunityAreas}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setImprovementOpportunityAreas(newContent)} // This updates backgroundBrief on blur
  //                       // onChange={handleChangeJoditEditor} // Updates state on every change
  //                       onChange={(newContent) => {
  //                         isTyping.current = true;
  //                         debouncedSetImprovement(newContent)
  //                       }}
  //                     />

  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={() => handleNext()}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   }
  //     else if (screenNumber === 15) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h6">PREVIEW REPORT : OVERALL RISK ASSESSMENT INDICATOR</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div className="review-modal-body" style={{ display: "flex", gap: "10px", overflowY: "auto", marginBottom:"20px"}}>

  //   {/* Left Risk Section */}
  //   <div
  //     style={{
  //       width: "200px",
  //       padding: "10px",
  //       borderRadius: "4px",
  //       backgroundColor: currentRisk.color,
  //       color: "#000",
  //       flexShrink: 0,
  //       display:"flex",
  //       justifyContent:"center",
  //       alignItems:"center",
  //     }}
  //   >
  //    <p> 30%</p>
  //     {/* <p>{Math.floor((cumulativeScore / 10) * 100)}%</p> */}
  //   </div>

  //   {/* Right JoditEditor */}
  //   <div style={{ flexGrow: 1 }}>
  //     <JoditEditor
  //       ref={editor}
  //       placeholder="Enter your text here"
  //       value={overallAssessmentIndicator}
  //       config={config}
  //       tabIndex={1}
  //       onBlur={(newContent) => setOverallAssessmentIndicator(newContent)} 
  //       onChange={(newContent) => { debouncedSetOverallAssessment(newContent) }} 
  //     />
  //   </div>
  // </div>
  //                   <div style={{ marginBottom: "20px", overflowX: "auto" }}>
  //                   <h3 style={{color:"#307260",fontFamily:"Montserrat"}}>Risk Legend</h3>
  //                    <p style={{fontFamily: "Montserrat"}}>The above image is per display purpose only</p>
  //             <table style={{ width: "100%", borderCollapse: "collapse",fontFamily:"Montserrat" }}>
  //               <thead>
  //                 <tr style={{ backgroundColor: "#307260", color: "#efc71d" }}>
  //                   <th style={{ padding: "8px", border: "1px solid #000",fontSize:"14px" }}>Score Range</th>
  //                   <th style={{ padding: "8px", border: "1px solid #000",fontSize:"14px" }}>Risk Level</th>
  //                   <th style={{ padding: "8px", border: "1px solid #000",fontSize:"14px" }}>Interpretation</th>
  //                 </tr>
  //               </thead>
  //               <tbody>
  //                 {riskLevels.map((level, index) => (
  //                   <tr key={index}>
  //                     <td style={{ textAlign: "center", color: level.color, border: "1px solid #000", padding: "8px",fontSize:"13px" }}>
  //                       {level.range}
  //                     </td>
  //                     <td style={{ textAlign: "center", backgroundColor: level.color, color: "#000", border: "1px solid #000", padding: "8px",fontSize:"13px" }}>
  //                       {level.risk}
  //                     </td>
  //                     <td style={{ border: "1px solid #000", padding: "8px",fontSize:"13px" }}>
  //                       {level.interpretation}
  //                     </td>
  //                   </tr>
  //                 ))}
  //               </tbody>
  //             </table>
  //           </div>

  //                 </Typography>

  //               </div>
  //               <hr />
  //               <div className="review-modal-footer">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   color="primary"
  //                   onClick={handleNext}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Next &#187;
  //                 </button>
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   } 
  //   else if (screenNumber === 16) {
  //     return (
  //       <>
  //         <Modal open={open} onClose={handleClose}>
  //           <div className="review-modal-container">
  //             <div className="review-modal-content">
  //               <div className="review-modal-header">
  //                 <Typography variant="h5">Preview Report</Typography>
  //                 <button className="custom-close-button" onClick={handleClose}>
  //                   &#10005; {/* Unicode for 'X' */}
  //                 </button>
  //               </div>
  //               <div className="review-modal-body" style={{ overflowY: "auto" }}>
  //                 <Typography variant="body1" component="div">
  //                   <div>
  //                     <div className="sub-headings">CONCLUSION</div>
  //                     <JoditEditor
  //                       // ref={editor}
  //                       ref={conclusionEditor}
  //                       placeholder="Enter your text here"
  //                       value={conclusion}
  //                       config={config}
  //                       tabIndex={1}
  //                       onBlur={(newContent) => setConclusion(newContent)} // This updates backgroundBrief on blur
  //                       onChange={(newContent) => { debouncedSetConclusion(newContent) }} // Updates state on every change
  //                     />
  //                   </div>
  //                 </Typography>
  //               </div>
  //               <hr />
  //               <div className="review-modal-footer" id="conclusionFooter">
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleSave}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Save
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handlePrev}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   &#171; Prev
  //                 </button>
  //                 <button
  //                   className="button-styles"
  //                   onClick={handleComplete}
  //                   // disabled={isComplete}
  //                   style={{ background: "#efc71d" }}
  //                 >
  //                   Complete
  //                 </button>
  //                 <ExportSavedReportPDF
  //                   selectedOrganization={selectedOrganization}
  //                   selectedSite={selectedSite}
  //                   backgroundBrief={backgroundBrief}
  //                   contents={contents}
  //                   exeSummary={exeSummary}
  //                   criticalObservations={criticalObservations}
  //                   selectedObservations={selectedObservations}
  //                   selectedArea={selectedArea}
  //                   selectedCategory={selectedCategory}
  //                   recommendations={recommendations}
  //                   conclusion={conclusion}
  //                   selectedDateTime={selectedDateTime.split("T")[0]}
  //                   reportType="HSE"
  //                   isSaved={isSaved}
  //                    chartImage={chartImage}
  //                   otherDetails={otherDetails}
  //                   ReportUID={ReportUID}
  //                   startDate={startDate}
  //                   endDate={endDate}
  //                   bestPractice={bestPractice}
  //                   theWayForward={theWayForward}
  //                   name={name}
  //                   facilityInfo={facilityInfo}
  //                   introduction={introduction}
  //                 />
  //                 <ExportExcel
  //                   selectedOrganization={selectedOrganization}
  //                   selectedSite={selectedSite}
  //                   backgroundBrief={backgroundBrief}
  //                   contents={contents}
  //                   exeSummary={exeSummary}
  //                   criticalObservations={criticalObservations}
  //                   selectedObservations={selectedObservations}
  //                   selectedArea={selectedArea}
  //                   selectedCategory={selectedCategory}
  //                   recommendations={recommendations}
  //                   conclusion={conclusion}
  //                   selectedDateTime={selectedDateTime.split("T")[0]}
  //                   isSaved={isSaved}
  //                   reportType="HSE"
  //                   otherDetails={otherDetails}
  //                   ReportUID={ReportUID}
  //                   bestPractice={bestPractice}
  //                   theWayForward={theWayForward}
  //                   facilityInfo={facilityInfo}
  //                   introduction={introduction}
  //                 />
  //                 <ExportWordDoc
  //                   selectedOrganization={selectedOrganization}
  //                   selectedSite={selectedSite}
  //                   backgroundBrief={backgroundBrief}
  //                   contents={contents}
  //                   exeSummary={exeSummary}
  //                   criticalObservations={criticalObservations}
  //                   selectedObservations={selectedObservations}
  //                   selectedArea={selectedArea}
  //                   selectedCategory={selectedCategory}
  //                   recommendations={recommendations}
  //                   conclusion={conclusion}
  //                   selectedDateTime={selectedDateTime.split("T")[0]}
  //                   isSaved={isSaved}
  //                   otherDetails={otherDetails}
  //                   reportType="HSE"
  //                   chartImage={chartImage}
  //                   ReportUID={ReportUID}
  //                   bestPractice={bestPractice}
  //                   theWayForward={theWayForward}
  //                   startDate={startDate}
  //                   endDate={endDate}
  //                   name={name}
  //                   facilityInfo={facilityInfo}
  //                   introduction={introduction}
  //                 />
  //               </div>
  //             </div>
  //           </div>
  //         </Modal>
  //       </>
  //     );
  //   }




};

export default HsePreviewReportModal;
